import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import Webcam from 'react-webcam';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';
import { loadFaceTrackingModel } from './utils/faceTrackingModel';
import handleSrc from './assets/images/handle.png';
import watermarkLogo from './assets/images/logo.webp';
import iconCart from './assets/images/icon-cart.png';
import iconFaceShape from './assets/images/icon-face-shape.png';
import iconMultimedia from './assets/images/icon-multimedia.png';
import { GLASSES_CATALOG } from './glassesCatalog';
import MediaPreviewModal from './components/MediaPreviewModal';
import CompareModal from './components/CompareModal';
import { LazyImageWithFallback, LazyReveal } from './components/LazyGlassesCatalog';
// GlassesPanel is now embedded directly in the component for better styling control
import {
  addSelfie,
  captureStageComposite,
  captureVideoCoverFrame,
  clearSelfies,
  loadSelfies,
  removeSelfie,
} from './utils/screenshot';
import {
  blobToDataUrl,
  deleteVideoClip,
  downloadBlob,
  loadVideoClips,
  saveVideoClip,
} from './utils/videoClips';
import { FACEMESH_OCCLUDER_TRIANGLES, FACEMESH_VERTEX_COUNT } from './utils/facemeshOcclusion';
import { getTranslations } from './i18n/vto';

// ============================================
// PERFORMANCE NOTES
// ============================================
// Desktop is typically faster because MediaPipe Tasks can run with the GPU delegate,
// while mobile often prefers CPU delegate for stability on some WebGL stacks.

// ============================================
// LOW-END DEVICE DETECTION
// ============================================
// Detects if device is low-end mobile (e.g., Samsung A54, budget Android)
// These devices will use the desktop layout (smaller canvas) for better performance
const isLowEndMobile = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent.toLowerCase();
  
  // Only check on mobile devices
  const isMobileDevice = /android|iphone|ipad|ipod|mobile/i.test(ua);
  if (!isMobileDevice) return false;

  // iOS: Safari never includes model number in UA (e.g. "iPhone 15"), so treat all iPhone/iPad as high-end
  if (/iphone|ipad|ipod/i.test(ua)) return false;

  // Check for known high-end Android devices (these should use fullscreen mobile layout)
  const isHighEnd = 
    // Samsung S series (S20+, S21+, S22+, S23+, S24+, S25+)
    /sm-s9|sm-g9|sm-s[2-9][0-9][0-9]|galaxy\s*s2[0-9]|galaxy\s*s[3-9][0-9]/i.test(ua) ||
    // Samsung Z Fold/Flip
    /sm-f|galaxy\s*z/i.test(ua) ||
    // Google Pixel 6+
    /pixel\s*[6-9]/i.test(ua);

  if (isHighEnd) return false;

  // Check device capabilities
  const cores = navigator.hardwareConcurrency || 4;
  const memory = navigator.deviceMemory || 4; // Chrome only, defaults to 4

  // Low-end: less than 6 cores OR less than 6GB RAM
  if (cores < 6 || memory < 6) {
    return true;
  }

  // Check for known mid-range/low-end Samsung A series
  const isMidRangeSamsung = /sm-a[3-7]\d|galaxy\s*a[3-7]\d/i.test(ua);
  if (isMidRangeSamsung) {
    return true;
  }

  // Check for budget brands
  const isBudgetDevice = /redmi|poco|realme|oppo\s*a|vivo\s*y/i.test(ua);
  if (isBudgetDevice) {
    return true;
  }

  return false;
};

// Cache the result (computed once)
let cachedIsLowEndMobile = null;
const getIsLowEndMobile = () => {
  if (cachedIsLowEndMobile === null) {
    cachedIsLowEndMobile = isLowEndMobile();
    if (cachedIsLowEndMobile) {
      console.log('[VTO] Low-end mobile detected - using desktop layout for better performance');
    }
  }
  return cachedIsLowEndMobile;
};

// Check if device is any mobile (for disabling iris prediction)
let cachedIsMobileDevice = null;
const getIsMobileDevice = () => {
  if (cachedIsMobileDevice === null) {
    if (typeof navigator === 'undefined') {
      cachedIsMobileDevice = false;
    } else {
      const ua = navigator.userAgent.toLowerCase();
      cachedIsMobileDevice = /android|iphone|ipad|ipod|mobile/i.test(ua);
    }
  }
  return cachedIsMobileDevice;
};

// ============================================
// THREE.js loader cache (avoids re-downloading same URLs across texture loads)
// ============================================
THREE.Cache.enabled = true;

// ============================================
// IMAGE PREFETCH (warms browser HTTP cache before THREE.js loader requests it)
// ============================================
const _prefetchedUrls = new Set();
/** Fire-and-forget: tell the browser to fetch an image URL into HTTP cache. */
const prefetchImage = (url) => {
  if (!url || typeof url !== 'string' || _prefetchedUrls.has(url)) return;
  // Only preload http(s), absolute /, or blob — skip data URLs and bundled imports
  if (!url.startsWith('http') && !url.startsWith('/') && !url.startsWith('blob:')) return;
  _prefetchedUrls.add(url);
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = url;
};

const schedulePrefetchTask = (task) => {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    const idleId = window.requestIdleCallback(task, { timeout: 120 });
    return () => window.cancelIdleCallback?.(idleId);
  }

  const timeoutId = window.setTimeout(task, 48);
  return () => window.clearTimeout(timeoutId);
};

// ============================================
// HINGE POSITION CACHE (aspect-ratio-only calc, safe to memo per URL)
// ============================================
const _hingeCache = new Map(); // url -> { hingeX, hingeY }

// Three.js camera depth constants (must match the camera setup below)
const CAMERA_Z = 5;
const BASE_GLASSES_Z = 1;

// Small correction for sideways drift when turning (tune this).
// If drift goes the wrong way, flip the sign.
const YAW_TO_X_OFFSET = -0.09;

// Anchor glasses closer to the nose bridge so they feel "worn" when turning.
// 0 = use eyeCenter(168), 1 = use noseBridge(6)
// Higher value = glasses stick closer to face when turning
const NOSE_ANCHOR_BLEND = 0.88;
// Small vertical nudge (negative = down)
const NOSE_Y_OFFSET = -0.01;

// How much the frame yaws with your head. Higher = feels more "stuck" to face.
// Higher value needed for mobile where face detection can be less precise
const GLASSES_YAW_MULT = 1.0;

// Temple arm configuration
const TEMPLE_CONFIG = {
  lengthMm: 140,      // Default temple length in mm (standard glasses ~140mm)
  width: 4,           // Height of temple arm
  thickness: 0.03,    // 3D thickness (depth) of temple arm in world units
  color: 0x1a1a1a,    // Dark color matching the frame
  hingeOffset: 0.92,  // X offset from center where temples attach (slightly inward for overlap)
  yOffset: 0.35,      // Y offset for temple attachment point (approx frame top)
  handleHeight: 2,    // PNG handle plane height in world units (tune this)
  heightScale: 0.8,   // Reduce handle height by 20% for more natural appearance
  // Distance-based scaling: disabled for proportional scaling with frame
  referenceScale: 1.0,
  scaleSensitivity: 0, // Proportional - temples scale 1:1 with frame
};

// Mobile-specific adjustments
const MOBILE_GLASSES_Z_OFFSET = 0.27; // Extra offset to bring glasses closer to face on mobile
// Global "glued to face" bias for 2D overlay (smaller Z = closer to face plane)
const GLASSES_FACE_GLUE_Z_BIAS = 0.10;
// Reference Z used by our scale=1 "base frame pixels" math.
// Must match the Z we treat as our baseline pose (otherwise mobile gets a scale bias).
const BASE_GLASSES_Z_REF = BASE_GLASSES_Z - GLASSES_FACE_GLUE_Z_BIAS;

// Temple bending (for less "stiff/cardboard" feel)
const TEMPLE_BEND_SEGMENTS_HIGH = 28;
const TEMPLE_BEND_SEGMENTS_LOW = 14;
const TEMPLE_BEND_Z = 0.10;   // how much the temple curves back in Z (world units)
const TEMPLE_BEND_Y = 0.06;   // how much it drops down towards the ear (world units)

// Face occluder (depth-only) to prevent the far temple crossing over the face.
// Uses MediaPipe FaceMesh "face oval" landmark indices.
const FACE_OVAL_INDICES = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288,
  397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136,
  172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109,
];
const FACE_OCCLUDER_SCALE_MIN = 1.05;
const FACE_OCCLUDER_SCALE_MAX = 1.16;
const FACE_OCCLUDER_Z = 0.014;
const FACE_OCCLUDER_RETRIANGULATE_EVERY = 12; // frames (cheap: only 36 pts)
const FAR_TEMPLE_Z_PULL_MAX = 0.03;

// ============================================
// IPD-BASED SIZING CONSTANTS
// ============================================
// Frame geometry base dimensions
const FRAME_BASE_WORLD_UNITS = 2.0;  // Frame PlaneGeometry width
// Multi-stack planes for pseudo 3D thickness.
// Higher values look more volumetric (at a GPU cost). We keep a conservative base and bump up when 2D->3D look is enabled.
const FRAME_STACK_LAYERS_BASE = 10;
const FRAME_STACK_LAYERS_HIGH = 18;
const FRAME_DEPTH = 0.06;            // Total Z extent (world units)
// Temple/handle multistack: same "stretched" depth distribution as front frame; count matches previous (1 main + 7 inflated = 8)
const TEMPLE_STACK_LAYERS_BASE = 10;
const TEMPLE_STACK_LAYERS_HIGH = 16; // temples benefit from a bit less than front
const TEMPLE_DEPTH = 0.04;           // Total Z extent for temple thickness (world units)
const FRAME_OPACITY_STACK_THRESHOLD = 0.9; // Only stack (multi-layer) where texture alpha >= this; below = single layer for transparency

// Average adult interpupillary distance (IPD) in mm - used as reference for sizing
// Iris landmarks 468 (left) and 473 (right) when predictIrises: true
const AVERAGE_IPD_MM = 63;
// Average outer canthal (eye-to-eye) width in mm - used to estimate per-user PD from proportions
const AVERAGE_OUTER_CANTHAL_MM = 95;

// Standard frame width reference for mm-to-world-units conversion
const FRAME_REFERENCE_MM = 140;

// Conversion factor: mm to Three.js world units
// At scale=1.0, 2 world units should represent ~140mm frame WIDTH
const MM_TO_WORLD_UNITS = FRAME_BASE_WORLD_UNITS / FRAME_REFERENCE_MM;

// Temple length is calculated as ratio of frame width (see createTempleArm)
// No separate conversion factor needed - temples use same coordinate system as frame

// Calculate BASE_FRAME_PIXELS dynamically based on Three.js camera projection
// This ensures glasses appear the same real-world size across all devices/aspect ratios
const getBaseFramePixels = (canvasWidth, canvasHeight) => {
  const vFovRad = 75 * Math.PI / 180; // Camera vertical FOV (75 degrees)
  const aspect = canvasWidth / canvasHeight;
  const hFovRad = 2 * Math.atan(aspect * Math.tan(vFovRad / 2)); // Horizontal FOV from aspect
  // IMPORTANT: use BASE_GLASSES_Z_REF (matches the baseline we use in pose math).
  const distance = CAMERA_Z - BASE_GLASSES_Z_REF;
  
  // How many pixels the 2-world-unit frame spans at scale=1.0
  return FRAME_BASE_WORLD_UNITS * canvasWidth / (2 * distance * Math.tan(hFovRad / 2));
};

// Helper function to calculate 3D face rotation from landmarks
const calculateFaceRotation = (keypoints) => {
  // Key landmark indices for MediaPipe Face Mesh
  const noseTip = keypoints[1];         // Nose tip
  const noseBridge = keypoints[6];      // Nose bridge (between eyes)
  const leftEyeOuter = keypoints[33];   // Left eye outer corner
  const rightEyeOuter = keypoints[263]; // Right eye outer corner
  // Inner eye corners (indices 133, 362) available if needed for enhanced tracking
  const foreheadTop = keypoints[10];    // Forehead
  const chin = keypoints[152];          // Chin
  const leftCheek = keypoints[234];     // Left cheek
  const rightCheek = keypoints[454];    // Right cheek
  const leftEar = keypoints[127];       // Left ear area
  const rightEar = keypoints[356];      // Right ear area

  // Calculate YAW (left-right rotation)
  // Use the difference in Z depth between left and right sides
  const leftZ = (leftEyeOuter[2] + leftCheek[2] + leftEar[2]) / 3;
  const rightZ = (rightEyeOuter[2] + rightCheek[2] + rightEar[2]) / 3;
  const eyeWidth = Math.abs(rightEyeOuter[0] - leftEyeOuter[0]);
  
  // Yaw from depth difference
  const depthDiff = rightZ - leftZ;
  const yaw = Math.atan2(depthDiff, eyeWidth * 0.5) * 1.5;
  
  // Also use horizontal position asymmetry for additional yaw calculation
  const noseToLeftEye = Math.abs(noseBridge[0] - leftEyeOuter[0]);
  const noseToRightEye = Math.abs(noseBridge[0] - rightEyeOuter[0]);
  const asymmetryYaw = (noseToRightEye - noseToLeftEye) / eyeWidth * 0.8;
  
  // Combined yaw estimation
  const finalYaw = yaw * 0.4 + asymmetryYaw * 0.2;

  // Calculate PITCH (up-down rotation)
  // Use vertical relationship between nose tip, nose bridge, and face center
  const faceHeight = Math.abs(chin[1] - foreheadTop[1]);
  const noseBridgeToTip = noseTip[1] - noseBridge[1];
  const expectedNoseLength = faceHeight * 0.25;
  const pitch = Math.atan2(noseBridgeToTip - expectedNoseLength, faceHeight * 0.3) * 0.5;

  // Calculate ROLL (tilt).
  //
  // Problem with eye-line only: when the face tilts strongly (~45°+) the eye landmarks
  // compress toward each other due to perspective, making the measured eye-line angle
  // smaller than the actual head tilt.  At high yaw, the eye-line also becomes unreliable.
  //
  // Solution: blend eye-line roll with a vertical-axis roll estimate (chin→forehead angle),
  // which is unaffected by yaw-induced eye compression and gives full-range roll tracking.

  // Primary: eye-line angle
  const eyeLine = new THREE.Vector2(
    rightEyeOuter[0] - leftEyeOuter[0],
    rightEyeOuter[1] - leftEyeOuter[1]
  );
  const rollEye = Math.atan2(eyeLine.y, eyeLine.x);

  // Secondary: vertical midline angle (chin → forehead).
  // In neutral pose this is (0, -faceHeight) i.e. pointing straight up.
  // When face tilts, the midline vector rotates by the same roll angle.
  // atan2(dx, -dy) gives the tilt of the midline relative to vertical.
  const midX = foreheadTop[0] - chin[0];
  const midY = foreheadTop[1] - chin[1]; // negative = upward
  const rollMid = Math.atan2(midX, -midY); // positive = tilt right (matches eye convention)

  // At low yaw the eye-line is very accurate; at high yaw blend toward midline.
  // Midline blend also kicks in at low tilts (|rollEye| > 10°) because perspective compression
  // causes the eye-line to under-report tilt even at moderate angles (~30-40°).
  const yawBlendT = Math.min(1, Math.abs(finalYaw) / (Math.PI / 6)); // 0→30° yaw → 0→1
  const tiltBlendT = Math.min(1, Math.max(0, (Math.abs(rollEye) - 0.17) / 0.35)); // 10°→30° tilt → 0→1
  const blendT = Math.max(yawBlendT * 0.5, tiltBlendT);
  // rollMid naturally has full range (no perspective compression), but can be noisier.
  // At high tilts, weight it more heavily so glasses truly follow the lean.
  const roll = rollEye * (1 - blendT) + rollMid * blendT;

  return { yaw: finalYaw, pitch, roll };
};

// const dist2D = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]); // (unused)

// Map a FaceMesh point (in the tracker/video pixel space) into displayed view coords.
// Supports both `objectFit: cover` (crop) and `contain` (letterbox) layouts + mirroring,
// so overlay doesn't drift near edges.
const mapVideoPointToView = (p, videoW, videoH, viewW, viewH, mirrored, fit = 'cover') => {
  const cover = fit !== 'contain';
  const s = cover ? Math.max(viewW / videoW, viewH / videoH) : Math.min(viewW / videoW, viewH / videoH);
  const dispW = videoW * s;
  const dispH = videoH * s;
  const offsetX = (viewW - dispW) / 2;
  const offsetY = (viewH - dispH) / 2;

  let x = p[0] * s + offsetX;
  const y = p[1] * s + offsetY;
  if (mirrored) x = viewW - x;

  return [x, y];
};

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const lerp01 = (a, b, t) => a + (b - a) * clamp01(t);
const smoothstep01 = (edge0, edge1, x) => {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
};

const createTempleStabilityState = () => ({
  init: false,
  lastT: 0,
  rightIsFar: false,
  leftAngleY: 0,
  rightAngleY: 0,
  leftAngleZ: 0,
  rightAngleZ: 0,
  lengthScale: 1,
});

const angleDegAt = (a, b, c) => {
  // angle ABC in degrees
  const abx = a[0] - b[0];
  const aby = a[1] - b[1];
  const cbx = c[0] - b[0];
  const cby = c[1] - b[1];
  const abLen = Math.hypot(abx, aby) || 1e-6;
  const cbLen = Math.hypot(cbx, cby) || 1e-6;
  const dot = (abx * cbx + aby * cby) / (abLen * cbLen);
  const ang = Math.acos(Math.max(-1, Math.min(1, dot)));
  return (ang * 180) / Math.PI;
};

// 5-type face shape heuristic with confidence (oval / round / square / heart / oblong).
// Not biometric; just rough geometry. Returns { shape, confidence, quality, valid, scores, features }.
// Uses FaceMesh 3D landmarks to measure in a face-local coordinate system (more stable under head pose).
const estimateFaceShape4 = (keypoints, videoW, videoH) => {
  const FACE_OVAL_IDX = [
    10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377,
    152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109, 10,
  ];

  // If we don't have enough points, bail out gracefully.
  if (!Array.isArray(keypoints) || keypoints.length < 200) {
    return { shape: 'oval', confidence: 0, quality: 0, valid: false, scores: {}, features: {} };
  }

  // --- small vector helpers (keypoints are [x,y,z?]) ---
  const p3 = (p) => (p ? [p[0] || 0, p[1] || 0, p[2] || 0] : [0, 0, 0]);
  const sub3 = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
  const dot3 = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const cross3 = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  const len3 = (v) => Math.hypot(v[0], v[1], v[2]) || 1e-6;
  const norm3 = (v) => {
    const l = len3(v);
    return [v[0] / l, v[1] / l, v[2] / l];
  };

  // --- stable anchor points ---
  const eyeL = keypoints[33] ?? keypoints[234];
  const eyeR = keypoints[263] ?? keypoints[454];
  const originP = keypoints[168] ?? keypoints[1] ?? keypoints[4];
  const chinP = keypoints[152] ?? keypoints[200];
  if (!eyeL || !eyeR || !originP || !chinP) {
    return { shape: 'oval', confidence: 0, quality: 0, valid: false, scores: {}, features: {} };
  }

  // --- build a face-local orthonormal basis (x: left->right eyes, y: down, z: out of face plane) ---
  const o = p3(originP);
  const lE = p3(eyeL);
  const rE = p3(eyeR);
  const chin = p3(chinP);
  const xAxis = norm3(sub3(rE, lE));
  const yTemp = norm3(sub3(chin, o));
  const zAxisRaw = cross3(xAxis, yTemp);
  const zLen = len3(zAxisRaw);
  if (!Number.isFinite(zLen) || zLen < 1e-3) {
    return { shape: 'oval', confidence: 0, quality: 0, valid: false, scores: {}, features: {} };
  }
  const zAxis = norm3(zAxisRaw);
  const yAxis = norm3(cross3(zAxis, xAxis));

  const toLocal = (p) => {
    const v = sub3(p3(p), o);
    return [dot3(v, xAxis), dot3(v, yAxis), dot3(v, zAxis)];
  };

  // --- quality / pose proxy (mainly yaw/roll) ---
  const eyeDist = Math.max(1e-6, len3(sub3(rE, lE)));
  const yawProxy = Math.abs((rE[2] || 0) - (lE[2] || 0)) / eyeDist;
  const rollRad = Math.atan2((eyeR?.[1] ?? 0) - (eyeL?.[1] ?? 0), (eyeR?.[0] ?? 0) - (eyeL?.[0] ?? 0));
  const yawQ = 1 - smoothstep01(0.10, 0.35, yawProxy);
  const rollQ = 1 - smoothstep01(0.20, 0.65, Math.abs(rollRad));
  const quality = clamp01(yawQ * rollQ);

  // --- compute widths/heights in face-local space ---
  const oval = FACE_OVAL_IDX.map((i) => keypoints[i]).filter(Boolean).map((p) => toLocal(p));
  if (!oval.length) return { shape: 'oval', confidence: 0, quality: 0, valid: false, scores: {}, features: {} };

  const ys = oval.map((p) => p[1]);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const faceHeight = Math.max(1e-6, maxY - minY);

  const widthAtY = (yTarget) => {
    const byY = oval
      .map((p) => ({ p, dy: Math.abs(p[1] - yTarget) }))
      .sort((a, b) => a.dy - b.dy)
      .slice(0, 12)
      .map((x) => x.p);
    const left = byY.reduce((best, p) => (p[0] < best[0] ? p : best), byY[0] || [0, 0, 0]);
    const right = byY.reduce((best, p) => (p[0] > best[0] ? p : best), byY[0] || [0, 0, 0]);
    return Math.max(1e-6, right[0] - left[0]);
  };

  // Slightly lower "forehead" band to reduce hairline sensitivity.
  const yTop = lerp01(minY, maxY, 0.28);
  const yMid = lerp01(minY, maxY, 0.52);
  const yJaw = lerp01(minY, maxY, 0.80);

  const foreheadWidth = widthAtY(yTop);
  const midWidth = widthAtY(yMid);
  const jawWidth = widthAtY(yJaw);

  // Jaw/chin geometry in face-local plane (stable under roll/yaw).
  const leftJawP = keypoints[172] ?? keypoints[127];
  const rightJawP = keypoints[397] ?? keypoints[356];
  const chinLocal = toLocal(keypoints[152] ?? chinP);
  const leftJaw = leftJawP ? toLocal(leftJawP) : null;
  const rightJaw = rightJawP ? toLocal(rightJawP) : null;
  const jawAngle = leftJaw && rightJaw ? angleDegAt(leftJaw, chinLocal, rightJaw) : 100;

  const safeCheek = Math.max(1e-6, midWidth);
  const lengthRatio = faceHeight / safeCheek;
  const jawRatio = jawWidth / safeCheek;
  const foreheadRatio = foreheadWidth / safeCheek;
  const widthVariance = Math.abs(foreheadWidth - jawWidth) / safeCheek;

  const scores = {
    // Scoring (0..1). These are heuristics to be stable.
    heart: clamp01(
      0.48 * smoothstep01(1.00, 1.10, foreheadRatio) +
        0.28 * (1 - smoothstep01(0.92, 1.02, jawRatio)) +
        0.24 * (1 - smoothstep01(85, 110, jawAngle))
    ),
    oval: clamp01(
      0.55 * smoothstep01(1.20, 1.55, lengthRatio) +
        0.20 * (1 - smoothstep01(0.92, 1.02, jawRatio)) +
        0.15 * smoothstep01(0.05, 0.18, widthVariance) +
        0.10 * smoothstep01(88, 115, jawAngle)
    ),
    round: clamp01(
      0.55 * (1 - smoothstep01(1.18, 1.38, lengthRatio)) +
        0.20 * smoothstep01(0.96, 1.06, jawRatio) +
        0.15 * smoothstep01(0.00, 0.10, widthVariance) +
        0.10 * smoothstep01(98, 125, jawAngle)
    ),
    square: clamp01(
      0.45 * smoothstep01(0.98, 1.08, jawRatio) +
        0.25 * smoothstep01(1.15, 1.45, lengthRatio) +
        0.20 * smoothstep01(0.00, 0.10, widthVariance) +
        0.10 * smoothstep01(85, 110, jawAngle)
    ),
    oblong: clamp01(
      0.60 * smoothstep01(1.45, 1.80, lengthRatio) +
        0.20 * (1 - smoothstep01(0.06, 0.18, widthVariance)) +
        0.20 * smoothstep01(0.96, 1.06, jawRatio)
    ),
  };

  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const shape = sorted[0][0];
  const best = sorted[0][1];
  const second = sorted[1]?.[1] ?? 0;
  // Confidence: how much the best wins vs runner-up, with a quality penalty for strong head pose.
  const confidence = clamp01(((best - second) / 0.55) * (0.35 + 0.65 * quality));
  // Only accept samples for locking when quality is decent (prevents "wrong early lock" on yawed faces).
  const valid = quality >= 0.55 && best >= 0.35;

  return {
    shape,
    confidence,
    quality,
    valid,
    scores,
    features: { lengthRatio, jawRatio, foreheadRatio, widthVariance, jawAngle, yawProxy, rollRad },
  };
};

const FACE_SHAPE_LABEL_BA = {
  oval: 'Ovalno',
  round: 'Okruglo',
  square: 'Kvadratno',
  heart: 'Srcoliko',
  oblong: 'Izduženo',
};

// Face-shape locking behavior (decide once at start)
const FACE_SHAPE_LOCK = {
  window: 24,        // rolling window size
  minFrames: 12,     // need at least this many frames
  minWinRatio: 0.70, // stricter dominance to avoid wrong locks
  minAvgConf: 0.55,  // stricter confidence threshold
};

// PD locking: set once from initial samples, then never change
const PD_LOCK = {
  minSamples: 12,    // need at least this many valid samples before locking
  windowSize: 15,    // rolling window for initial average
};

// PD display: iris-based estimate with uncertainty (avoid misleading hard numbers)
const IRIS_DIAMETER_MM = 11.7; // average human iris diameter; used as rough calibration
// Face width calibration (bizygomatic width) via iris-as-ruler.
// We only calibrate when looking straight (low yaw) to avoid foreshortening.
const FACE_WIDTH_CALIB = {
  yawMaxRad: 0.14,        // ~8°
  rollMaxRad: 0.18,       // ~10°
  pitchMaxRad: 0.18,      // ~10°
  minSamples: 10,
  windowSize: 18,
};
const PD_ESTIMATE = {
  minSamples: 12,
  windowSize: 24,
  maxStdDevMm: 2.5,     // require stability before showing
  maxYawRad: 0.35,      // ~20deg; larger yaw makes 2D distances unreliable
  minConfidenceToShow: 0.65,
  maxUncertaintyToShow: 3, // if >3mm, hide number (show \"Measuring...\" instead)
};

// Fit toast: show fit quality based on size + face shape matching
// Size matching: compare glasses frameWidth to user's measured PD
// Rule of thumb: ideal frame width ≈ face width, which correlates with PD
// Small PD (< 58mm) → prefer narrower frames (< 135mm)
// Medium PD (58-66mm) → medium frames (130-145mm)
// Large PD (> 66mm) → wider frames (> 140mm)
const FIT_TYPE = {
  PERFECT: 'perfect',     // matches both size AND face shape
  SIZE_ONLY: 'size',      // matches size but NOT face shape
  SHAPE_ONLY: 'shape',    // matches face shape but NOT size
  NONE: null,             // neither matches (don't show toast)
};

const FIT_TOAST_CONFIG = {
  [FIT_TYPE.PERFECT]: {
    labelKey: 'fitPerfect',
    fallbackLabel: 'Savršen izbor',
    color: '#10b981',           // green
    // Strong (opaque) background so it stays readable over video/watermark
    bgColor: '#06241b',
    borderColor: '#10b981',
  },
  [FIT_TYPE.SIZE_ONLY]: {
    labelKey: 'fitSize',
    fallbackLabel: 'Tvoj broj',
    color: '#3b82f6',           // blue
    bgColor: '#071a33',
    borderColor: '#3b82f6',
  },
  [FIT_TYPE.SHAPE_ONLY]: {
    labelKey: 'fitShape',
    fallbackLabel: 'Za tvoj oblik lica',
    color: '#a855f7',           // purple
    bgColor: '#1c1030',
    borderColor: '#a855f7',
  },
};

// Size matching thresholds (frameWidth in mm, PD in mm)
const getSizeMatch = (frameWidth, pd) => {
  if (pd == null || frameWidth == null) return false;
  // Heuristic: face width ≈ PD * 2.1 to 2.3
  // Ideal frame should be close to face width
  // Allow ±15mm tolerance for "good fit"
  const estimatedFaceWidth = pd * 2.2;
  const diff = Math.abs(frameWidth - estimatedFaceWidth);
  return diff <= 18; // 18mm tolerance
};

const getEffectiveFrameWidthMm = (frameWidthMm, handleWidthMm, bridgeLengthCm) => {
  const rawFrame = Number(frameWidthMm);
  const rawHandle = Number(handleWidthMm);
  const rawBridgeCm = Number(bridgeLengthCm);
  const fallback = Number.isFinite(rawFrame) && rawFrame > 0 ? rawFrame : FRAME_REFERENCE_MM;

  const bridgeMm = Number.isFinite(rawBridgeCm) && rawBridgeCm > 0 ? rawBridgeCm * 10 : 18;

  // Some catalogs store a narrow front metric (e.g. 110) that does not represent full outer frame width.
  // Lift those values using bridge + endpiece allowance so AR sizing matches real S/M/L perception.
  if (fallback < 120) {
    const inferredFromBridge = fallback + bridgeMm + 12;
    if (Number.isFinite(rawHandle) && rawHandle > 90) {
      const nearTemple = Math.max(inferredFromBridge, rawHandle - 4);
      return Math.min(rawHandle + 10, nearTemple);
    }
    return inferredFromBridge;
  }

  // If front width is much smaller than temple length, treat it as underreported and lift it
  // toward a realistic front-vs-temple proportion so S/M/L differences render correctly.
  if (Number.isFinite(rawHandle) && rawHandle > 90 && fallback < rawHandle - 10) {
    const lifted = rawHandle - 4;
    return Math.max(fallback, lifted);
  }

  return fallback;
};

const normalizeCategory = (value) => {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return null;
  if (['sunglasses', 'sun', 'sun-glasses', 'sun_glasses'].includes(v)) return 'sunglasses';
  if (['dioptric', 'prescription', 'optical', 'eyeglasses', 'glasses'].includes(v)) return 'dioptric';
  return null;
};

const normalizeGender = (value) => {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return null;
  if (['male', 'men', 'man', 'm'].includes(v)) return 'male';
  if (['female', 'women', 'woman', 'f'].includes(v)) return 'female';
  if (['unisex', 'uni'].includes(v)) return 'unisex';
  return null;
};

const matchesGenderWithUnisex = (selectedGender, itemGender) => {
  if (!selectedGender) return true;
  if (!itemGender) return false;
  if (selectedGender === 'male' || selectedGender === 'female') {
    return itemGender === selectedGender || itemGender === 'unisex';
  }
  // If selected is unisex, show all genders in the same category.
  if (selectedGender === 'unisex') return true;
  return itemGender === selectedGender;
};

// Mobile: use actual viewport dimensions (100vw x 100vh) - like native camera app
const MOBILE_DYNAMIC_CANVAS = true; // true = viewport size, false = 800x600 with CSS scale

const VirtualTryOn = ({ catalog: customCatalog, embedded = false, showCatalog = true, locale: localeProp, translations, initialSelectedGlassesId = null, enableCatalogPrefetch = true, onSelectedGlassesLoaded = null }) => {
  const t = translations || getTranslations(localeProp || 'en');
  // Use custom catalog if provided, otherwise use default
  const activeCatalog = customCatalog && customCatalog.length > 0 ? customCatalog : GLASSES_CATALOG;
  
  // Helper functions that use the active catalog - memoized to avoid dependency warnings
  const getGlassesFromCatalog = useCallback((id) => {
    return activeCatalog.find((g) => g.id === id) ?? activeCatalog[0];
  }, [activeCatalog]);
  
  const getSimilarFromCatalog = useCallback(({ selectedId, faceShape: shape, faceShapeLocked: locked }) => {
    const others = activeCatalog.filter((g) => g.id !== selectedId);
    if (!locked || !shape) return others;
    const preferred = others.filter((g) => (g.recommendedForFaceShapes || []).includes(shape));
    return preferred.length ? preferred : others;
  }, [activeCatalog]);
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const scanCanvasRef = useRef(null);
  // Cosmetic scan overlay: show briefly while "initializing" face analysis.
  const scanOverlayUntilRef = useRef(0);
  const trackingCanvasRef = useRef(null); // offscreen: fixed-size, cover-cropped input for trackers on mobile
  const watermarkCanvasRef = useRef(null);
  const stageRef = useRef(null);
  const cameraBoxRef = useRef(null); // on mobile: smaller "camera box" inside fullscreen stage
  const headerRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const sceneRef = useRef(null);
  const envMapRef = useRef(null); // PMREM env texture
  const pmremRef = useRef(null);
  const textureLoaderRef = useRef(null);
  const gltfLoaderRef = useRef(null);
  const ktx2LoaderRef = useRef(null);
  const dracoLoaderRef = useRef(null);
  const frameMeshRef = useRef(null);
  const glbModelRef = useRef(null); // Current loaded GLB 3D model
  const currentAssetTypeRef = useRef('2d'); // Track current asset type
  const leftTempleRef = useRef(null);
  const rightTempleRef = useRef(null);
  const faceOccluderRef = useRef(null); // depth-only mesh to occlude far temple
  const faceOccluderPosAttrRef = useRef(null); // BufferAttribute for fast updates
  const faceOccluderIndexAttrRef = useRef(null); // BufferAttribute for fast index updates (triangulation)
  const faceOccluderTriCounterRef = useRef(0);
  const lastEnvEstimateRef = useRef({ t: 0, exp: 1.0, rgb: [1, 1, 1] });
  const envSampleCanvasRef = useRef(null);
  const cameraMatchRef = useRef(null); // hooks/debug: luma/noise/tint (camera artifact matching)
  const dynamicEnvRTRef = useRef(null);
  const noWebglFallbackRef = useRef(false);
  const fallbackFrameImageRef = useRef(null);
  const fallbackFrameSrcRef = useRef(null);
  const ambientLightRef = useRef(null);
  const keyLightRef = useRef(null);
  const fillLightRef = useRef(null);
  const activeFrameTextureRef = useRef(null);
  const activeHandleTextureRef = useRef(null);
  const activeFrameNormalMapRef = useRef(null);
  const activeHandleNormalMapRef = useRef(null);
  const glassesLoadTokenRef = useRef(0);
  const normalMapCacheRef = useRef(new Map()); // src -> THREE.Texture (CanvasTexture)
  const handleTextureCacheRef = useRef(new Map()); // handleSrc -> THREE.Texture (reused across models)
  const pendingFrameTextureSrcRef = useRef(null);
  const setFrameTextureSrcRef = useRef(null);
  const currentFrameWidthRef = useRef(140); // Current product frame width in mm (default 140mm)
  const currentHandleWidthRef = useRef(140); // Current product temple length in mm
  const currentFrontContentScaleXRef = useRef(1); // Visible content width ratio (alpha bounds), 1 = no padding
  const currentHandleContentScaleXRef = useRef(1); // Visible content length ratio (alpha bounds), 1 = no padding
  const currentScaleCorrectionRef = useRef(1); // Manual multiplier (1 = none)
  const currentHingeOffsetRef = useRef(0.95); // Where temples attach (0-1, edge=1)
  const currentTempleYOffsetRef = useRef(0.02); // Vertical offset of temples
  const currentTempleLengthRef = useRef(3); // Temple arm length
  const [model, setModel] = useState(null);
  const [glassesGroup, setGlassesGroup] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGlassesAssetLoading, setIsGlassesAssetLoading] = useState(false);
  const [hasInitialGlassesLoaded, setHasInitialGlassesLoaded] = useState(false);
  const [faceShape, setFaceShape] = useState(null);
  const [faceShapeConfidence, setFaceShapeConfidence] = useState(0);
  const [faceShapeLocked, setFaceShapeLocked] = useState(false);
  // Fit toast: shows fit quality (perfect/size/shape) in top-right corner - persistent while model selected
  const [fitToast, setFitToast] = useState({ open: false, type: null });
  const [calibratedFaceWidthMm, setCalibratedFaceWidthMm] = useState(null);
  const faceWidthCalibRef = useRef({
    ratio: null,
    faceWidthMm: null,
    samples: [],
    locked: false,
  });
  // Initialize mobile detection and stage size synchronously to avoid flash/resize
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    const isSmallScreen = window.matchMedia('(max-width: 640px)').matches;
    return isSmallScreen;
  });
  const [stageSize, setStageSize] = useState(() => {
    if (typeof window === 'undefined') return { w: 800, h: 600 };
    const mobile = window.matchMedia('(max-width: 640px)').matches;
    const useMobileLayout = mobile;
    if (useMobileLayout && MOBILE_DYNAMIC_CANVAS) {
      const vw = window.innerWidth || 800;
      const vh = window.visualViewport?.height || window.innerHeight || 600;
      return { w: Math.round(vw), h: Math.round(vh) };
    }
    return { w: 800, h: 600 };
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // slide-out menu
  const [useClipOn, setUseClipOn] = useState(false); // toggle clip-on attachment view
  // Mobile canvas should match device dimensions; do not apply any extra "zoom" scaling.
  const [selectedGlassesId, setSelectedGlassesId] = useState(() => {
    try {
      return initialSelectedGlassesId || localStorage.getItem('vto:selectedGlassesId') || activeCatalog[0]?.id || 'sunglasses_black';
    } catch {
      return initialSelectedGlassesId || activeCatalog[0]?.id || 'sunglasses_black';
    }
  });
  const [selfies, setSelfies] = useState(() => loadSelfies());
  const [activeSelfieId, setActiveSelfieId] = useState(() => {
    try {
      const first = loadSelfies()?.[0]?.id || null;
      return first;
    } catch {
      return null;
    }
  });
  const [videoClips, setVideoClips] = useState([]);
  const [activeClipId, setActiveClipId] = useState(null);
  const [isRecordingClip, setIsRecordingClip] = useState(false);
  const [isRenderingVideo, setIsRenderingVideo] = useState(false);
  const [clipThumbs, setClipThumbs] = useState({}); // id -> dataUrl
  
  // Compare state (for screenshots only)
  const [compareAId, setCompareAId] = useState(null);
  const [compareBId, setCompareBId] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareRenders, setCompareRenders] = useState({ a: null, b: null, combined: null, loading: false });
  const [comparePicker, setComparePicker] = useState({ open: false, aId: null });
  const [comparingWithSelfieId, setComparingWithSelfieId] = useState(null); // Selfie ID to compare with
  const compareCatalog = useMemo(() => {
    if (!Array.isArray(activeCatalog) || activeCatalog.length <= 1) return activeCatalog || [];

    const selected = activeCatalog.find((g) => g.id === selectedGlassesId) || activeCatalog[0];
    if (!selected) return activeCatalog;

    const selectedCategory = normalizeCategory(selected.category);
    const selectedGender = normalizeGender(selected.gender);

    // No metadata on selected item: keep previous behavior and show all models.
    if (!selectedCategory && !selectedGender) return activeCatalog;

    const filtered = activeCatalog.filter((item) => {
      if (item.id === selected.id) return true;

      const itemCategory = normalizeCategory(item.category);
      const itemGender = normalizeGender(item.gender);

      if (selectedCategory) {
        if (!itemCategory || itemCategory !== selectedCategory) return false;
      }

      if (selectedGender) { 
        if (!matchesGenderWithUnisex(selectedGender, itemGender)) return false;
      }

      return true;
    });

    return filtered.length ? filtered : [selected];
  }, [activeCatalog, selectedGlassesId]);

  // Progressive reveal: render 6 thumbnails at a time so the catalog
  // paints in fast batches instead of blocking on all items at once.
  const [catalogRevealCount, setCatalogRevealCount] = useState(6);
  useEffect(() => {
    setCatalogRevealCount(6);
    if (!compareCatalog || compareCatalog.length <= 6) return;
    let count = 6;
    const id = setInterval(() => {
      count += 6;
      setCatalogRevealCount(count);
      if (count >= compareCatalog.length) clearInterval(id);
    }, 100);
    return () => clearInterval(id);
  }, [compareCatalog]);

  // Gallery modal state
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryTab, setGalleryTab] = useState('photos'); // 'photos' | 'videos'
  
  // Long-press recording state (direct MediaRecorder approach)
  const [isLongPressing, setIsLongPressing] = useState(false);
  const [recordingProgress, setRecordingProgress] = useState(0); // 0-100
  const longPressTimerRef = useRef(null);
  const shutterPointerActiveRef = useRef(false);
  const recordingStartTimeRef = useRef(null);
  const isLongPressingRef = useRef(false);
  const isProcessingVideoRef = useRef(false);
  
  // Direct recording refs (MediaRecorder approach)
  const recordingCanvasRef = useRef(null); // Combined canvas for recording
  const mainRecorderRef = useRef(null); // MediaRecorder for glasses video
  const rawRecorderRef = useRef(null); // MediaRecorder for raw webcam
  const mainChunksRef = useRef([]); // Chunks for glasses video
  const rawChunksRef = useRef([]); // Chunks for raw webcam
  const recordingMimeRef = useRef(null); // MIME that worked for recording (used for blob type)
  const recordingAnimFrameRef = useRef(null); // Animation frame for canvas updates
  const recordingFirstFrameRef = useRef(null); // First frame blob for gallery thumbnail
  const recordingAudioStreamRef = useRef(null); // Optional mic stream (audio-only) for video clips
  const recordingPermissionPromiseRef = useRef(null); // Pending mic permission request (if any)
  const recordingPermissionAskedRef = useRef(false); // Avoid repeated prompts after explicit deny
  const glassesStripRef = useRef(null); // Horizontal strip scroll container (mobile)

  // PD (Pupillary Distance) measurement state — set once from initial samples, then locked
  const [measuredPD, setMeasuredPD] = useState(null);
  const [pdUncertainty, setPdUncertainty] = useState(null); // ± mm (only when PD is shown)
  const [pdConfidence, setPdConfidence] = useState(0); // 0..1
  const measuredPDRef = useRef(null);
  const pdSamplesRef = useRef([]);
  const pdLockedRef = useRef(false);

  // ============================================
  // FEATURE FLAGS (rollout / quick rollback)
  // Enable via localStorage: vto:flag:<name> = '0' | '1'
  // Or URL param: ?vtoFlag_<name>=0|1
  // ============================================
  const [vtoFlags] = useState(() => {
    const readBool = (name, defaultValue) => {
      try {
        if (typeof window === 'undefined') return defaultValue;
        const qs = new URLSearchParams(window.location.search || '');
        const qv = qs.get(`vtoFlag_${name}`);
        if (qv === '0' || qv === '1') return qv === '1';
        const lv = localStorage.getItem(`vto:flag:${name}`);
        if (lv === '0' || lv === '1') return lv === '1';
        return defaultValue;
      } catch {
        return defaultValue;
      }
    };
    return {
      assetContentScaleCorrection: readBool('assetContentScaleCorrection', true),
      pdEstimateWithUncertainty: readBool('pdEstimateWithUncertainty', true),
      toneMapping2DDisabled: readBool('toneMapping2DDisabled', true),
      // Keep 2D assets visually faithful to source textures (less synthetic glare/reflection).
      twoD3DLook: readBool('twoD3DLook', false),
      // Disable environment reflections by default for a flatter, color-faithful eyewear look.
      reflectionsEnabled: readBool('reflectionsEnabled', false),
      // Extra speed knob: reduces tracking update rate on ALL devices (including high-end),
      // to lower CPU/GPU load and keep the UI smoother.
      // Enable via `?vtoFlag_fastTracking=1` or `localStorage.setItem('vto:flag:fastTracking','1')`.
      fastTracking: readBool('fastTracking', false),
      // Sizing: treat `frameWidthMm` as hinge-to-hinge width (more correct than using the full image width),
      // and adjust scaling by the current hinge offset.
      // Disable with `?vtoFlag_hingeBasedSizing=0` if you prefer legacy sizing.
      hingeBasedSizing: readBool('hingeBasedSizing', true),
      // Aggressive responsiveness mode (cannot be true 0ms, but minimizes perceived lag).
      // Disable with `?vtoFlag_ultraLowLatency=0` if you prefer steadier smoothing.
      // Default OFF: ultra-low-latency disables some quality/stability knobs and can cause sizing/crop drift on mobile.
      // Default ON on mobile for responsiveness (60fps + reduced smoothing/overlays).
      ultraLowLatency: readBool('ultraLowLatency', getIsMobileDevice()),
      // Prefer GPU delegate on mobile (where stable). Default is conservative (CPU-first) to avoid
      // cases where GPU init hangs on some mobile WebGL stacks.
      // Override via `?vtoFlag_mobileGpuFirst=0|1` or `localStorage vto:flag:mobileGpuFirst`.
      mobileGpuFirst: readBool('mobileGpuFirst', false),
    };
  });

  const createBevelNormalMapFromAlpha = useCallback((img, opts = {}) => {
    const {
      size = 256,
      blurPx = 2,
      strength = 2.0, // higher = stronger bevel
      alphaThreshold = 10,
    } = opts;

    const w0 = img?.width || img?.naturalWidth || 0;
    const h0 = img?.height || img?.naturalHeight || 0;
    if (!w0 || !h0) return null;

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    // Draw alpha mask (blurred) into grayscale heightmap
    ctx.clearRect(0, 0, size, size);
    ctx.filter = blurPx > 0 ? `blur(${blurPx}px)` : 'none';
    ctx.drawImage(img, 0, 0, size, size);
    ctx.filter = 'none';

    let id;
    try {
      id = ctx.getImageData(0, 0, size, size);
    } catch {
      return null;
    }
    const src = id.data;

    // Height is alpha (0..1), thresholded slightly
    const h = new Float32Array(size * size);
    for (let i = 0; i < size * size; i++) {
      const a = src[i * 4 + 3];
      h[i] = a > alphaThreshold ? a / 255 : 0;
    }

    const out = ctx.createImageData(size, size);
    const dst = out.data;

    const idx = (x, y) => y * size + x;
    const sample = (x, y) => {
      const xx = Math.max(0, Math.min(size - 1, x));
      const yy = Math.max(0, Math.min(size - 1, y));
      return h[idx(xx, yy)];
    };

    // Sobel-like gradient on heightmap → normal
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const hl = sample(x - 1, y);
        const hr = sample(x + 1, y);
        const hu = sample(x, y - 1);
        const hd = sample(x, y + 1);

        const dx = (hr - hl) * strength;
        const dy = (hd - hu) * strength;

        // Normal points out of height field
        let nx = -dx;
        let ny = -dy;
        let nz = 1.0;
        const len = Math.hypot(nx, ny, nz) || 1;
        nx /= len; ny /= len; nz /= len;

        const o = (y * size + x) * 4;
        dst[o] = Math.round((nx * 0.5 + 0.5) * 255);
        dst[o + 1] = Math.round((ny * 0.5 + 0.5) * 255);
        dst[o + 2] = Math.round((nz * 0.5 + 0.5) * 255);
        // Keep alpha from original (so we can alphaTest/transparent consistently)
        dst[o + 3] = Math.round(Math.max(0, Math.min(255, h[idx(x, y)] * 255)));
      }
    }

    ctx.putImageData(out, 0, 0);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.NoColorSpace;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.needsUpdate = true;
    return tex;
  }, []);

  const getOrCreateBevelNormalMap = useCallback((key, img, opts) => {
    if (!key || !img) return null;
    const cache = normalMapCacheRef.current;
    if (cache?.has(key)) return cache.get(key);
    const tex = createBevelNormalMapFromAlpha(img, opts);
    if (tex && cache) cache.set(key, tex);
    return tex;
  }, [createBevelNormalMapFromAlpha]);
  
  // ============================================
  // DEBUG / DIAGNOSTICS (feature-flagged)
  // Enable via: localStorage.setItem('vto:debug','1') OR URL param ?vtoDebug=1
  // ============================================
  const [debugEnabled, setDebugEnabled] = useState(() => {
    try {
      if (typeof window === 'undefined') return false;
      const qs = new URLSearchParams(window.location.search || '');
      if (qs.get('vtoDebug') === '1') return true;
      return localStorage.getItem('vto:debug') === '1';
    } catch {
      return false;
    }
  });
  const debugInfoRef = useRef(null); // updated in the render loop
  const [debugSnapshot, setDebugSnapshot] = useState(null); // throttled for UI rendering

  useEffect(() => {
    if (!debugEnabled) return;
    const id = window.setInterval(() => {
      try {
        setDebugSnapshot(debugInfoRef.current ? { ...debugInfoRef.current } : null);
      } catch {
        // ignore
      }
    }, 250);
    return () => window.clearInterval(id);
  }, [debugEnabled]);

  const downloadDiagnosticsJson = useCallback(() => {
    try {
      const currentGlasses = activeCatalog.find((g) => g.id === selectedGlassesId) || activeCatalog[0] || null;
      const payload = {
        ts: new Date().toISOString(),
        ua: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        selectedGlassesId: selectedGlassesId || null,
        selectedGlasses: currentGlasses ? {
          id: currentGlasses.id ?? null,
          name: currentGlasses.name ?? null,
          assetType: currentGlasses.assetType ?? null,
          frameWidth: currentGlasses.frameWidth ?? null,
          handleWidth: currentGlasses.handleWidth ?? null,
          templeHingeOffset: currentGlasses.templeHingeOffset ?? null,
          templeYOffset: currentGlasses.templeYOffset ?? null,
          templeLength: currentGlasses.templeLength ?? null,
          modelUrlPreview: currentGlasses.modelUrl ? String(currentGlasses.modelUrl).slice(0, 160) : null,
          srcPreview: currentGlasses.src ? String(currentGlasses.src).slice(0, 160) : null,
          handleSrcPreview: currentGlasses.handleSrc ? String(currentGlasses.handleSrc).slice(0, 160) : null,
        } : null,
        measuredPD: measuredPD ?? null,
        calibratedFaceWidthMm: calibratedFaceWidthMm ?? null,
        faceShape: faceShape ?? null,
        faceShapeConfidence: faceShapeConfidence ?? null,
        faceShapeLocked: !!faceShapeLocked,
        snapshot: debugInfoRef.current ?? null,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vto-diagnostics-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.warn('[VTO] Failed to export diagnostics:', e);
    }
  }, [activeCatalog, selectedGlassesId, measuredPD, calibratedFaceWidthMm, faceShape, faceShapeConfidence, faceShapeLocked]);

  const toggleDebug = useCallback(() => {
    try {
      const next = !debugEnabled;
      setDebugEnabled(next);
      localStorage.setItem('vto:debug', next ? '1' : '0');
    } catch {
      setDebugEnabled((v) => !v);
    }
  }, [debugEnabled]);

  // Ref for openClipPreview to avoid circular dependency
  const openClipPreviewRef = useRef(null);

  // UX: toast + onboarding (first-time guide) — defined before swipe handlers that use it
  const [toast, setToast] = useState({ open: false, message: '', kind: 'info', position: 'bottom' });
  const toastTimerRef = useRef(null);
  const [showSwipeHint, setShowSwipeHint] = useState(true); // full-screen overlay, hide after 2s
  const [showStripSwipeHint, setShowStripSwipeHint] = useState(true); // hint above strip, hide on first swipe/arrow
  const showToast = useCallback((message, kind = 'info', ms = 2400, position = 'bottom') => {
    try {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    } catch {
      // ignore
    }
    setToast({ open: true, message: String(message || ''), kind, position });
    toastTimerRef.current = setTimeout(() => {
      setToast((prev) => ({ ...prev, open: false }));
    }, ms);
  }, [
    getOrCreateBevelNormalMap,
    vtoFlags.assetContentScaleCorrection,
    vtoFlags.toneMapping2DDisabled,
    vtoFlags.twoD3DLook,
  ]);

  // Calculate fit type based on size (PD vs frameWidth) and face shape matching
  const calculateFitType = useCallback((glasses, pd, faceWidthMm, shape, shapeLocked) => {
    if (!glasses) return FIT_TYPE.NONE;

    const frameWidth = getEffectiveFrameWidthMm(glasses.frameWidth, glasses.handleWidth, glasses.bridgeLengthCm);
    const recommendedShapes = glasses.recommendedForFaceShapes || [];

    const sizeMatches =
      (faceWidthMm != null && Number.isFinite(faceWidthMm) && Math.abs(frameWidth - faceWidthMm) <= 18) ||
      (pd != null && getSizeMatch(frameWidth, pd));
    const shapeMatches = shapeLocked && shape && recommendedShapes.includes(shape);

    if (sizeMatches && shapeMatches) return FIT_TYPE.PERFECT;
    if (sizeMatches) return FIT_TYPE.SIZE_ONLY;
    if (shapeMatches) return FIT_TYPE.SHAPE_ONLY;
    return FIT_TYPE.NONE;
  }, []);

  // Keep fit toast visible persistently while model is selected and we have measurements
  useEffect(() => {
    // Need at least one measurement locked to show fit toast
    const hasMeasurements = calibratedFaceWidthMm != null || measuredPD != null || faceShapeLocked;
    if (!hasMeasurements) {
      setFitToast({ open: false, type: null });
      return;
    }

    const currentGlasses = activeCatalog.find((g) => g.id === selectedGlassesId) || activeCatalog[0];
    if (!currentGlasses) {
      setFitToast({ open: false, type: null });
      return;
    }

    const fitType = calculateFitType(currentGlasses, measuredPD, calibratedFaceWidthMm, faceShape, faceShapeLocked);
    if (fitType) {
      setFitToast({ open: true, type: fitType });
    } else {
      setFitToast({ open: false, type: null });
    }
  }, [selectedGlassesId, measuredPD, calibratedFaceWidthMm, faceShape, faceShapeLocked, activeCatalog, calculateFitType]);

  // Swipe left/right on stage to change glasses model (catalog) — touch + mouse
  const MIN_SWIPE_PX = 50;
  const swipeStartRef = useRef({ x: null, y: null });
  const applySwipeDelta = useCallback((deltaX) => {
    if (Math.abs(deltaX) < MIN_SWIPE_PX || !compareCatalog || compareCatalog.length <= 1) return;
    const idx = compareCatalog.findIndex((g) => g.id === selectedGlassesId);
    if (idx === -1) return;
    const nextIdx = deltaX < 0
      ? (idx + 1) % compareCatalog.length
      : (idx - 1 + compareCatalog.length) % compareCatalog.length;
    setSelectedGlassesId(compareCatalog[nextIdx].id);
    showToast(
      compareCatalog[nextIdx].name,
      'info',
      1200,
      'top'
    );
    setShowSwipeHint(false);
    setShowStripSwipeHint(false);
  }, [compareCatalog, selectedGlassesId, showToast, comparingWithSelfieId]);

  // Scroll glasses strip so selected model is centered (smooth)
  useEffect(() => {
    if (!glassesStripRef.current || !selectedGlassesId) return;
    const el = glassesStripRef.current.querySelector(`[data-glasses-id="${selectedGlassesId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [selectedGlassesId]);

  const handleSwipeTouchStart = useCallback((e) => {
    if (e.touches.length !== 1 || !compareCatalog || compareCatalog.length <= 1) return;
    swipeStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, [compareCatalog]);
  const handleSwipeTouchEnd = useCallback((e) => {
    const start = swipeStartRef.current;
    if (start.x == null || !e.changedTouches?.length) return;
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - start.x;
    swipeStartRef.current = { x: null, y: null };
    applySwipeDelta(deltaX);
  }, [applySwipeDelta]);

  const handleSwipeMouseDown = useCallback((e) => {
    if (e.button !== 0 || !compareCatalog || compareCatalog.length <= 1) return;
    swipeStartRef.current = { x: e.clientX, y: e.clientY };
  }, [compareCatalog]);
  const handleSwipeMouseUp = useCallback((e) => {
    const start = swipeStartRef.current;
    if (start.x == null) return;
    const deltaX = e.clientX - start.x;
    swipeStartRef.current = { x: null, y: null };
    applySwipeDelta(deltaX);
  }, [applySwipeDelta]);
  const handleSwipeMouseLeave = useCallback(() => {
    swipeStartRef.current = { x: null, y: null };
  }, []);

  // "Freeze frame" + preview modal (after screenshot / on media click)
  const [frozenStageDataUrl, setFrozenStageDataUrl] = useState(null);
  const [previewModal, setPreviewModal] = useState({ open: false, title: '', dataUrl: null, videoUrl: null });
  const [previewPrimary, setPreviewPrimary] = useState({ label: '', onPrimary: null, disabled: false });
  const previewVideoUrlRef = useRef(null);
  const clipVideoCacheRef = useRef(new Map()); // key: `${clipId}:${glassesId}` -> Blob
  const previewResumeVideoRef = useRef(false);
  const prevGalleryOpenRef = useRef(false);
  const tabWasHiddenRef = useRef(false);

  useEffect(() => {
    return () => {
      try {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      } catch {
        // ignore
      }
    };
  }, []);

  // Hide swipe hint after 2 seconds on initial load
  useEffect(() => {
    const t = setTimeout(() => setShowSwipeHint(false), 2000);
    return () => clearTimeout(t);
  }, []);

  // Watermark helpers (canvas watermark inside stage + baked into screenshots/video renders)
  const watermarkImgRef = useRef(null);
  useEffect(() => {
    if (typeof Image === 'undefined') return undefined;
    const img = new Image();
    img.src = watermarkLogo;
    watermarkImgRef.current = img;
    return () => {
      watermarkImgRef.current = null;
    };
  }, []);

  const drawWatermark2D = useCallback((ctx, w, h, opts = {}) => {
    if (!ctx || !w || !h) return;
    const opacity = typeof opts.opacity === 'number' ? opts.opacity : 0.6;
    const padding = typeof opts.padding === 'number' ? opts.padding : Math.round(Math.min(w, h) * 0.03);
    const targetH = typeof opts.height === 'number' ? opts.height : Math.round(h * 0.035);

    const img = watermarkImgRef.current;
    ctx.save();
    ctx.globalAlpha = opacity;

    if (img && img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      const aspect = img.naturalWidth / img.naturalHeight;
      const drawH = Math.max(18, Math.min(targetH, 64));
      const drawW = Math.round(drawH * aspect);
      const x = w - padding - drawW;
      const y = padding;
      ctx.drawImage(img, x, y, drawW, drawH);
    } else {
      const text = 'visionlive';
      const fontSize = Math.max(12, Math.round(h * 0.022));
      ctx.font = `600 ${fontSize}px "Google Sans", Inter, system-ui, -apple-system, Segoe UI, sans-serif`;
      ctx.textBaseline = 'top';
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      const metrics = ctx.measureText(text);
      const x = w - padding - metrics.width;
      const y = padding;
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 6;
      ctx.fillText(text, x, y);
    }

    ctx.restore();
  }, []);

  // Helper: calculate effective DPR (with dynamic canvas, just use device DPR * user zoom)
  const getEffectiveDpr = useCallback(() => {
    // Mobile screens are often DPR=3; cap harder on mobile for stability/perf
    // (slightly softer cap on desktop for crispness).
    const maxDpr = isMobile ? (getIsLowEndMobile() ? 1.4 : 1.8) : 2.0;
    const deviceDpr = window.devicePixelRatio || 1;
    const baseDpr = Math.min(deviceDpr, maxDpr);
    // Apply a proportional quality reduction even on newer devices (keeps motion "glued" and stable).
    const qualityScale = isMobile ? (getIsLowEndMobile() ? 0.82 : 0.85) : 1;
    return Math.min(maxDpr, baseDpr * qualityScale);
  }, [isMobile]);

  useEffect(() => {
    const canvas = watermarkCanvasRef.current;
    const stageEl = cameraBoxRef.current || stageRef.current;
    if (!canvas || !stageEl) return undefined;

    const dpr = getEffectiveDpr();
    const draw = () => {
      const w = Math.round(stageEl.clientWidth || stageSize.w || 800);
      const h = Math.round(stageEl.clientHeight || stageSize.h || 600);
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      drawWatermark2D(ctx, w, h, { opacity: 0.45, height: Math.round(h * 0.022) });
    };

    draw();
    const img = watermarkImgRef.current;
    if (img && !(img.complete && img.naturalWidth > 0)) {
      const onLoad = () => draw();
      img.addEventListener?.('load', onLoad);
      return () => img.removeEventListener?.('load', onLoad);
    }
    return undefined;
  }, [drawWatermark2D, stageSize.h, stageSize.w, getEffectiveDpr]);

  // Load saved video clips (IndexedDB)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const clips = await loadVideoClips();
        if (cancelled) return;
        setVideoClips(clips);
        setActiveClipId((prev) => prev || clips?.[0]?.id || null);

        // Build thumbnails (first frame or stored thumbnail) lazily
        const entries = await Promise.all(
          (clips || []).slice(0, 12).map(async (c) => {
            try {
              const blob = c?.thumbnail || c?.firstFrame || c?.frames?.[0] || null;
              const url = await blobToDataUrl(blob);
              return [c.id, url];
            } catch {
              return [c.id, null];
            }
          })
        );
        if (!cancelled) {
          const next = {};
          for (const [id, url] of entries) next[id] = url;
          setClipThumbs(next);
        }
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Legacy smoothing/jitter/prediction pipeline was removed after switching to MediaPipe Tasks.

  // Stabilize face-shape label (avoid flicker)
  const faceShapeWindowRef = useRef([]);
  const faceShapeLockedRef = useRef(false);

  // For the "face scan" animation overlay
  const lastKeypointsRef = useRef(null);
  const lastVideoDimsRef = useRef({ w: 0, h: 0 });
  const trackerBootRef = useRef({ t0: 0, fallbackTried: false });
  const trackerStateRef = useRef({ lastInferenceMs: 0, hasIrisData: false });
  const trackerMissRef = useRef(0);
  const poseSmoothingRef = useRef({
    init: false,
    pos: new THREE.Vector3(),
    rot: { yaw: 0, pitch: 0, roll: 0 },
    scale: 1,
    // 3-frame median for position/scale — low lag, kills single spikes
    history: [],    // Array of { pos: Vector3, scale }
    // 5-frame median for rotation — more noise rejection; rotation jitter is visually worst
    rotHistory: [], // Array of { yaw, pitch, roll }
    // Velocity tracking for adaptive alpha
    prevMedPos: new THREE.Vector3(), // median target last frame (for velocity measurement)
    vel: 0,        // peak-decay velocity for post-burst settle
    boostAlpha: 0, // decaying alpha boost after confirmed fast movement
    // Sustained motion counter:
    //   +1 each frame medDelta > rampStart  (real movement)
    //   -2 each frame medDelta <= rampStart (stopped / tremor)
    // Camera shake alternates above/below rampStart → net negative → stays at 0 → alphaMin
    // Real movement is consistently above → climbs to 10 → alphaMax
    sustainCounter: 0,
  });

  // Mobile: stabilize temple (handle) behavior to avoid "flapping" / side switching jitter.
  const templeStabilityRef = useRef(createTempleStabilityState());

  // Detect hinge position - returns { hingeX, hingeY } in world coordinates
  // Frame is 2 units wide, height varies based on aspect ratio
  // Images are perfectly cropped, so top of image = top of frame
  const detectHingePosition = useCallback(async (imageSrc) => {
    // Return cached result if available (computation is purely geometric / aspect-ratio based)
    if (_hingeCache.has(imageSrc)) return _hingeCache.get(imageSrc);
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const aspectRatio = img.width / img.height;
          const worldHeight = 2 / aspectRatio;
          
          // Frame mesh is centered at y=0, so top is at worldHeight/2
          const hingeY = worldHeight / 2;
          // Slightly inward to overlap frame edge (no gap)
          const hingeX = 0.92;
          
          const result = { hingeX, hingeY };
          _hingeCache.set(imageSrc, result);
          resolve(result);
        } catch (e) {
          console.warn('Failed to detect hinge position:', e);
          resolve({ hingeX: 0.95, hingeY: 0 });
        }
      };
      img.onerror = () => {
        resolve({ hingeX: 0.95, hingeY: 0 });
      };
      img.src = imageSrc;
    });
  }, []);

  // Helper to detect hinge position from an already-loaded texture
  // Images are perfectly cropped, so top of image = top of frame
  const detectHingePositionFromTexture = useCallback((texture) => {
    if (!texture?.image) return { hingeX: 0.95, hingeY: 0 };
    
    try {
      const img = texture.image;
      const w = img.width || img.naturalWidth || 100;
      const h = img.height || img.naturalHeight || 50;
      
      const aspectRatio = w / h;
      const worldHeight = 2 / aspectRatio;
      
      // Frame mesh is centered at y=0, so top is at worldHeight/2
      const hingeY = worldHeight / 2;
      // Slightly inward to overlap frame edge (no gap)
      const hingeX = 0.92;
      
      return { hingeX, hingeY };
    } catch (e) {
      console.warn('Failed to detect hinge position from texture:', e);
      return { hingeX: 0.95, hingeY: 0 };
    }
  }, []);

  // Legacy helper for backward compatibility

  // Create temple arm using a PNG plane (mirror for right side)
  // customLengthMm: temple length in mm
  // frameWidthMm: frame width in mm (for proportional sizing)
  const createTempleArm = useCallback((isLeft, handleTexture, customLengthMm = null, frameWidthMm = null, handleContentScaleX = null) => {
    const group = new THREE.Group();

    const direction = isLeft ? -1 : 1;

    // Temple length as ratio of frame width with visual boost
    // Frame is always 2 world units, so temple = (templeMm / frameWidthMm) * 2 * boost
    // Boost factor accounts for temples being viewed at an angle (foreshortening)
    const templeMm = customLengthMm ?? FRAME_REFERENCE_MM; // Default 140mm
    const frameMm = frameWidthMm ?? FRAME_REFERENCE_MM; // Default 140mm
    // "If it fits, it fits": keep real foreshortening (no visual length boost).
    const templeVisualBoost = 1.0;
    const contentScaleX = (vtoFlags.assetContentScaleCorrection && handleContentScaleX && Number.isFinite(handleContentScaleX) && handleContentScaleX > 0.2)
      ? handleContentScaleX
      : 1;
    // If the handle texture has transparent padding along its length, scale up to compensate.
    const handleLength = ((templeMm / frameMm) * FRAME_BASE_WORLD_UNITS * templeVisualBoost) / contentScaleX;
    let handleHeight = TEMPLE_CONFIG.handleHeight;
    
    if (handleTexture?.image) {
      const texImg = handleTexture.image;
      const imgWidth = texImg.width || texImg.naturalWidth || 1;
      const imgHeight = texImg.height || texImg.naturalHeight || 1;
      const aspectRatio = imgWidth / imgHeight;
      handleHeight = handleLength / aspectRatio;
    }
    
    // Apply height scale to reduce handle height (more natural appearance)
    handleHeight *= TEMPLE_CONFIG.heightScale;

    // Store sizing so we can do hinge→ear constraints later (single-joint IK-ish).
    group.userData.templeLengthWorld = handleLength;
    group.userData.templeHeightWorld = handleHeight;

    const pbrEnabled = vtoFlags.twoD3DLook && !getIsLowEndMobile();

    // Shared geometry for all temple layers (same size – no inflation scaling)
    // Use segmented geometry + vertex bending so the handle doesn't look like a stiff flat card.
    const segX = pbrEnabled ? TEMPLE_BEND_SEGMENTS_HIGH : TEMPLE_BEND_SEGMENTS_LOW;
    const handleGeometry = new THREE.PlaneGeometry(handleLength, handleHeight, segX, 2);
    try {
      const pos = handleGeometry.attributes.position;
      const halfL = handleLength / 2;
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        // Hinge is on the "right" edge of the geometry (x = +halfL) for BOTH sides (right side gets mirrored via scale.x = -1)
        const progress = THREE.MathUtils.clamp((halfL - x) / handleLength, 0, 1); // 0 at hinge, 1 at ear end
        const curve = Math.pow(progress, 1.9);
        // Push the far end slightly "back" (towards face plane) and down to mimic real temple wrap.
        pos.setZ(i, pos.getZ(i) - TEMPLE_BEND_Z * curve);
        pos.setY(i, y - TEMPLE_BEND_Y * curve);
      }
      pos.needsUpdate = true;
      handleGeometry.computeVertexNormals();
    } catch {
      // If geometry bending fails, keep a flat temple.
    }

    const handleNormalKey = handleTexture?.userData?.srcKey || handleTexture?.uuid || null;
    const handleNormalMap =
      pbrEnabled && handleTexture?.image
        ? getOrCreateBevelNormalMap(handleNormalKey, handleTexture.image, { size: 256, blurPx: 1, strength: 2.6 })
        : null;

    const baseMaterial = handleTexture
      ? (pbrEnabled
          ? new THREE.MeshStandardMaterial({
              map: handleTexture,
              transparent: true,
              side: THREE.FrontSide,
              depthWrite: false,
              roughness: 1.0,
              metalness: 0.0,
              envMapIntensity: 0.0,
              normalMap: handleNormalMap || null,
              toneMapped: true,
            })
          : new THREE.MeshBasicMaterial({
              map: handleTexture,
              transparent: true,
              side: THREE.FrontSide,
              depthWrite: false,
              toneMapped: !vtoFlags.toneMapping2DDisabled,
            }))
      : (pbrEnabled
          ? new THREE.MeshStandardMaterial({
              color: TEMPLE_CONFIG.color,
              transparent: true,
              opacity: 0.9,
              side: THREE.FrontSide,
              depthWrite: false,
              roughness: 1.0,
              metalness: 0.0,
              envMapIntensity: 0.0,
              normalMap: handleNormalMap || null,
              toneMapped: true,
            })
          : new THREE.MeshBasicMaterial({
              color: TEMPLE_CONFIG.color,
              transparent: true,
              opacity: 0.9,
              side: THREE.FrontSide,
              depthWrite: false,
              toneMapped: !vtoFlags.toneMapping2DDisabled,
            }));

    // Stretched depth: layers distributed evenly in Z (like front frame), not stacked backward
    const baseZ = 0.005;
    const templeStackLayers = pbrEnabled ? TEMPLE_STACK_LAYERS_HIGH : TEMPLE_STACK_LAYERS_BASE;
    for (let i = 0; i < templeStackLayers; i++) {
      const t = templeStackLayers > 1 ? i / (templeStackLayers - 1) : 0.5;
      const z = baseZ - TEMPLE_DEPTH + t * TEMPLE_DEPTH;

      const mat = baseMaterial.clone();
      const slice = new THREE.Mesh(handleGeometry, mat);
      slice.name = i === templeStackLayers - 1 ? (isLeft ? 'leftTemple' : 'rightTemple') : `${isLeft ? 'leftTemple' : 'rightTemple'}_stack_${i}`;

      if (!isLeft) {
        slice.scale.x = -1;
      }

      // Fix mirrored normal-map direction on the right side
      if (pbrEnabled && mat && mat.normalMap) {
        const sx = isLeft ? 0.85 : -0.85;
        const sy = 0.85;
        mat.normalScale = new THREE.Vector2(sx, sy);
      }

      slice.position.x = direction * (handleLength / 2);
      slice.position.y = -handleHeight / 2;
      slice.position.z = z;

      group.add(slice);
    }

    return group;
  }, []);

  const loadTexture = useCallback((src) => {
    return new Promise((resolve, reject) => {
      const loader = textureLoaderRef.current;
      if (!loader) return reject(new Error('TextureLoader not ready'));

      loader.load(
        src,
        (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace;
          // Better filtering for realistic edges (reduces aliasing/cartoonish look)
          tex.minFilter = THREE.LinearMipmapLinearFilter;
          tex.magFilter = THREE.LinearFilter;
          tex.generateMipmaps = true;
          tex.wrapS = THREE.ClampToEdgeWrapping;
          tex.wrapT = THREE.ClampToEdgeWrapping;
          try {
            // Higher anisotropy improves sharpness at oblique angles
            const r = rendererRef.current;
            const aniso = r?.capabilities?.getMaxAnisotropy?.() || 0;
            tex.anisotropy = Math.min(8, aniso || 8);
          } catch {
            // ignore
          }
          resolve(tex);
        },
        undefined,
        (err) => reject(err)
      );
    });
  }, []);

  const setFrameTextureSrc = useCallback(
    async (src) => {
      const frameRef = frameMeshRef.current;
      if (!frameRef) {
        console.log('[CLIPON] setFrameTextureSrc: frameMeshRef is null, queuing:', src?.substring(0, 80));
        pendingFrameTextureSrcRef.current = src;
        return;
      }

      console.log('[CLIPON] setFrameTextureSrc: loading texture for', src?.substring(0, 80));
      try {
        const tex = await loadTexture(src);
        console.log('[CLIPON] setFrameTextureSrc: texture loaded OK for', src?.substring(0, 80));
        if (activeFrameTextureRef.current && activeFrameTextureRef.current.dispose) {
          activeFrameTextureRef.current.dispose();
        }
        activeFrameTextureRef.current = tex;

        // Generate a bevel normal-map from alpha for 3D-like highlights (2D only)
        let normalMap = null;
        if (vtoFlags.twoD3DLook && tex?.image && currentAssetTypeRef.current === '2d' && !getIsLowEndMobile()) {
          try {
            normalMap = getOrCreateBevelNormalMap(src, tex.image, { size: 256, blurPx: 1, strength: 3.0 });
          } catch {
            normalMap = null;
          }
        }
        activeFrameNormalMapRef.current = normalMap;

        const isGroup = frameRef.type === 'Group' && frameRef.children.length > 0;
        if (isGroup) {
          frameRef.children.forEach((child) => {
            if (!child.material) return;
            if (child.material.uniforms && child.material.uniforms.map) {
              child.material.uniforms.map.value = tex;
            } else {
              child.material.map = tex;
            }
            // Apply normal map on PBR materials (or ignore for shader materials)
            if (normalMap && (child.material.isMeshStandardMaterial || child.material.isMeshPhysicalMaterial)) {
              child.material.normalMap = normalMap;
              child.material.normalScale = new THREE.Vector2(1.0, 1.0);
              child.material.envMapIntensity = 0.0;
            }
            child.material.needsUpdate = true;
          });
          if (tex.image) {
            const imgWidth = tex.image.width || tex.image.naturalWidth || 1;
            const imgHeight = tex.image.height || tex.image.naturalHeight || 1;
            const aspectRatio = imgWidth / imgHeight;
            const newGeometry = new THREE.PlaneGeometry(2, 2 / aspectRatio);
            const oldGeometry = frameRef.children[0].geometry;
            frameRef.children.forEach((c) => { c.geometry = newGeometry; });
            if (oldGeometry) oldGeometry.dispose();
          }
        } else {
          frameRef.material.map = tex;
          if (normalMap && (frameRef.material.isMeshStandardMaterial || frameRef.material.isMeshPhysicalMaterial)) {
            frameRef.material.normalMap = normalMap;
            frameRef.material.normalScale = new THREE.Vector2(1.0, 1.0);
            frameRef.material.envMapIntensity = 0.0;
          }
          frameRef.material.needsUpdate = true;
          if (tex.image) {
            const imgWidth = tex.image.width || tex.image.naturalWidth || 1;
            const imgHeight = tex.image.height || tex.image.naturalHeight || 1;
            const aspectRatio = imgWidth / imgHeight;
            const newGeometry = new THREE.PlaneGeometry(2, 2 / aspectRatio);
            if (frameRef.geometry) frameRef.geometry.dispose();
            frameRef.geometry = newGeometry;
          }
        }
      } catch (e) {
        console.warn('Failed to load frame texture:', e);
        if (frameRef.type === 'Group' && frameRef.children.length) {
          frameRef.children.forEach((c) => {
            if (!c.material) return;
            if (c.material.uniforms && c.material.uniforms.map) c.material.uniforms.map.value = null;
            else c.material.map = null;
            c.material.needsUpdate = true;
          });
        } else if (frameRef.material) {
          frameRef.material.map = null;
          frameRef.material.needsUpdate = true;
        }
      }
    },
    [getOrCreateBevelNormalMap, loadTexture, vtoFlags.twoD3DLook]
  );

  // Keep a ref to avoid hooking setFrameTextureSrc into heavyweight init effects.
  useEffect(() => {
    setFrameTextureSrcRef.current = setFrameTextureSrc;
  }, [setFrameTextureSrc]);

  // Immediately warm the HTTP cache for the selected glasses BEFORE THREE.js requests it.
  // Fired synchronously (no idle callback) so the browser has the response ready
  // by the time TextureLoader makes its XHR — turning it into a near-instant cache hit.
  useEffect(() => {
    const glasses = activeCatalog.find((g) => g.id === selectedGlassesId) ?? activeCatalog[0];
    if (!glasses) return;
    [glasses.src, glasses.handleSrc].forEach((url) => {
      if (!url || typeof url !== 'string') return;
      if (!url.startsWith('http') && !url.startsWith('/') && !url.startsWith('blob:')) return;
      if (_prefetchedUrls.has(url)) return;
      _prefetchedUrls.add(url);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = url;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGlassesId]);

  useEffect(() => {
    try {
      localStorage.setItem('vto:selectedGlassesId', selectedGlassesId);
    } catch {
      // ignore
    }
  }, [selectedGlassesId]);

  // Reset clip-on mode whenever the selected glasses changes
  useEffect(() => {
    setUseClipOn(false);
  }, [selectedGlassesId]);

  // Keep selection valid for the currently active catalog.
  useEffect(() => {
    if (!compareCatalog || compareCatalog.length === 0) return;

    const selectedExists = compareCatalog.some((item) => item.id === selectedGlassesId);
    if (!selectedExists) {
      const fallbackId = compareCatalog[0]?.id;
      if (fallbackId) setSelectedGlassesId(fallbackId);
    }
  }, [compareCatalog, selectedGlassesId]);

  useEffect(() => {
    const loadResources = async () => {
      try {
        // Note: Camera access is handled by the Webcam component
        // We just need to wait for it to be ready

        // Face tracking model (prefer MediaPipe Tasks; fallback to TFJS only if needed).
        const isMobileDevice = getIsMobileDevice() || isMobile;
        const isLowEnd = isMobileDevice && getIsLowEndMobile();
        const delegates = !isMobileDevice
          ? ['GPU', 'CPU']
          : (isLowEnd
              ? ['CPU', 'GPU']
              : (vtoFlags.mobileGpuFirst
                  ? ['GPU', 'CPU']
                  : ['CPU', 'GPU']));
        trackerBootRef.current = { t0: performance.now(), fallbackTried: false };
        const loadedModel = await loadFaceTrackingModel({
          prefer: 'mediapipe',
          maxFaces: 1,
          mediapipe: {
            // Mobile: prefer GPU first on capable devices (faster), but keep CPU fallback.
            // iOS: default is CPU-first unless `mobileGpuFirst` is enabled.
            delegates,
          },
          tfjs: {
            shouldLoadIrisModel: true,
          },
        });
        setModel(loadedModel);

        // Allow refs to be attached after first paint (e.g. Strict Mode or conditional DOM)
        await new Promise((r) => requestAnimationFrame(r));

        // Three.js Setup — require canvas in DOM so we render to the visible element
        const canvas = canvasRef.current;
        const stageEl = cameraBoxRef.current || stageRef.current;
        if (!canvas || !stageEl) {
          console.warn('[VTO] Canvas or stage ref not ready, skipping Three.js init');
          setIsLoading(false);
          return;
        }
        const width = stageEl.clientWidth || canvas.clientWidth || 800;
        const height = stageEl.clientHeight || canvas.clientHeight || 600;
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
        camera.position.z = 5;
        
        const renderer = new THREE.WebGLRenderer({ 
          canvas, 
          alpha: true,
          antialias: true,
          premultipliedAlpha: false, // Avoid edge line when canvas overlaid on photo (WebGL alpha best practice)
          preserveDrawingBuffer: true,
          powerPreference: 'high-performance',
          depth: true,
          stencil: false,
        });
        renderer.setSize(width, height);
        // Use the same DPR policy as the rest of the app (mobile quality reduction / zoom handling).
        renderer.setPixelRatio(getEffectiveDpr());
        
        // CRITICAL: Configure renderer for proper 3D depth rendering
        // Enable depth testing and writing (essential for 3D models to render correctly)
        const gl = renderer.getContext();
        gl.enable(gl.DEPTH_TEST); // Enable depth testing
        gl.depthFunc(gl.LEQUAL); // Standard depth function
        
        // Configure renderer for proper 3D model rendering
        renderer.shadowMap.enabled = false; // Disabled for performance, enable if needed
        
        // Configure color space and tone mapping for 3D models with PBR materials
        // Note: Three.js r152+ uses outputColorSpace instead of outputEncoding
        if (renderer.outputColorSpace !== undefined) {
          // Newer Three.js (r152+)
          renderer.outputColorSpace = THREE.SRGBColorSpace;
        } else if (renderer.outputEncoding !== undefined) {
          // Older Three.js (<r152)
          renderer.outputEncoding = THREE.sRGBEncoding;
        }
        
        // Keep output color-faithful by default; avoid cinematic tone mapping shifts.
        renderer.toneMapping = vtoFlags.toneMapping2DDisabled ? THREE.NoToneMapping : THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.0;
        
        // Ensure proper 3D rendering mode (not 2D flat rendering)
        renderer.sortObjects = true; // Sort objects for proper depth rendering
        rendererRef.current = renderer;
        cameraRef.current = camera;
        sceneRef.current = scene;
        textureLoaderRef.current = new THREE.TextureLoader();

        // Environment reflections are optional and disabled by default (less glare, truer colors).
        if (vtoFlags.reflectionsEnabled) {
          try {
            const pmrem = new THREE.PMREMGenerator(renderer);
            pmrem.compileEquirectangularShader?.();
            const envRT = pmrem.fromScene(new RoomEnvironment(), 0.04);
            scene.environment = envRT.texture;
            envMapRef.current = envRT.texture;
            pmremRef.current = pmrem;
          } catch (e) {
            console.warn('[VTO] Failed to create environment map:', e);
          }
        } else {
          scene.environment = null;
          envMapRef.current = null;
          pmremRef.current = null;
        }

        // Add lighting (we'll modulate it from camera feed for "environment matching")
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        scene.add(ambientLight);
        ambientLightRef.current = ambientLight;
        
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(0, 1, 2);
        scene.add(directionalLight);
        keyLightRef.current = directionalLight;
        
        // Add subtle fill light from below
        const fillLight = new THREE.DirectionalLight(0xffffff, 0.3);
        fillLight.position.set(0, -1, 1);
        scene.add(fillLight);
        fillLightRef.current = fillLight;

        // Asset pipeline upgrades (GLB realism path scaffolding):
        // - KTX2/BasisU texture support
        // - DRACO mesh compression (optional)
        // - Meshopt compression (optional)
        try {
          const ktx2 = new KTX2Loader();
          // Use CDN-hosted Basis transcoders to avoid bundling WASM into CRA.
          ktx2.setTranscoderPath('https://unpkg.com/three@0.161.0/examples/jsm/libs/basis/');
          ktx2.detectSupport(renderer);

          const loader = new GLTFLoader();
          loader.setCrossOrigin('anonymous');
          loader.setKTX2Loader(ktx2);

          // Optional: DRACO
          try {
            const dracoMod = await import('three/examples/jsm/loaders/DRACOLoader');
            const DRACOLoader = dracoMod.DRACOLoader || dracoMod.default;
            if (DRACOLoader) {
              const draco = new DRACOLoader();
              draco.setDecoderPath('https://unpkg.com/three@0.161.0/examples/jsm/libs/draco/');
              loader.setDRACOLoader(draco);
              dracoLoaderRef.current = draco;
            }
          } catch {
            dracoLoaderRef.current = null;
          }

          // Optional: Meshopt
          try {
            const meshoptMod = await import('three/examples/jsm/libs/meshopt_decoder.module.js');
            const meshopt = meshoptMod.MeshoptDecoder || meshoptMod.default || meshoptMod;
            if (meshopt) loader.setMeshoptDecoder(meshopt);
          } catch {
            // ignore
          }

          gltfLoaderRef.current = loader;
          ktx2LoaderRef.current = ktx2;
        } catch (e) {
          // Fallback: regular GLTFLoader (no KTX2/DRACO/Meshopt)
          gltfLoaderRef.current = new GLTFLoader();
          gltfLoaderRef.current.setCrossOrigin('anonymous');
          ktx2LoaderRef.current = null;
          dracoLoaderRef.current = null;
        }

        // Create glasses group to hold all parts
        const glassesParentGroup = new THREE.Group();
        // Hide until first face is detected (prevents static flash of handles/frame)
        glassesParentGroup.visible = false;
        scene.add(glassesParentGroup);

        // Face occluder (depth-only): enables temples to remain visible without "drawing over" the face.
        // We render this AFTER the frame, BEFORE temples; it writes depth but not color.
        try {
          const useFullMesh = !!(FACEMESH_OCCLUDER_TRIANGLES && FACEMESH_OCCLUDER_TRIANGLES.length > 3000);
          const n = useFullMesh ? FACEMESH_VERTEX_COUNT : FACE_OVAL_INDICES.length;
          const geom = new THREE.BufferGeometry();
          const positions = new Float32Array(n * 3);
          const posAttr = new THREE.BufferAttribute(positions, 3);
          geom.setAttribute('position', posAttr);
          if (useFullMesh) {
            // Canonical (precomputed) triangle indices in FaceMesh UV space → stable topology.
            geom.setIndex(new THREE.BufferAttribute(FACEMESH_OCCLUDER_TRIANGLES, 1));
          } else {
            // Fallback: triangle fan over face oval.
            const indexArray = new Uint16Array(Math.max(0, (n - 2) * 3));
            for (let i = 1, k = 0; i < n - 1; i++, k += 3) {
              indexArray[k] = 0;
              indexArray[k + 1] = i;
              indexArray[k + 2] = i + 1;
            }
            geom.setIndex(new THREE.BufferAttribute(indexArray, 1));
          }
          geom.computeVertexNormals();

          const mat = new THREE.MeshBasicMaterial({
            color: 0x000000,
            side: THREE.DoubleSide,
            depthTest: true,
            depthWrite: true,
            colorWrite: false,
            transparent: true,
            opacity: 0,
          });
          const mesh = new THREE.Mesh(geom, mat);
          mesh.name = 'faceOccluder';
          mesh.frustumCulled = false;
          mesh.renderOrder = 1; // frame(0) -> occluder(1) -> temples(2)
          mesh.visible = false;
          glassesParentGroup.add(mesh);
          faceOccluderRef.current = mesh;
          faceOccluderPosAttrRef.current = posAttr;
          faceOccluderIndexAttrRef.current = geom.index;
        } catch (e) {
          console.warn('[VTO] Failed to create face occluder:', e);
        }
        // Build glasses group once; the frame texture can be swapped later.
        // Use handle from first catalog item if custom catalog provided
        const initialGlasses = activeCatalog[0];
        const initialHandleSrc = initialGlasses?.handleSrc || handleSrc;
        let handleTexture = null;
        try {
          handleTexture = await loadTexture(initialHandleSrc);
        } catch (e) {
          console.warn('Failed to load handle texture (fallback to solid temples):', e);
          handleTexture = null;
        }

                // Multi-stack front frame: back layers only draw where texture alpha >= 0.6 (frame);
                // low-alpha parts (lenses) are drawn only by front layer so they stay transparent
                const initialAspect = 2;
                const sharedGeo = new THREE.PlaneGeometry(2, 2 / initialAspect);
                const halfDepth = FRAME_DEPTH / 2;
                const opacityThreshold = FRAME_OPACITY_STACK_THRESHOLD;
                const backLayerVert = `
                  varying vec2 vUv;
                  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
                `;
                const backLayerFrag = `
                  uniform sampler2D map;
                  varying vec2 vUv;
                  void main() {
                    vec4 c = texture2D(map, vUv);
                    if (c.a < ${opacityThreshold}) discard;
                    gl_FragColor = c;
                  }
                `;
                const frameContainer = new THREE.Group();
                frameContainer.name = 'frame';
                const frameStackLayers =
                  vtoFlags.twoD3DLook && !getIsLowEndMobile() ? FRAME_STACK_LAYERS_HIGH : FRAME_STACK_LAYERS_BASE;
                const isFrontLayer = (i) => i === frameStackLayers - 1;
                for (let i = 0; i < frameStackLayers; i++) {
                  const t = frameStackLayers > 1 ? i / (frameStackLayers - 1) : 0.5;
                  const z = -halfDepth + t * FRAME_DEPTH;
                  const mat = isFrontLayer(i)
                    ? (vtoFlags.twoD3DLook
                        ? new THREE.MeshPhysicalMaterial({
                            color: 0xffffff,
                            transparent: true,
                            side: THREE.DoubleSide,
                            // Keep blending (not alphaTest) on front so lenses stay truly transparent
                            // IMPORTANT: don't write depth from the blended front slice, otherwise
                            // temples can be incorrectly depth-culled behind transparent lens pixels.
                            depthWrite: false,
                            roughness: 1.0,
                            metalness: 0.0,
                            clearcoat: 0.0,
                            clearcoatRoughness: 1.0,
                            envMapIntensity: 0.0,
                            toneMapped: true,
                            polygonOffset: true,
                            polygonOffsetFactor: -1,
                            polygonOffsetUnits: (i - (frameStackLayers - 1) / 2) * 2,
                          })
                        : new THREE.MeshBasicMaterial({
                            color: 0xffffff,
                            transparent: true,
                            side: THREE.DoubleSide,
                            toneMapped: !vtoFlags.toneMapping2DDisabled,
                            // IMPORTANT: don't write depth from the blended front slice (see note above).
                            depthWrite: false,
                            polygonOffset: true,
                            polygonOffsetFactor: -1,
                            polygonOffsetUnits: (i - (frameStackLayers - 1) / 2) * 2,
                          }))
                    : (vtoFlags.twoD3DLook
                        ? new THREE.MeshStandardMaterial({
                            color: 0xffffff,
                            transparent: true,
                            side: THREE.DoubleSide,
                            depthWrite: true,
                            // Back layers should only render the frame material (not lenses)
                            alphaTest: opacityThreshold,
                            roughness: 1.0,
                            metalness: 0.0,
                            envMapIntensity: 0.0,
                            toneMapped: true,
                          })
                        : new THREE.ShaderMaterial({
                            uniforms: { map: { value: null } },
                            vertexShader: backLayerVert,
                            fragmentShader: backLayerFrag,
                            transparent: true,
                            side: THREE.DoubleSide,
                            depthWrite: true,
                            toneMapped: !vtoFlags.toneMapping2DDisabled,
                          }));
                  const slice = new THREE.Mesh(sharedGeo, mat);
                  slice.position.set(0, 0, z);
                  slice.name = isFrontLayer(i) ? 'frameSlice' : 'frameStackSlice';
                  // Z-index behavior: draw the frame after temples, and ignore depth so it always overlays handles.
                  slice.renderOrder = 3;
                  if (slice.material) slice.material.depthTest = false;
                  frameContainer.add(slice);
                }
        frameMeshRef.current = frameContainer;
        // Apply initial/pending frame texture immediately (fix: first load shows the model frame)
        const pending = pendingFrameTextureSrcRef.current;
        pendingFrameTextureSrcRef.current = null;
        let initialId = 'sunglasses_black';
        try {
          initialId = localStorage.getItem('vto:selectedGlassesId') || initialId;
        } catch {
          // ignore
        }
        const initialSrc = pending || getGlassesFromCatalog(initialId)?.src;
        if (initialSrc) setFrameTextureSrcRef.current?.(initialSrc);

        // Only add frame mesh here - temples will be created by the useEffect
        // when glassesGroup becomes available, with correct product dimensions
        activeHandleTextureRef.current = handleTexture;

        glassesParentGroup.add(frameContainer);
        // Note: temples are NOT added here - they will be created by the
        // selectedGlassesId useEffect once glassesGroup is set
        setGlassesGroup(glassesParentGroup);

        // Handle WebGL context loss (common on mobile)
        canvas.addEventListener('webglcontextlost', (event) => {
          event.preventDefault();
          console.warn('WebGL context lost');
        });
        canvas.addEventListener('webglcontextrestored', () => {
          console.log('WebGL context restored');
        });

        // Animation loop
        renderer.setAnimationLoop(() => {
          renderer.render(scene, camera);
        });

      } catch (error) {
        console.error("Initialization error:", error);
        try {
          const msg = String(error?.message || error || '').toLowerCase();
          if (msg.includes('webgl') || msg.includes('context')) {
            noWebglFallbackRef.current = true;
            console.warn('[VTO] WebGL unavailable; using 2D fallback overlay.');
          }
        } catch {
          // ignore
        }
        setIsLoading(false);
      }
    };

    loadResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createTempleArm, loadTexture]);

  // Cleanup env map / PMREM generator
  useEffect(() => {
    return () => {
      try {
        // If we generated a PMREM render target dynamically, dispose that first (it owns the texture).
        if (dynamicEnvRTRef.current?.dispose) {
          dynamicEnvRTRef.current.dispose();
          dynamicEnvRTRef.current = null;
        } else {
          envMapRef.current?.dispose?.();
        }
      } catch {
        // ignore
      }
      try {
        pmremRef.current?.dispose?.();
      } catch {
        // ignore
      }
      try {
        ktx2LoaderRef.current?.dispose?.();
        ktx2LoaderRef.current = null;
      } catch {
        // ignore
      }
      try {
        dracoLoaderRef.current?.dispose?.();
        dracoLoaderRef.current = null;
      } catch {
        // ignore
      }
      try {
        const cache = normalMapCacheRef.current;
        if (cache && typeof cache.forEach === 'function') {
          cache.forEach((tex) => {
            try { tex?.dispose?.(); } catch { /* ignore */ }
          });
          cache.clear?.();
        }
      } catch {
        // ignore
      }
    };
  }, []);

  // Cleanup face-tracker resources (MediaPipe Tasks holds WASM + GPU/CPU resources).
  useEffect(() => {
    return () => {
      try {
        model?.close?.();
      } catch {
        // ignore
      }
      try {
        model?.dispose?.();
      } catch {
        // ignore
      }
    };
  }, [model]);

  useEffect(() => {
    const glasses = getGlassesFromCatalog(selectedGlassesId);
    if (!glasses) return;
    
    // Wait for glassesGroup to be available
    if (!glassesGroup) return;

    // Resolve effective src — use clip-on image when toggled, exactly like selecting a different glasses item
    const effectiveSrc = (useClipOn && glasses.clipOnSrc) ? glasses.clipOnSrc : glasses.src;
    const effectiveHingeOffset = glasses.templeHingeOffset;
    const effectiveYOffset = glasses.templeYOffset;

    const loadToken = ++glassesLoadTokenRef.current;
    setIsGlassesAssetLoading(true);
    const finishAssetLoading = () => {
      if (glassesLoadTokenRef.current === loadToken) {
        setIsGlassesAssetLoading(false);
        setHasInitialGlassesLoaded((prev) => prev || true);
        if (typeof onSelectedGlassesLoaded === 'function') {
          onSelectedGlassesLoaded(glasses);
        }
      }
    };

    const is3DModel = glasses.assetType === '3d' && glasses.modelUrl;
    
    // Helper to clear existing 3D model
    const clear3DModel = () => {
      if (glbModelRef.current && glassesGroup.children.includes(glbModelRef.current)) {
        glassesGroup.remove(glbModelRef.current);
        glbModelRef.current.traverse((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
        glbModelRef.current = null;
      }
    };

    // Helper to show/hide 2D elements
    const set2DElementsVisible = (visible) => {
      if (frameMeshRef.current) {
        frameMeshRef.current.visible = visible;
      }
      if (leftTempleRef.current) {
        leftTempleRef.current.visible = visible;
      }
      if (rightTempleRef.current) {
        rightTempleRef.current.visible = visible;
      }
    };

    // Handle 3D model loading
    if (is3DModel) {
      console.log('Loading 3D model:', glasses.modelUrl?.substring(0, 100) + '...');
      currentAssetTypeRef.current = '3d';
      
      // Hide 2D elements
      set2DElementsVisible(false);
      
      // Clear any existing 3D model
      clear3DModel();
      
      // Initialize GLTF loader if needed
      if (!gltfLoaderRef.current) {
        // gltfLoaderRef is created above (with optional KTX2 support).
      }

      // Helper function to load GLB from URL (blob, http, or signed URL)
      const loadGlbFromUrl = (url) => {
        console.log('Loading GLB from URL:', url.substring(0, 100) + '...');
        
        // For signed URLs or external URLs, we may need to handle CORS
        // GLTFLoader handles this automatically, but we can add custom request options if needed
        const onProgress = (progress) => {
          if (progress.total > 0) {
            const percent = Math.round((progress.loaded / progress.total) * 100);
            console.log('Loading 3D model:', percent + '%');
          }
        };
        
        const onError = (error) => {
          console.error('Failed to load 3D model:', error);
          console.error('Error details:', {
            message: error.message,
            url: url.substring(0, 100),
            type: error.type || 'unknown'
          });
          
          // Fallback: show 2D elements if available
          if (glasses.src) {
            console.log('Falling back to 2D image');
            set2DElementsVisible(true);
            setFrameTextureSrc(glasses.src);
            currentAssetTypeRef.current = '2d';
          }
          finishAssetLoading();
        };
        
        const onLoad = (gltf) => {
          console.log('GLB model loaded successfully');
          const model = gltf.scene;
          
          // ============================================
          // 3D MODEL SETUP - Proper 3D Rendering
          // ============================================
          // Unlike 2D (flat planes), 3D models are full meshes with depth
          // They need proper orientation, materials, and 3D space positioning
          
          // First, ensure the model renders as a proper 3D object (not flattened)
          model.traverse((child) => {
            if (child.isMesh) {
              // CRITICAL: Ensure mesh renders in 3D space with proper depth
              child.frustumCulled = true; // Enable frustum culling for performance
              child.renderOrder = 0; // Render order (0 = normal, higher = later)
              
              // Ensure geometry has proper 3D structure
              if (child.geometry) {
                // Compute normals if missing (needed for proper 3D lighting)
                if (!child.geometry.attributes.normal || child.geometry.attributes.normal.count === 0) {
                  child.geometry.computeVertexNormals();
                }
                child.geometry.computeBoundingBox();
                child.geometry.computeBoundingSphere();
              }
              
              // Configure materials for proper 3D rendering
              if (child.material) {
                child.material.needsUpdate = true;
                
                // Enable depth testing and writing (critical for 3D rendering)
                child.material.depthTest = true;
                child.material.depthWrite = true;
                
                // Handle different material types
                if (child.material.isMeshStandardMaterial || child.material.isMeshPhysicalMaterial) {
                  // PBR materials - ensure they work with lighting
                  child.material.roughness = child.material.roughness ?? 0.5;
                  child.material.metalness = child.material.metalness ?? 0.0;
                } else if (child.material.isMeshBasicMaterial) {
                  // Basic materials - ensure they render correctly
                  child.material.depthTest = true;
                  child.material.depthWrite = true;
                }
                
                // Ensure textures are properly loaded
                if (child.material.map) {
                  child.material.map.needsUpdate = true;
                  child.material.map.flipY = false; // GLTF uses flipped Y
                }
                
                // Handle normal maps for 3D detail
                if (child.material.normalMap) {
                  child.material.normalMap.needsUpdate = true;
                }
              }
            }
          });
          
          // Calculate bounding box for proper 3D scaling
          const box = new THREE.Box3().setFromObject(model);
          const size = new THREE.Vector3();
          const center = new THREE.Vector3();
          box.getSize(size);
          box.getCenter(center);
          
          // Scale based on frame width (same system as 2D for consistency)
          const frameWidthMm = getEffectiveFrameWidthMm(glasses.frameWidth, glasses.handleWidth, glasses.bridgeLengthCm);
          const referenceWidthMm = 140; // Reference: 140mm = 2 world units
          const targetWidthWorldUnits = (frameWidthMm / referenceWidthMm) * 2.0;
          
          // Use X-axis width (typical for glasses models)
          // If X is very small, use the largest dimension
          const modelWidth = size.x > 0.1 ? size.x : Math.max(size.x, size.y, size.z);
          const scaleFactor = modelWidth > 0 ? targetWidthWorldUnits / modelWidth : 1.0;
          
          // Apply scaling to maintain 3D proportions
          model.scale.setScalar(scaleFactor);
          
          // Center the model at origin (critical for proper 3D positioning)
          // The model should be centered so face tracking positions it correctly
          model.position.set(
            -center.x * scaleFactor,
            -center.y * scaleFactor,
            -center.z * scaleFactor
          );
          
          // IMPORTANT: Ensure model is properly oriented for 3D rendering
          // Most GLB glasses models are exported facing forward (negative Z direction)
          // Reset any unwanted rotations, but preserve the model's intended orientation
          // If model appears backwards, uncomment and adjust:
          // model.rotation.y = Math.PI; // 180 degree rotation if needed
          
          // Ensure model faces camera (typical glasses orientation)
          // Models should face -Z direction (toward camera at Z=5)
          // If your models are exported differently, adjust here
          
          // Create container group for the 3D model
          // This group will be positioned by face tracking in 3D space
          const modelGroup = new THREE.Group();
          modelGroup.add(model);
          modelGroup.name = 'glbModel';
          
          // CRITICAL: Ensure the group renders as 3D (not flattened)
          modelGroup.matrixAutoUpdate = true;
          
          // Add to glasses group (parent that gets positioned by face tracking)
          glassesGroup.add(modelGroup);
          glbModelRef.current = modelGroup;
          
          console.log('3D model added to scene (proper 3D rendering)', {
            originalSize: { x: size.x.toFixed(3), y: size.y.toFixed(3), z: size.z.toFixed(3) },
            frameWidthMm,
            scaleFactor: scaleFactor.toFixed(3),
            center: { x: center.x.toFixed(3), y: center.y.toFixed(3), z: center.z.toFixed(3) },
            meshCount: model.children.filter(c => c.isMesh).length,
            hasDepth: size.z > 0.01, // Check if model has 3D depth
            renderMode: '3D' // Confirms it's rendering as 3D, not 2D
          });
          finishAssetLoading();
        };
        
        // Load the GLB/GLTF file
        gltfLoaderRef.current.load(url, onLoad, onProgress, onError);
      };

      // Check if modelUrl is a data URL (base64) - needs conversion to blob
      if (glasses.modelUrl.startsWith('data:')) {
        console.log('Converting data URL to blob for GLTFLoader...');
        try {
          // Convert data URL to blob
          fetch(glasses.modelUrl)
            .then(res => res.blob())
            .then(blob => {
              const blobUrl = URL.createObjectURL(blob);
              console.log('Blob URL created:', blobUrl);
              loadGlbFromUrl(blobUrl);
              // Note: We should revoke this URL after loading, but for simplicity
              // we'll let it be garbage collected when the component unmounts
            })
            .catch(err => {
              console.error('Failed to convert data URL to blob:', err);
              if (glasses.src) {
                set2DElementsVisible(true);
                setFrameTextureSrc(glasses.src);
              }
              finishAssetLoading();
            });
        } catch (err) {
          console.error('Error processing data URL:', err);
          if (glasses.src) {
            set2DElementsVisible(true);
            setFrameTextureSrc(glasses.src);
          }
          finishAssetLoading();
        }
      } else {
        // Regular URL (http/https or blob) - load directly
        loadGlbFromUrl(glasses.modelUrl);
      }
      
      return; // Don't continue to 2D loading
    }
    
    // Handle 2D image loading
    if (!effectiveSrc) {
      finishAssetLoading();
      return;
    }
    
    currentAssetTypeRef.current = '2d';
    
    // Clear any existing 3D model
    clear3DModel();
    
    // Show 2D elements
    set2DElementsVisible(true);
    
    // Update frame texture
    const frameLoadPromise = setFrameTextureSrc(effectiveSrc);
    
    // Update frame dimensions from product
    currentFrameWidthRef.current = getEffectiveFrameWidthMm(glasses.frameWidth, glasses.handleWidth, glasses.bridgeLengthCm);
    currentHandleWidthRef.current = glasses.handleWidth || 140;
    currentFrontContentScaleXRef.current = glasses.frontContentScaleX || 1;
    currentHandleContentScaleXRef.current = glasses.handleContentScaleX || 1;
    currentScaleCorrectionRef.current = glasses.scaleCorrection || 1;
    
    // Recreate temples with product's settings
    const recreateTemplesForModel = async () => {
      const loader = textureLoaderRef.current;
      if (!loader) return;
      
      try {
        const hingePromise =
          effectiveHingeOffset !== undefined && effectiveHingeOffset !== null
            ? Promise.resolve({
                hingeX: effectiveHingeOffset,
                hingeY: effectiveYOffset ?? 0,
              })
            : detectHingePosition(effectiveSrc).catch(() => ({
                hingeX: currentHingeOffsetRef.current || 0.95,
                hingeY: currentTempleYOffsetRef.current || 0,
              }));

        const handleSrcToUse = glasses.handleSrc || handleSrc;
        const texCache = handleTextureCacheRef.current;
        const handleTexturePromise = texCache?.has(handleSrcToUse)
          ? Promise.resolve(texCache.get(handleSrcToUse))
          : new Promise((resolve, reject) => {
          loader.load(
            handleSrcToUse,
            (tex) => {
              tex.colorSpace = THREE.SRGBColorSpace;
              tex.userData = { ...(tex.userData || {}), srcKey: handleSrcToUse };
              tex.minFilter = THREE.LinearFilter;
              tex.magFilter = THREE.LinearFilter;
              tex.generateMipmaps = false;
              texCache?.set(handleSrcToUse, tex);
              resolve(tex);
            },
            undefined,
            reject
          );
        });

        const [hingeData, newHandleTexture] = await Promise.all([hingePromise, handleTexturePromise]);
        const hingeX = hingeData.hingeX;
        const hingeY = hingeData.hingeY;
        
        // Temple length (physical) in mm from product.
        // Backend stores this as `handleWidth` (mm). `templeLength` is a legacy "world units" knob.
        // Convert legacy world units (typically 2-5) → mm by assuming 3 world units ≈ 140mm.
        let templeLengthMm = glasses.handleWidth;
        if (templeLengthMm === undefined || templeLengthMm === null) {
          templeLengthMm = glasses.templeLength;
        }
        if (templeLengthMm === undefined || templeLengthMm === null) {
          templeLengthMm = FRAME_REFERENCE_MM; // Default 140mm
        } else if (templeLengthMm < 50) {
          templeLengthMm = (templeLengthMm / 3) * FRAME_REFERENCE_MM;
        }
        
        
        currentHingeOffsetRef.current = hingeX;
        currentTempleYOffsetRef.current = hingeY;
        currentTempleLengthRef.current = templeLengthMm;
        
        // Remove old temples from scene (if they exist)
        if (leftTempleRef.current && glassesGroup.children.includes(leftTempleRef.current)) {
          glassesGroup.remove(leftTempleRef.current);
          leftTempleRef.current.traverse((child) => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              if (child.material.map && child.material.map !== newHandleTexture) {
                child.material.map.dispose?.();
              }
              child.material.dispose();
            }
          });
          leftTempleRef.current = null;
        }
        if (rightTempleRef.current && glassesGroup.children.includes(rightTempleRef.current)) {
          glassesGroup.remove(rightTempleRef.current);
          rightTempleRef.current.traverse((child) => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              if (child.material.map && child.material.map !== newHandleTexture) {
                child.material.map.dispose?.();
              }
              child.material.dispose();
            }
          });
          rightTempleRef.current = null;
        }
        
        // Create new temples with correct length (proportional to frame width)
        const frameWidthMm = getEffectiveFrameWidthMm(glasses.frameWidth, glasses.handleWidth, glasses.bridgeLengthCm);
        const handleContentScaleX = glasses.handleContentScaleX || currentHandleContentScaleXRef.current || 1;
        const leftTemple = createTempleArm(true, newHandleTexture, templeLengthMm, frameWidthMm, handleContentScaleX);
        leftTemple.name = 'leftTemple';
        leftTemple.position.set(-hingeX, hingeY, 0);
        leftTempleRef.current = leftTemple;
        
        const rightTemple = createTempleArm(false, newHandleTexture, templeLengthMm, frameWidthMm, handleContentScaleX);
        rightTemple.name = 'rightTemple';
        rightTemple.position.set(hingeX, hingeY, 0);
        rightTempleRef.current = rightTemple;
        
        // Add new temples to scene
        glassesGroup.add(leftTemple);
        glassesGroup.add(rightTemple);
        
        
        // Dispose old texture
        if (activeHandleTextureRef.current && activeHandleTextureRef.current !== newHandleTexture) {
          activeHandleTextureRef.current.dispose?.();
        }
        activeHandleTextureRef.current = newHandleTexture;
        
      } catch (e) {
        console.warn('Failed to recreate temples for model:', e);
      }
    };

    Promise.all([frameLoadPromise, recreateTemplesForModel()]).finally(() => {
      finishAssetLoading();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGlassesId, useClipOn, setFrameTextureSrc, detectHingePosition, createTempleArm, glassesGroup]);

  // ============================================
  // PREFETCH: wait for selected model to finish, then trickle catalog items one-by-one
  // ============================================
  useEffect(() => {
    if (!enableCatalogPrefetch) return undefined;
    // Wait until the active model is fully loaded before prefetching others
    if (isGlassesAssetLoading) return undefined;

    const idx = activeCatalog.findIndex((g) => g.id === selectedGlassesId);
    const queue = [];
    const queuedUrls = new Set();

    const enqueueItemAssets = (item) => {
      [item?.src, item?.handleSrc].forEach((url) => {
        if (!url || queuedUrls.has(url)) return;
        queuedUrls.add(url);
        queue.push(url);
      });
    };

    if (idx >= 0) {
      for (let offset = 1; offset < activeCatalog.length; offset += 1) {
        enqueueItemAssets(activeCatalog[(idx + offset) % activeCatalog.length]);
      }
    } else {
      for (let i = 0; i < activeCatalog.length; i += 1) {
        enqueueItemAssets(activeCatalog[i]);
      }
    }

    if (!queue.length) return undefined;

    let cancelled = false;

    // Sequential prefetch: load one image, wait for it, then move to the next
    const runSequentialPrefetch = () => {
      if (cancelled || queue.length === 0) return;

      const nextUrl = queue.shift();
      if (!nextUrl || typeof nextUrl !== 'string' || _prefetchedUrls.has(nextUrl)) {
        // Skip already-cached, schedule next immediately
        schedulePrefetchTask(runSequentialPrefetch);
        return;
      }
      if (!nextUrl.startsWith('http') && !nextUrl.startsWith('/') && !nextUrl.startsWith('blob:')) {
        schedulePrefetchTask(runSequentialPrefetch);
        return;
      }

      _prefetchedUrls.add(nextUrl);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = img.onerror = () => {
        if (!cancelled && queue.length > 0) {
          schedulePrefetchTask(runSequentialPrefetch);
        }
      };
      img.src = nextUrl;
    };

    // Kick off prefetch after a short idle delay
    const cancel = schedulePrefetchTask(runSequentialPrefetch);

    return () => {
      cancelled = true;
      if (cancel) cancel();
    };
  }, [activeCatalog, enableCatalogPrefetch, selectedGlassesId, isGlassesAssetLoading]);

  const resumeLiveVideo = useCallback(() => {
    try {
      const v = webcamRef.current?.video;
      if (v && typeof v.play === 'function') v.play();
    } catch {
      // ignore
    }
  }, []);

  const pauseLiveVideo = useCallback(() => {
    try {
      const v = webcamRef.current?.video;
      if (v && typeof v.pause === 'function') v.pause();
    } catch {
      // ignore
    }
  }, []);

  const resetTrackingState = useCallback(({ hideModel = false, resetBoot = false } = {}) => {
    lastKeypointsRef.current = null;
    lastVideoDimsRef.current = { w: 0, h: 0 };
    trackerMissRef.current = 0;
    trackerStateRef.current = { lastInferenceMs: 0, hasIrisData: false };

    poseSmoothingRef.current.init = false;
    poseSmoothingRef.current.pos.set(0, 0, 0);
    poseSmoothingRef.current.rot = { yaw: 0, pitch: 0, roll: 0 };
    poseSmoothingRef.current.scale = 1;
    poseSmoothingRef.current.history = [];
    poseSmoothingRef.current.rotHistory = [];
    poseSmoothingRef.current.prevMedPos.set(0, 0, 0);
    poseSmoothingRef.current.vel = 0;
    poseSmoothingRef.current.boostAlpha = 0;
    poseSmoothingRef.current.sustainCounter = 0;

    templeStabilityRef.current = createTempleStabilityState();

    if (leftTempleRef.current) {
      leftTempleRef.current.rotation.set(0, 0, 0);
      leftTempleRef.current.scale.set(1, 1, 1);
    }
    if (rightTempleRef.current) {
      rightTempleRef.current.rotation.set(0, 0, 0);
      rightTempleRef.current.scale.set(1, 1, 1);
    }
    if (glassesGroup && hideModel) {
      glassesGroup.visible = false;
    }
    if (noWebglFallbackRef.current && canvasRef.current) {
      try {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      } catch {
        // ignore
      }
    }
    if (resetBoot) {
      trackerBootRef.current = { t0: performance.now(), fallbackTried: false };
    }
  }, [glassesGroup]);

  const closePreviewModal = useCallback(() => {
    try {
      if (previewVideoUrlRef.current) URL.revokeObjectURL(previewVideoUrlRef.current);
    } catch {
      // ignore
    }
    previewVideoUrlRef.current = null;
    isProcessingVideoRef.current = false; // Allow screenshots again
    setPreviewPrimary({ label: t('share'), onPrimary: null, disabled: false });
    setPreviewModal({ open: false, title: t('preview'), dataUrl: null, videoUrl: null });
    setFrozenStageDataUrl(null);
    resetTrackingState({ hideModel: true, resetBoot: true });
    if (previewResumeVideoRef.current) {
      previewResumeVideoRef.current = false;
      resumeLiveVideo();
    }
  }, [resetTrackingState, resumeLiveVideo]);

  useEffect(() => {
    const handleVisibleReturn = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (!tabWasHiddenRef.current) return;
      tabWasHiddenRef.current = false;
      resetTrackingState({ hideModel: true, resetBoot: true });
      resumeLiveVideo();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        tabWasHiddenRef.current = true;
        resetTrackingState({ hideModel: true });
        return;
      }
      handleVisibleReturn();
    };

    const handlePageShow = (event) => {
      if (event?.persisted) tabWasHiddenRef.current = true;
      handleVisibleReturn();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [resetTrackingState, resumeLiveVideo]);

  useEffect(() => {
    if (prevGalleryOpenRef.current === galleryOpen) return;

    if (galleryOpen) {
      resetTrackingState({ hideModel: true });
      prevGalleryOpenRef.current = galleryOpen;
      return;
    }

    resetTrackingState({ hideModel: true, resetBoot: true });
    resumeLiveVideo();
    prevGalleryOpenRef.current = galleryOpen;
  }, [galleryOpen, resetTrackingState, resumeLiveVideo]);

  // NOTE: preview open handlers are defined later (after renderSelfieWithModel) to satisfy eslint.

  const takeSelfie = useCallback(() => {
    try {
      const stageEl = cameraBoxRef.current || stageRef.current;
      const videoEl = webcamRef.current?.video;
      if (!stageEl || !videoEl) return;

      // Check if in compare mode - if yes, handle compare flow
      if (comparingWithSelfieId) {
        // Find the original selfie (screenshot A)
        const selfieA = (selfies || []).find(s => s && s.id === comparingWithSelfieId);
        if (!selfieA) {
          showToast(t('screenshotNotFound'), 'error');
          setComparingWithSelfieId(null);
          return;
        }

        // Capture current screenshot (model B) - use normal flow but don't show preview
        const baseFrameDataUrl = captureVideoCoverFrame({
          stageEl,
          videoEl,
          mirrored: true,
          mimeType: 'image/jpeg',
          quality: 0.9,
        });

        const g = glassesGroup;
        const leftTemple = g?.getObjectByName?.('leftTemple');
        const rightTemple = g?.getObjectByName?.('rightTemple');
        const stageW = stageEl.clientWidth || stageSize.w || 800;
        const stageH = stageEl.clientHeight || stageSize.h || 600;

        let templeHinges = null;
        if (leftTemple && rightTemple && cameraRef.current) {
          try {
            const camera = cameraRef.current;
            const leftHinge = new THREE.Vector3(0, 0, 0);
            leftHinge.applyMatrix4(leftTemple.matrixWorld);
            leftHinge.project(camera);
            const leftHingeScreen = {
              x: (leftHinge.x * 0.5 + 0.5) * stageW,
              y: (-(leftHinge.y * 0.5 - 0.5)) * stageH
            };
            const rightHinge = new THREE.Vector3(0, 0, 0);
            rightHinge.applyMatrix4(rightTemple.matrixWorld);
            rightHinge.project(camera);
            const rightHingeScreen = {
              x: (rightHinge.x * 0.5 + 0.5) * stageW,
              y: (-(rightHinge.y * 0.5 - 0.5)) * stageH
            };
            templeHinges = {
              leftHinge: leftHingeScreen,
              rightHinge: rightHingeScreen
            };
          } catch (e) {
            console.warn('Failed to project temple hinges:', e);
          }
        }

        const keypoints = lastKeypointsRef.current;
        let earPositions = null;
        if (keypoints && keypoints.length > 356) {
          const leftEar = keypoints[127];
          const rightEar = keypoints[356];
          const vd = lastVideoDimsRef.current || { w: videoEl.videoWidth, h: videoEl.videoHeight };
          const videoW = vd?.w || videoEl.videoWidth || 1;
          const videoH = vd?.h || videoEl.videoHeight || 1;
          if (leftEar && rightEar && videoW > 0 && videoH > 0) {
            // Store UN-mirrored stage coordinates; renderSelfieWithModel mirrors these onto the mirrored base frame.
            // Keep sizing consistent across devices: stage preview uses the same `cover` presentation as the live view.
            const fit = 'cover';
            const [lx, ly] = mapVideoPointToView(leftEar, videoW, videoH, stageW, stageH, false, fit);
            const [rx, ry] = mapVideoPointToView(rightEar, videoW, videoH, stageW, stageH, false, fit);
            earPositions = {
              leftEar: { x: lx, y: ly },
              rightEar: { x: rx, y: ry },
            };
          }
        }

        const pose = g ? {
          position: [g.position.x, g.position.y, g.position.z],
          rotation: { x: g.rotation.x, y: g.rotation.y, z: g.rotation.z },
          scale: g.scale.x,
          templeHinges,
          earPositions,
          leftTemple: leftTemple ? {
            visible: leftTemple.visible !== false,
            rotation: { x: leftTemple.rotation.x, y: leftTemple.rotation.y, z: leftTemple.rotation.z },
          } : null,
          rightTemple: rightTemple ? {
            visible: rightTemple.visible !== false,
            rotation: { x: rightTemple.rotation.x, y: rightTemple.rotation.y, z: rightTemple.rotation.z },
          } : null,
        } : null;

        let compositeDataUrl = null;
        try {
          compositeDataUrl = captureStageComposite({
            stageEl,
            videoEl,
            scanCanvas: scanCanvasRef.current,
            threeCanvas: canvasRef.current,
            mirrored: true,
            mimeType: 'image/jpeg',
            quality: 0.9,
          });
        } catch {
          // ignore
        }

        // Save screenshot B to gallery
        const next = addSelfie({
          baseFrameDataUrl,
          pose,
          stageW,
          stageH,
          mirrored: true,
          max: 12,
          compositeDataUrl,
          glassesId: selectedGlassesId,
        });
        setSelfies(next);
        const newSelfieId = next[0]?.id || null;
        setActiveSelfieId(newSelfieId);

        // Reset compare mode FIRST to avoid loops
        setComparingWithSelfieId(null);

        // Get model IDs from selfies
        const modelAId = selfieA.glassesId || selectedGlassesId;
        const modelBId = selectedGlassesId;

        // Directly trigger compare with the two selfies
        setTimeout(() => {
          setCompareAId(modelAId);
          setCompareBId(modelBId);
          setCompareOpen(true);
          setCompareRenders({ a: null, b: null, combined: null, loading: true });

          (async () => {
            try {
              const w = Math.round(selfieA.stage?.w || 800);
              const h = Math.round(selfieA.stage?.h || 600);

              // A = original screenshot's composite
              let aImg = selfieA.compositeDataUrl || selfieA.baseFrameDataUrl;

              // B = new screenshot's composite
              let bImg = compositeDataUrl || baseFrameDataUrl;

              const loadImg = (src) =>
                new Promise((resolve, reject) => {
                  const img = new Image();
                  img.onload = () => resolve(img);
                  img.onerror = reject;
                  img.src = src;
                });

              const [ia, ib] = await Promise.all([loadImg(aImg), loadImg(bImg)]);

              const combined = document.createElement('canvas');
              combined.width = w * 2;
              combined.height = h;
              const cctx = combined.getContext('2d');
              if (cctx) {
                cctx.drawImage(ia, 0, 0, w, h);
                cctx.drawImage(ib, w, 0, w, h);
                drawWatermark2D(cctx, w * 2, h, { opacity: 0.5, height: Math.round(h * 0.025) });
              }
              const combinedUrl = combined.toDataURL('image/jpeg', 0.92);

              setCompareRenders({ a: aImg, b: bImg, combined: combinedUrl, loading: false });
            } catch (e) {
              console.warn('Compare render failed:', e);
              setCompareRenders({ a: null, b: null, combined: null, loading: false });
            }
          })();
        }, 100);

        return;
      }

      // 1) Persist selfie "base frame" as before (gallery uses this)
      const baseFrameDataUrl = captureVideoCoverFrame({
        stageEl,
        videoEl,
        mirrored: true,
        mimeType: 'image/jpeg',
        quality: 0.9,
      });

      const g = glassesGroup;
      const leftTemple = g?.getObjectByName?.('leftTemple');
      const rightTemple = g?.getObjectByName?.('rightTemple');

      const stageW = stageEl.clientWidth || stageSize.w || 800;
      const stageH = stageEl.clientHeight || stageSize.h || 600;

      // Project temple hinge positions from 3D to 2D screen coordinates
      let templeHinges = null;
      if (leftTemple && rightTemple && cameraRef.current) {
        try {
          const camera = cameraRef.current;

          // Left temple hinge (start point at temple origin)
          const leftHinge = new THREE.Vector3(0, 0, 0);
          leftHinge.applyMatrix4(leftTemple.matrixWorld);
          leftHinge.project(camera);
          const leftHingeScreen = {
            x: (leftHinge.x * 0.5 + 0.5) * stageW,
            y: (-(leftHinge.y * 0.5 - 0.5)) * stageH
          };

          // Right temple hinge
          const rightHinge = new THREE.Vector3(0, 0, 0);
          rightHinge.applyMatrix4(rightTemple.matrixWorld);
          rightHinge.project(camera);
          const rightHingeScreen = {
            x: (rightHinge.x * 0.5 + 0.5) * stageW,
            y: (-(rightHinge.y * 0.5 - 0.5)) * stageH
          };

          templeHinges = {
            leftHinge: leftHingeScreen,
            rightHinge: rightHingeScreen
          };
        } catch (e) {
          console.warn('Failed to project temple hinges:', e);
        }
      }

      // Get ear positions from face keypoints
      const keypoints = lastKeypointsRef.current;
      let earPositions = null;
      if (keypoints && keypoints.length > 356) {
        const leftEar = keypoints[127];
        const rightEar = keypoints[356];
        const vd = lastVideoDimsRef.current || { w: videoEl.videoWidth, h: videoEl.videoHeight };
        const videoW = vd?.w || videoEl.videoWidth || 1;
        const videoH = vd?.h || videoEl.videoHeight || 1;
        if (leftEar && rightEar && videoW > 0 && videoH > 0) {
          // Store UN-mirrored stage coordinates; renderSelfieWithModel mirrors these onto the mirrored base frame.
          // Keep sizing consistent across devices: stage preview uses the same `cover` presentation as the live view.
          const fit = 'cover';
          const [lx, ly] = mapVideoPointToView(leftEar, videoW, videoH, stageW, stageH, false, fit);
          const [rx, ry] = mapVideoPointToView(rightEar, videoW, videoH, stageW, stageH, false, fit);
          earPositions = {
            leftEar: { x: lx, y: ly },
            rightEar: { x: rx, y: ry },
          };
        }
      }

      const pose =
        g
          ? {
              position: [g.position.x, g.position.y, g.position.z],
              rotation: { x: g.rotation.x, y: g.rotation.y, z: g.rotation.z },
              scale: g.scale.x,
              templeHinges, // 2D screen coordinates of hinge positions
              earPositions, // 2D screen coordinates of ears
              leftTemple: leftTemple
                ? {
                    visible: leftTemple.visible !== false, // Default true unless explicitly false
                    rotation: { x: leftTemple.rotation.x, y: leftTemple.rotation.y, z: leftTemple.rotation.z },
                  }
                : null,
              rightTemple: rightTemple
                ? {
                    visible: rightTemple.visible !== false, // Default true unless explicitly false
                    rotation: { x: rightTemple.rotation.x, y: rightTemple.rotation.y, z: rightTemple.rotation.z },
                  }
                : null,
            }
          : null;

      let compositeDataUrl = null;
      try {
        compositeDataUrl = captureStageComposite({
          stageEl,
          videoEl,
          scanCanvas: scanCanvasRef.current,
          threeCanvas: canvasRef.current,
          mirrored: true,
          mimeType: 'image/jpeg',
          quality: 0.9,
        });
      } catch {
        // ignore
      }

      const next = addSelfie({
        baseFrameDataUrl,
        pose,
        stageW: stageEl.clientWidth || stageSize.w || 800,
        stageH: stageEl.clientHeight || stageSize.h || 600,
        mirrored: true,
        max: 12,
        compositeDataUrl,
        glassesId: selectedGlassesId,
      });
      setSelfies(next);
      setActiveSelfieId(next[0]?.id || null);

      if (compositeDataUrl) {
        setFrozenStageDataUrl(compositeDataUrl);
        pauseLiveVideo();
        previewResumeVideoRef.current = true;
        setPreviewModal({ open: true, title: t('screenshot'), dataUrl: compositeDataUrl });
      }
    } catch (e) {
      console.warn('Selfie capture failed:', e);
      showToast(t('captureFailed'), 'error');
    }
  }, [glassesGroup, pauseLiveVideo, showToast, stageSize.h, stageSize.w, comparingWithSelfieId, selectedGlassesId, selfies, drawWatermark2D]);

  // Long-press recording handlers - Direct MediaRecorder approach
  const MAX_RECORDING_MS = 12_000;
  const MAX_RECORDING_S = Math.round(MAX_RECORDING_MS / 1000);
  const LONG_PRESS_THRESHOLD_MS = 300;

  const requestRecordingPermissionsIfNeeded = useCallback(async () => {
    if (recordingPermissionAskedRef.current) return;

    const mediaDevices = navigator?.mediaDevices;
    if (!mediaDevices?.getUserMedia) return;

    // If we already have an audio track on webcam stream, nothing else to ask.
    try {
      const webcamStream = webcamRef.current?.stream || webcamRef.current?.video?.srcObject || null;
      if (webcamStream && typeof webcamStream.getAudioTracks === 'function' && webcamStream.getAudioTracks().length > 0) {
        recordingPermissionAskedRef.current = true;
        return;
      }
    } catch {
      // ignore
    }

    // If supported, skip prompt when browser already says granted.
    try {
      if (navigator?.permissions?.query) {
        const status = await navigator.permissions.query({ name: 'microphone' });
        if (status?.state === 'granted') {
          recordingPermissionAskedRef.current = true;
          return;
        }
      }
    } catch {
      // ignore: permission query unsupported on some browsers
    }

    try {
      const micStream = await mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });

      if (micStream && typeof micStream.getTracks === 'function') {
        micStream.getTracks().forEach((track) => {
          try { track.stop(); } catch { /* ignore */ }
        });
      }
      recordingPermissionAskedRef.current = true;
    } catch (err) {
      // We still allow silent recording, but we proactively asked as requested.
      recordingPermissionAskedRef.current = true;
      console.warn('Recording permission preflight failed:', err);
    }
  }, []);
  
  const startLongPressRecording = useCallback(async () => {
    if (isRecordingClip || isLongPressingRef.current) return;
    
    const stageEl = cameraBoxRef.current || stageRef.current;
    const videoEl = webcamRef.current?.video;
    const threeCanvas = canvasRef.current;
    if (!stageEl || !videoEl || videoEl.readyState !== 4 || !threeCanvas) {
      return;
    }
    
    isLongPressingRef.current = true;
    setIsLongPressing(true);
    setRecordingProgress(0);
    setIsRecordingClip(true);
    recordingStartTimeRef.current = Date.now();
    
    // Clear previous chunks
    mainChunksRef.current = [];
    rawChunksRef.current = [];
    recordingFirstFrameRef.current = null;
    
    // Setup combined canvas (video + glasses overlay)
    let w = Math.round(stageEl.clientWidth || 800);
    let h = Math.round(stageEl.clientHeight || 600);
    const RECORDING_MIN = 480;
    const RECORDING_MAX_MOBILE = 1080;
    const RECORDING_MAX_DESKTOP = 1920;
    const maxDim = getIsMobileDevice() ? RECORDING_MAX_MOBILE : RECORDING_MAX_DESKTOP;
    let scale = 1;
    if (w > maxDim || h > maxDim) scale = maxDim / Math.max(w, h);
    const largest = Math.max(w, h);
    if (largest < RECORDING_MIN && scale === 1) scale = RECORDING_MIN / largest;
    if (scale !== 1) {
      w = Math.max(2, Math.round(w * scale)) & ~1;
      h = Math.max(2, Math.round(h * scale)) & ~1;
    }
    w = Math.max(2, w) & ~1;
    h = Math.max(2, h) & ~1;

    if (!recordingCanvasRef.current) {
      recordingCanvasRef.current = document.createElement('canvas');
    }
    const canvas = recordingCanvasRef.current;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    // High quality video smoothing for sharp recordings
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Setup raw webcam canvas for parallel recording
    const rawCanvas = document.createElement('canvas');
    rawCanvas.width = w;
    rawCanvas.height = h;
    const rawCtx = rawCanvas.getContext('2d');
    rawCtx.imageSmoothingEnabled = true;
    rawCtx.imageSmoothingQuality = 'high';
    
    // Draw function - composites video + glasses + watermark
    let firstFrameCaptured = false;
    const drawFrame = () => {
      if (!isLongPressingRef.current) return;
      
      const elapsed = Date.now() - recordingStartTimeRef.current;
      setRecordingProgress(Math.min(100, (elapsed / MAX_RECORDING_MS) * 100));
      
      // Draw video frame (mirrored)
      const vw = videoEl.videoWidth || w;
      const vh = videoEl.videoHeight || h;
      const scale = Math.max(w / vw, h / vh);
      const dw = vw * scale;
      const dh = vh * scale;
      const dx = (w - dw) / 2;
      const dy = (h - dh) / 2;
      
      // Main canvas: video + glasses
      ctx.save();
      ctx.clearRect(0, 0, w, h);
      ctx.translate(w, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(videoEl, dx, dy, dw, dh);
      ctx.restore();
      
      // Draw glasses overlay (smoothing on for less pixelation when scaling)
      if (threeCanvas) {
        ctx.drawImage(threeCanvas, 0, 0, w, h);
      }
      
      // Draw watermark - smaller for video
      drawWatermark2D(ctx, w, h, { opacity: 0.45, height: Math.round(h * 0.025) });
      
      // Capture first frame for gallery thumbnail (saved to IndexedDB at high quality)
      if (!firstFrameCaptured && canvas) {
        firstFrameCaptured = true;
        canvas.toBlob((blob) => {
          if (blob) recordingFirstFrameRef.current = blob;
        }, 'image/jpeg', 0.92);
      }
      
      // Raw canvas: just mirrored video (no glasses)
      rawCtx.save();
      rawCtx.clearRect(0, 0, w, h);
      rawCtx.translate(w, 0);
      rawCtx.scale(-1, 1);
      rawCtx.drawImage(videoEl, dx, dy, dw, dh);
      rawCtx.restore();
      
      // Auto-stop at max duration
      if (elapsed >= MAX_RECORDING_MS) {
        stopLongPressRecording();
        return;
      }
      
      recordingAnimFrameRef.current = requestAnimationFrame(drawFrame);
    };
    
    // Prefer MP4 when available: Instagram/Messenger are more reliable with MP4 than WebM.
    // Keep WebM fallback for browsers where MP4 recorder is unavailable.
    // Moderate bitrates (2.5–4 Mbps) avoid browser rejections; enough to avoid pixelation.
    const isMobile = getIsMobileDevice();
    const mimeCandidates = [
      'video/mp4;codecs=avc1',
      'video/mp4',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=vp8',
      'video/webm;codecs=vp9',
      'video/webm',
    ].filter((m) => typeof window.MediaRecorder !== 'undefined' && window.MediaRecorder.isTypeSupported(m));
    if (mimeCandidates.length === 0) mimeCandidates.push('video/webm');

    const mainVideoBps = isMobile ? 2_500_000 : 4_000_000;
    const rawVideoBps = isMobile ? 2_000_000 : 3_000_000;
    const recordingFps = 30;

    // Audio: best-effort microphone capture for clips.
    // We keep webcam constraints video-only so camera works even if mic is denied.
    // If webcam stream has audio tracks (rare), use those; otherwise request audio-only mic stream.
    let audioTracks = [];
    try {
      const webcamStream = webcamRef.current?.stream || webcamRef.current?.video?.srcObject || null;
      if (webcamStream && typeof webcamStream.getAudioTracks === 'function') {
        audioTracks = webcamStream.getAudioTracks() || [];
      }
    } catch {
      // ignore
    }
    if (!audioTracks.length) {
      try {
        const micStream = await navigator?.mediaDevices?.getUserMedia?.({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false,
        });
        if (micStream) {
          recordingAudioStreamRef.current = micStream;
          if (typeof micStream.getAudioTracks === 'function') {
            audioTracks = micStream.getAudioTracks() || [];
          }
        }
      } catch (e) {
        // Mic not available/allowed — record video without audio.
        // Keep silent to avoid spamming toasts; user can grant mic later.
        console.warn('Microphone not available for recording:', e);
      }
    }

    // If user released while waiting for mic permission, abort cleanly.
    if (!isLongPressingRef.current) {
      try {
        const s = recordingAudioStreamRef.current;
        if (s && typeof s.getTracks === 'function') {
          s.getTracks().forEach((t) => {
            try { t.stop(); } catch { /* ignore */ }
          });
        }
      } catch {
        // ignore
      }
      recordingAudioStreamRef.current = null;
      return;
    }

    const createRecorder = (stream, bps, chunks, mimeList) => {
      for (const mime of mimeList) {
        try {
          const r = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: bps });
          r.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
          r.start(100);
          return { recorder: r, mime };
        } catch (_) {
          try {
            const r = new MediaRecorder(stream, { mimeType: mime });
            r.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
            r.start(100);
            return { recorder: r, mime };
          } catch (_) { /* try next mime */ }
        }
      }
      return null;
    };

    const mainCanvasStream = canvas.captureStream(recordingFps);
    const mainStream = audioTracks.length
      ? new MediaStream([...mainCanvasStream.getVideoTracks(), ...audioTracks])
      : mainCanvasStream;
    const mainResult = createRecorder(mainStream, mainVideoBps, mainChunksRef.current, mimeCandidates);
    if (mainResult) {
      mainRecorderRef.current = mainResult.recorder;
      recordingMimeRef.current = mainResult.mime;
    } else {
      console.warn('Main recorder failed: no supported mime/bitrate combination');
    }

    const rawCanvasStream = rawCanvas.captureStream(recordingFps);
    const rawStream = audioTracks.length
      ? new MediaStream([...rawCanvasStream.getVideoTracks(), ...audioTracks])
      : rawCanvasStream;
    const rawMimes = recordingMimeRef.current ? [recordingMimeRef.current] : mimeCandidates;
    const rawResult = createRecorder(rawStream, rawVideoBps, rawChunksRef.current, rawMimes);
    if (rawResult) rawRecorderRef.current = rawResult.recorder;
    
    // Start drawing
    drawFrame();
    
  }, [isRecordingClip, drawWatermark2D]);
  
  const stopLongPressRecording = useCallback(async () => {
    // Clear long-press timer
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    
    // Cancel animation frame
    if (recordingAnimFrameRef.current) {
      cancelAnimationFrame(recordingAnimFrameRef.current);
      recordingAnimFrameRef.current = null;
    }
    
    const wasRecording = isLongPressingRef.current;
    const duration = recordingStartTimeRef.current ? Date.now() - recordingStartTimeRef.current : 0;
    
    // IMPORTANT: Set processing flag BEFORE clearing isLongPressing
    // This prevents takeSelfie from being called (e.g. duplicate pointerUp) during/after stop
    if (wasRecording) {
      isProcessingVideoRef.current = true;
    }
    
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    isLongPressingRef.current = false;
    setIsLongPressing(false);
    setRecordingProgress(0);
    recordingStartTimeRef.current = null;
    
    // Only process if we recorded for at least 500ms
    if (wasRecording && duration > 500) {
      
      const recordedMime = recordingMimeRef.current || mainRecorderRef.current?.mimeType || 'video/mp4';
      const ext = recordedMime.includes('mp4') ? 'mp4' : 'webm';
      const blobMime = recordedMime.includes('mp4') ? 'video/mp4' : 'video/webm';
      
      // Stop recorders and collect blobs
      const stopRecorder = (recorder, chunks) => new Promise((resolve) => {
        if (!recorder || recorder.state === 'inactive') {
          resolve(chunks.length > 0 ? new Blob(chunks, { type: blobMime }) : null);
          return;
        }
        recorder.onstop = () => {
          const blob = chunks.length > 0 ? new Blob(chunks, { type: blobMime }) : null;
          resolve(blob);
        };
        recorder.stop();
      });
      
      try {
        const [mainBlob, rawBlob] = await Promise.all([
          stopRecorder(mainRecorderRef.current, mainChunksRef.current),
          stopRecorder(rawRecorderRef.current, rawChunksRef.current),
        ]);
        
        if (mainBlob && mainBlob.size > 0) {
          // Open video modal FIRST (before save). If IndexedDB fails, user still sees video and can share.
          const videoUrl = URL.createObjectURL(mainBlob);
          previewVideoUrlRef.current = videoUrl;
setPreviewModal({ open: true, title: t('video'), dataUrl: null, videoUrl });
            setPreviewPrimary({
            label: t('share'),
            onPrimary: async () => {
              try {
                const fileName = `mojenaocale-video.${ext}`;
                const file = new File([mainBlob], fileName, { type: blobMime });
                if (navigator?.share && navigator?.canShare?.({ files: [file] })) {
                  try {
                    // Some targets (Instagram/Messenger) reject title/text with file shares.
                    await navigator.share({ files: [file] });
                  } catch (shareErr) {
                    await navigator.share({
                      title: 'MojeNaocale.ba',
                      text: 'Moj izgled sa naočalama',
                      files: [file],
                    });
                  }
                  return;
                }
              } catch (e) {
                console.log('Share failed:', e);
              }
              downloadBlob(mainBlob, `mojenaocale-video.${ext}`);
            },
            disabled: false,
          });

          // Save to gallery in background (don't block modal)
          try {
            // Give first-frame capture time to complete (toBlob is async)
            await new Promise((r) => setTimeout(r, 150));
            let thumbnail = recordingFirstFrameRef.current || null;
            if (!thumbnail) {
              try {
                const canvas = recordingCanvasRef.current;
                if (canvas) {
                  thumbnail = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
                }
              } catch {
                // ignore
              }
            }
            recordingFirstFrameRef.current = null;
            // Save highest-quality output to IndexedDB: full bitrate blob (no re-encode), high-quality thumbnail.
            const clip = {
              id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
              ts: Date.now(),
              v: 2,
              durationMs: duration,
              withGlassesBlob: mainBlob,
              rawVideoBlob: rawBlob,
              thumbnail: thumbnail,
              glassesId: selectedGlassesId,
            };
            await saveVideoClip(clip);
            const clips = await loadVideoClips();
            setVideoClips(clips);
            setActiveClipId(clip.id);
            if (thumbnail) {
              try {
                const thumbUrl = await blobToDataUrl(thumbnail);
                setClipThumbs((prev) => ({ ...prev, [clip.id]: thumbUrl }));
              } catch {
                // ignore
              }
            }
          } catch (e) {
            console.warn('Gallery save failed:', e);
            showToast(t('videoNotSavedToGallery'), 'error');
          }
          // isProcessingVideoRef stays true until modal closed (closePreviewModal)
        } else {
          isProcessingVideoRef.current = false;
          if (mainBlob == null || mainBlob.size === 0) {
            showToast(t('videoNotSaved'), 'error');
          }
        }
      } catch (e) {
        console.warn('Video processing failed:', e);
        showToast(t('recordingFailed'), 'error');
        isProcessingVideoRef.current = false;
      }
    } else {
      // Stop recorders without processing (short recording or not recording)
      try {
        if (mainRecorderRef.current?.state !== 'inactive') mainRecorderRef.current?.stop();
        if (rawRecorderRef.current?.state !== 'inactive') rawRecorderRef.current?.stop();
      } catch {
        // ignore
      }
      if (wasRecording) {
        isProcessingVideoRef.current = false;
      }
    }
    
    // Clear refs
    mainRecorderRef.current = null;
    rawRecorderRef.current = null;
    mainChunksRef.current = [];
    rawChunksRef.current = [];
    recordingMimeRef.current = null;
    try {
      const s = recordingAudioStreamRef.current;
      if (s && typeof s.getTracks === 'function') {
        s.getTracks().forEach((t) => {
          try { t.stop(); } catch { /* ignore */ }
        });
      }
    } catch {
      // ignore
    }
    recordingAudioStreamRef.current = null;

    setIsRecordingClip(false);
  }, [showToast, selectedGlassesId]);
  
  const handleShutterPointerDown = useCallback((e) => {
    e.preventDefault();
    if (isRecordingClip || isLongPressingRef.current) return;
    shutterPointerActiveRef.current = true;

    // Kick off permission preflight immediately while the user is pressing.
    recordingPermissionPromiseRef.current = requestRecordingPermissionsIfNeeded();
    
    // Start timer for long-press detection
    longPressTimerRef.current = setTimeout(async () => {
      if (recordingPermissionPromiseRef.current) {
        try {
          await recordingPermissionPromiseRef.current;
        } catch {
          // ignore
        }
      }
      if (!shutterPointerActiveRef.current) return;
      startLongPressRecording();
    }, LONG_PRESS_THRESHOLD_MS);
  }, [isRecordingClip, requestRecordingPermissionsIfNeeded, startLongPressRecording]);
  
  const handleShutterPointerUp = useCallback((e) => {
    e.preventDefault();
    shutterPointerActiveRef.current = false;
    
    // If we're processing a video, don't do anything
    if (isProcessingVideoRef.current) return;
    
    // If we haven't started recording yet, it's a tap -> screenshot
    if (!isLongPressingRef.current) {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      // Take screenshot only if not recording
      if (!isRecordingClip) {
        recordingPermissionPromiseRef.current = null;
        takeSelfie();
      }
    } else {
      // Stop recording
      stopLongPressRecording();
    }
  }, [takeSelfie, stopLongPressRecording, isRecordingClip]);
  
  const handleShutterPointerLeave = useCallback(() => {
    shutterPointerActiveRef.current = false;

    // If finger leaves the button area, stop recording
    if (isLongPressingRef.current) {
      stopLongPressRecording();
    } else if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    recordingPermissionPromiseRef.current = null;
  }, [stopLongPressRecording]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      if (recordingAnimFrameRef.current) cancelAnimationFrame(recordingAnimFrameRef.current);
    };
  }, []);

  // Cleanup on unmount for preview URLs
  useEffect(() => {
    return () => {
      if (previewVideoUrlRef.current) {
        URL.revokeObjectURL(previewVideoUrlRef.current);
      }
    }
  }, []);

  const renderSelfieWithModel = useCallback(
    async (selfie, glassesId, { skipWatermark = false, compareB = false } = {}) => {
      if (!selfie?.baseFrameDataUrl) throw new Error('Missing base frame');
      const modelDef = getGlassesFromCatalog(glassesId);
      if (!modelDef?.src) throw new Error('Missing model src');

      const w = Math.round(selfie.stage?.w || stageRef.current?.clientWidth || 800);
      const h = Math.round(selfie.stage?.h || stageRef.current?.clientHeight || 600);

      const loadImg = (src) =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = src;
        });

      const baseImg = await loadImg(selfie.baseFrameDataUrl);

      // Compare B: 2D fallback when 3D gives black screen (GPU can't handle two renders)
      if (compareB) {
        const toUrl = (v) => (typeof v === 'string' ? v : (v && v.default) || '');
        const frameSrc = toUrl(modelDef.src);
        const glassesImg = await loadImg(frameSrc);
        const handleToLoad = modelDef.handleSrc || handleSrc;
        let handleImg = null;
        try {
          // Reuse cached handle texture if available (avoids re-download on compare)
          const cachedTex = handleTextureCacheRef.current?.get(handleToLoad);
          if (cachedTex?.image) {
            handleImg = cachedTex.image;
          } else {
            const loader = new THREE.TextureLoader();
            const tex = await new Promise((resolve, reject) => {
              loader.load(handleToLoad, (t) => resolve(t), undefined, reject);
            });
            if (tex && tex.image) {
              handleImg = tex.image;
              handleTextureCacheRef.current?.set(handleToLoad, tex);
            }
          }
        } catch (_) {}
        const pose = selfie.pose || {};
        const scale = typeof pose.scale === 'number' ? pose.scale : 1;
        const p = pose.position || [0, 0, 1];
        const px = p[0], py = p[1], pz = p[2];
        const d = CAMERA_Z - pz;
        const vFovRad = 75 * Math.PI / 180;
        const tanHalfV = Math.tan(vFovRad / 2);
        const halfH = d * tanHalfV;
        const halfW = halfH * (w / h);
        const cx = d > 0.1 ? w / 2 * (1 + px / halfW) : w / 2;
        const cy = d > 0.1 ? h / 2 * (1 - py / halfH) : h / 2;
        const refD = CAMERA_Z - BASE_GLASSES_Z_REF;
        const basePx = getBaseFramePixels(w, h);
        const frameW = basePx * scale * (d > 0.1 ? refD / d : 1);
        const texAspect = (glassesImg.naturalWidth || 1) / (glassesImg.naturalHeight || 1);
        const frameH = frameW / texAspect;
        const rotZ = pose.rotation?.z ?? 0;
        const hingeX = modelDef.templeHingeOffset ?? 0.92;

        // Handle legacy temple length: if < 50, convert from world units to mm
        let templeMm = modelDef.handleWidth ?? modelDef.templeLength ?? TEMPLE_CONFIG.lengthMm;
        if (templeMm !== undefined && templeMm !== null && templeMm < 50) {
          templeMm = (templeMm / 3) * FRAME_REFERENCE_MM; // Legacy: 3 world units ≈ 140mm
        }

        const frameMm = modelDef.frameWidth ?? FRAME_REFERENCE_MM;
        // Temple length tuned for 2D appearance (shorter than 3D because no foreshortening)
        const templeLengthPx = Math.max(8, (templeMm / frameMm) * frameW * 0.7);
        const out = document.createElement('canvas');
        out.width = w;
        out.height = h;
        const ctx = out.getContext('2d');
        if (!ctx) throw new Error('2d context missing');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(baseImg, 0, 0, w, h);
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(-rotZ);
        ctx.drawImage(glassesImg, -frameW / 2, -frameH / 2, frameW, frameH);

        // Draw temples: try hinge→ear in screen space, fallback to frame-local
        if (handleImg && handleImg.naturalWidth > 0) {
          const handleAspect = handleImg.naturalWidth / handleImg.naturalHeight;
          const hinges = pose.templeHinges;
          const ears = pose.earPositions;

          if (hinges && ears) {
            // New approach: hinge → ear in screen space
            ctx.restore(); // Exit frame transform
            ctx.globalAlpha = 1;

            // Left temple
            if (pose.leftTemple?.visible !== false && hinges.leftHinge && ears.leftEar) {
              const hingeX = w - hinges.leftHinge.x; // Mirror
              const hingeY = hinges.leftHinge.y;
              const earX = w - ears.leftEar.x; // Mirror
              const earY = ears.leftEar.y;

              const dx = earX - hingeX;
              const dy = earY - hingeY;
              const distance = Math.sqrt(dx * dx + dy * dy);
              const angle = Math.atan2(dy, dx);
              const height = distance / handleAspect * TEMPLE_CONFIG.heightScale;

              ctx.save();
              ctx.translate(hingeX, hingeY);
              ctx.rotate(angle);
              ctx.drawImage(handleImg, 0, 0, handleImg.naturalWidth, handleImg.naturalHeight, 0, -height/2, distance, height);
              ctx.restore();
            }

            // Right temple
            if (pose.rightTemple?.visible !== false && hinges.rightHinge && ears.rightEar) {
              const hingeX = w - hinges.rightHinge.x; // Mirror
              const hingeY = hinges.rightHinge.y;
              const earX = w - ears.rightEar.x; // Mirror
              const earY = ears.rightEar.y;

              const dx = earX - hingeX;
              const dy = earY - hingeY;
              const distance = Math.sqrt(dx * dx + dy * dy);
              const angle = Math.atan2(dy, dx);
              const height = distance / handleAspect * TEMPLE_CONFIG.heightScale;

              ctx.save();
              ctx.translate(hingeX, hingeY);
              ctx.rotate(angle);
              ctx.drawImage(handleImg, 0, 0, handleImg.naturalWidth, handleImg.naturalHeight, 0, -height/2, distance, height);
              ctx.restore();
            }

            ctx.save(); // Re-enter for watermark
          } else {
            // Fallback for old screenshots without hinge/ear data: simple fixed angles
            const templeHeightPx = templeLengthPx / handleAspect * TEMPLE_CONFIG.heightScale;
            const hingePxX = (frameW / 2) * hingeX;
            const hingePxY = -frameH / 2;

            ctx.globalAlpha = 1;

            if (pose.leftTemple?.visible !== false) {
              ctx.save();
              ctx.translate(-hingePxX, hingePxY);
              ctx.rotate(Math.PI + 0.25);
              ctx.drawImage(handleImg, 0, 0, handleImg.naturalWidth, handleImg.naturalHeight, 0, -templeHeightPx/2, templeLengthPx, templeHeightPx);
              ctx.restore();
            }

            if (pose.rightTemple?.visible !== false) {
              ctx.save();
              ctx.translate(hingePxX, hingePxY);
              ctx.rotate(-0.25);
              ctx.drawImage(handleImg, 0, 0, handleImg.naturalWidth, handleImg.naturalHeight, 0, -templeHeightPx/2, templeLengthPx, templeHeightPx);
              ctx.restore();
            }

            ctx.restore(); // Exit frame transform
            ctx.save(); // Re-enter for watermark
          }
        } else {
          ctx.restore(); // Exit frame transform
          ctx.save(); // Re-enter for watermark
        }

        ctx.restore();
        if (!skipWatermark) drawWatermark2D(ctx, w, h, { opacity: 0.6 });
        return out.toDataURL('image/jpeg', 0.92);
      }

      const makeRenderer = () => {
        // Reduced supersampling to avoid GPU memory exhaustion (was 24x, now 3x)
        const supersampleFactor = 3; // Balance between quality and GPU memory usage
        const renderW = w * supersampleFactor;
        const renderH = h * supersampleFactor;
        
        const c = document.createElement('canvas');
        c.width = w; // Final canvas size
        c.height = h;
        // Render to temporary high-res canvas first
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = renderW;
        tempCanvas.height = renderH;
        
        const r = new THREE.WebGLRenderer({ 
          canvas: tempCanvas, 
          alpha: true, 
          antialias: true,
          premultipliedAlpha: false,
          preserveDrawingBuffer: true,
          powerPreference: 'high-performance'
        });
        r.setPixelRatio(1); // Use 1:1 pixel ratio for supersampling
        r.setSize(renderW, renderH, false);
        r.setClearColor(0x000000, 0);
        
        // Enable smooth rendering and maximum antialiasing
        const gl = r.getContext();
        if (gl) {
          gl.enable(gl.SMOOTH_LINE_WIDTH_RANGE);
          gl.enable(gl.SMOOTH_POINT_SIZE_RANGE);
          // Enable multisample antialiasing if available
          if (gl.getParameter(gl.SAMPLES)) {
            gl.enable(gl.MULTISAMPLE);
          }
          // Enable line smoothing
          gl.enable(gl.LINE_SMOOTH);
          gl.enable(gl.POLYGON_SMOOTH_HINT);
          gl.hint(gl.LINE_SMOOTH_HINT, gl.NICEST);
          gl.hint(gl.POLYGON_SMOOTH_HINT, gl.NICEST);
        }
        
        return { 
          renderer: r, 
          canvas: c,
          tempCanvas: tempCanvas,
          supersampleFactor: supersampleFactor,
          renderW: renderW,
          renderH: renderH
        };
      };
      const addLights = (s) => {
        s.add(new THREE.AmbientLight(0xffffff, 0.6));
        const d = new THREE.DirectionalLight(0xffffff, 0.8);
        d.position.set(0, 1, 2);
        s.add(d);
        const f = new THREE.DirectionalLight(0xffffff, 0.3);
        f.position.set(0, -1, 1);
        s.add(f);
      };
      const camera = new THREE.PerspectiveCamera(75, w / h, 0.1, 1000);
      camera.position.z = CAMERA_Z;

      const loader = new THREE.TextureLoader();
      const loadTextureLocal = (src) =>
        new Promise((resolve, reject) => {
          loader.load(
            src,
            (tex) => {
              tex.colorSpace = THREE.SRGBColorSpace;
              // Use better filtering to eliminate edge artifacts and reckanja
              tex.minFilter = THREE.LinearMipmapLinearFilter; // Better quality with mipmaps
              tex.magFilter = THREE.LinearFilter;
              tex.generateMipmaps = true; // Enable mipmaps for better quality
              tex.wrapS = THREE.ClampToEdgeWrapping; // Clamp to edge to prevent border artifacts
              tex.wrapT = THREE.ClampToEdgeWrapping;
              tex.anisotropy = 16; // Maximum anisotropy for best texture quality
              resolve(tex);
              },
              undefined,
            (err) => reject(err)
          );
        });

      const frameTexture = await loadTextureLocal(modelDef.src);
      // Keep mipmaps enabled (loadTextureLocal config) for smoother, more realistic edges
      let handleTexture = null;
      try {
        // Use product-specific handle or fallback to default
        const handleToLoad = modelDef.handleSrc || handleSrc;
        handleTexture = await loadTextureLocal(handleToLoad);
      } catch {
        handleTexture = null;
      }

      const texImg = frameTexture.image;
      const texAspect = texImg ? (texImg.width || 1) / (texImg.height || 1) : 2;
      // Use PlaneGeometry for front only: no box side faces = no visible line at frame edge (3D box caused rim line)
      const frameGeometry = new THREE.PlaneGeometry(2, 2 / texAspect);
      
      // Custom shader to eliminate jagged edges with edge feathering
      // UV inset: sample texture 3% away from edges so filtering/mipmaps never hit literal edge (avoids edge line)
      const uvInset = 0.02;
      const edgeDiscard = 0.02;
      const frameVertexShader = `
        varying vec2 vUv;
        varying vec2 vUvRaw;
        void main() {
          vUvRaw = uv;
          vUv = uv * (1.0 - 2.0 * ${uvInset}) + ${uvInset};
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `;
      
      // Non-premultiplied alpha + wide rim discard to avoid overlay line
      const frameFragmentShader = `
        uniform sampler2D map;
        uniform float featherAmount;
        varying vec2 vUv;
        varying vec2 vUvRaw;
        void main() {
          vec2 edgeDist = min(vUvRaw, 1.0 - vUvRaw);
          float minEdgeDist = min(edgeDist.x, edgeDist.y);
          if (minEdgeDist < ${edgeDiscard}) discard;
          vec4 texColor = texture2D(map, vUv);
          float fade = smoothstep(0.0, featherAmount, minEdgeDist);
          texColor.a *= fade;
          if (texColor.a < 0.02) discard;
          gl_FragColor = texColor;
        }
      `;
      // Back layers: same feathering but discard where alpha < threshold so only frame is stacked
      const opacityThreshold = FRAME_OPACITY_STACK_THRESHOLD;
      const frameFragmentShaderOpaqueOnly = `
        uniform sampler2D map;
        uniform float featherAmount;
        varying vec2 vUv;
        varying vec2 vUvRaw;
        void main() {
          vec2 edgeDist = min(vUvRaw, 1.0 - vUvRaw);
          float minEdgeDist = min(edgeDist.x, edgeDist.y);
          if (minEdgeDist < ${edgeDiscard}) discard;
          vec4 texColor = texture2D(map, vUv);
          float fade = smoothstep(0.0, featherAmount, minEdgeDist);
          texColor.a *= fade;
          if (texColor.a < 0.02) discard;
          if (texColor.a < ${opacityThreshold}) discard;
          gl_FragColor = texColor;
        }
      `;
      const baseFrameMaterial = new THREE.ShaderMaterial({
        uniforms: {
          map: { value: frameTexture },
          featherAmount: { value: 0.18 }
        },
        vertexShader: frameVertexShader,
        fragmentShader: frameFragmentShader,
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: true,
        toneMapped: !vtoFlags.toneMapping2DDisabled,
      });
      const backLayerMaterial = new THREE.ShaderMaterial({
        uniforms: {
          map: { value: frameTexture },
          featherAmount: { value: 0.18 }
        },
        vertexShader: frameVertexShader,
        fragmentShader: frameFragmentShaderOpaqueOnly,
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: true,
        toneMapped: !vtoFlags.toneMapping2DDisabled,
      });
      const halfDepth = FRAME_DEPTH / 2;
      const frameContainer = new THREE.Group();
      frameContainer.name = 'frame';
  const frameStackLayers = vtoFlags.twoD3DLook && !getIsLowEndMobile() ? FRAME_STACK_LAYERS_HIGH : FRAME_STACK_LAYERS_BASE;
  const isFrontLayer = (idx) => idx === frameStackLayers - 1;
  for (let i = 0; i < frameStackLayers; i++) {
    const t = frameStackLayers > 1 ? i / (frameStackLayers - 1) : 0.5;
        const z = -halfDepth + t * FRAME_DEPTH;
        const mat = isFrontLayer(i) ? baseFrameMaterial.clone() : backLayerMaterial.clone();
        mat.polygonOffset = true;
        mat.polygonOffsetFactor = -1;
    mat.polygonOffsetUnits = (i - (frameStackLayers - 1) / 2) * 2;
        const slice = new THREE.Mesh(frameGeometry.clone(), mat);
        slice.position.set(0, 0, z);
        slice.name = isFrontLayer(i) ? 'frameSlice' : 'frameStackSlice';
        slice.renderOrder = 1;
        frameContainer.add(slice);
      }
      frameGeometry.dispose();
      const frameGroup = new THREE.Group();
      frameGroup.add(frameContainer);

      let hingeX, hingeY;
      if (modelDef.templeHingeOffset !== undefined && modelDef.templeHingeOffset !== null) {
        hingeX = modelDef.templeHingeOffset;
        hingeY = modelDef.templeYOffset ?? 0;
      } else {
        const detected = detectHingePositionFromTexture(frameTexture);
        hingeX = detected.hingeX;
        hingeY = detected.hingeY;
      }
      const templeLength = modelDef.handleWidth ?? modelDef.templeLength ?? TEMPLE_CONFIG.lengthMm;
      const frameWidthMm = modelDef.frameWidth ?? FRAME_REFERENCE_MM;
      const handleContentScaleX = modelDef.handleContentScaleX ?? 1;

      const leftTemple = createTempleArm(true, handleTexture, templeLength, frameWidthMm, handleContentScaleX);
      if (!leftTemple) {
        console.error('[renderSelfieWithModel] leftTemple is null/undefined!');
      }
      leftTemple.name = 'leftTemple';
      // Store hinge position - will be applied after pose
      leftTemple.userData.hingePosition = { x: -hingeX, y: hingeY, z: 0 };
      const rightTemple = createTempleArm(false, handleTexture, templeLength, frameWidthMm, handleContentScaleX);
      if (!rightTemple) {
        console.error('[renderSelfieWithModel] rightTemple is null/undefined!');
      }
      rightTemple.name = 'rightTemple';
      // Store hinge position - will be applied after pose
      rightTemple.userData.hingePosition = { x: hingeX, y: hingeY, z: 0 };
      console.log('[renderSelfieWithModel] Temples created:', { leftTemple, rightTemple, leftChildren: leftTemple?.children?.length, rightChildren: rightTemple?.children?.length });
      // Ensure temples are visible and properly configured
      [leftTemple, rightTemple].forEach((t) => {
        t.visible = true;
        t.traverse((o) => {
          if (o.isMesh && o.material) {
            o.visible = true;
            // Use HIGHER renderOrder than frame so temples render on top
            o.renderOrder = 2; // Higher than frame (1); depth handles overlap
            o.material.depthTest = true; // Use depth test (matches live renderer with face occluder)
            o.material.depthWrite = false; // Same as frame and createTempleArm
            o.frustumCulled = false;
            console.log('[renderSelfieWithModel] Configuring temple mesh:', {
              name: o.name,
              visible: o.visible,
              material: o.material,
              hasTexture: !!o.material.map,
              renderOrder: o.renderOrder,
              depthTest: o.material.depthTest,
              depthWrite: o.material.depthWrite,
              position: o.position,
              worldPosition: new THREE.Vector3().setFromMatrixPosition(o.matrixWorld)
            });
            // Only for compare B: opaque handle so it’s visible; frame can’t occlude when we use two-layer.
            // Always ensure material is properly configured for visibility
            // Configure material exactly like createTempleArm does
            if (handleTexture) {
              o.material.map = handleTexture;
              o.material.transparent = true;
              o.material.side = THREE.FrontSide; // Single side to prevent duplication when mirrored
              o.material.depthWrite = false; // Same as createTempleArm
              o.material.needsUpdate = true;
            } else {
              // Fallback material (same as createTempleArm)
              o.material.color = new THREE.Color(TEMPLE_CONFIG.color);
              o.material.transparent = true;
              o.material.opacity = 0.9;
              o.material.side = THREE.FrontSide; // Single side to prevent duplication when mirrored
              o.material.depthWrite = false;
            }
          }
        });
      });

      // frameGroup is already created above with multistacking layers
      // Note: templeGroup is created but not used - temples are added directly to scene
      // This is intentional to match the main component behavior

      const pose = selfie.pose;
      
      // Apply temple visibility and rotations FIRST (before group transforms)
      // This ensures temples are positioned correctly relative to the head
      // NOTE: In screenshots, temples should ALWAYS be visible (ignore pose.visible)
      leftTemple.visible = true; // Always visible in screenshots
      rightTemple.visible = true; // Always visible in screenshots
      
      if (pose?.leftTemple?.rotation) {
        // Apply rotations from pose data
        const tr = pose.leftTemple.rotation;
        leftTemple.rotation.set(tr.x || 0, tr.y || 0, tr.z || 0);
      }
      
      if (pose?.rightTemple?.rotation) {
        // Apply rotations from pose data
        const tr = pose.rightTemple.rotation;
        rightTemple.rotation.set(tr.x || 0, tr.y || 0, tr.z || 0);
      }
      
      const applyPose = (g) => {
        if (!pose) return;
        const p = pose.position || [0, 0, 1];
        g.position.set(p[0], p[1], p[2]);
        const r = pose.rotation || { x: 0, y: 0, z: 0 };
        g.rotation.set(r.x || 0, r.y || 0, r.z || 0);
        const s = typeof pose.scale === 'number' ? pose.scale : 1;
        g.scale.setScalar(s);
      };
      applyPose(frameGroup);
      // Apply pose to temples, then add hinge offset
      applyPose(leftTemple);
      if (leftTemple.userData.hingePosition) {
        const hp = leftTemple.userData.hingePosition;
        leftTemple.position.add(new THREE.Vector3(hp.x, hp.y, hp.z));
      }
      applyPose(rightTemple);
      if (rightTemple.userData.hingePosition) {
        const hp = rightTemple.userData.hingePosition;
        rightTemple.position.add(new THREE.Vector3(hp.x, hp.y, hp.z));
      }
      console.log('[renderSelfieWithModel] After pose application:', {
        leftPos: leftTemple.position,
        rightPos: rightTemple.position,
        framePos: frameGroup.position
      });

      let out;
      // Use same rendering approach for both compare A and B to ensure temples render correctly
      // Add temples DIRECTLY to scene (like in main component), not through templeGroup
      const scene = new THREE.Scene();
      addLights(scene);
      console.log('[renderSelfieWithModel] Before adding to scene:', { leftTemple, rightTemple, frameGroup });
      if (leftTemple) {
        scene.add(leftTemple);  // Add temples directly (like in main component)
        console.log('[renderSelfieWithModel] leftTemple added to scene');
      } else {
        console.error('[renderSelfieWithModel] leftTemple is null, cannot add to scene!');
      }
      if (rightTemple) {
        scene.add(rightTemple);
        console.log('[renderSelfieWithModel] rightTemple added to scene');
      } else {
        console.error('[renderSelfieWithModel] rightTemple is null, cannot add to scene!');
      }
      scene.add(frameGroup);   // Then frame
      console.log('[renderSelfieWithModel] Scene children count:', scene.children.length, 'children:', scene.children.map(c => c.name || c.type));
      const { renderer, canvas: glCanvas, tempCanvas, supersampleFactor, renderW, renderH } = makeRenderer();
      console.log('[renderSelfieWithModel] Before render:', {
        sceneChildren: scene.children.length,
        leftTempleVisible: leftTemple.visible,
        rightTempleVisible: rightTemple.visible,
        leftTempleWorldPos: new THREE.Vector3().setFromMatrixPosition(leftTemple.matrixWorld),
        rightTempleWorldPos: new THREE.Vector3().setFromMatrixPosition(rightTemple.matrixWorld),
        frameWorldPos: new THREE.Vector3().setFromMatrixPosition(frameGroup.matrixWorld)
      });
      renderer.render(scene, camera);
      // Force WebGL to finish so drawImage(webglCanvas) doesn't read a blank buffer (fixes compare B black)
      const gl = renderer.getContext();
      if (gl) {
        try {
          gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4));
        } catch (_) { /* ignore */ }
      }
      console.log('[renderSelfieWithModel] After render, canvas size:', glCanvas.width, 'x', glCanvas.height);
      
      // Apply FXAA (Fast Approximate Anti-Aliasing) to eliminate jagged edges
      // FXAA shader code (simplified version for edge smoothing)
      const fxaaVertexShader = `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `;
      
      const fxaaFragmentShader = `
        uniform sampler2D tDiffuse;
        uniform vec2 resolution;
        varying vec2 vUv;
        
        // FXAA constants - more aggressive for better edge smoothing
        #define FXAA_REDUCE_MIN (1.0/256.0)
        #define FXAA_REDUCE_MUL (1.0/16.0)
        #define FXAA_SPAN_MAX 12.0
        
        void main() {
          vec2 inverseResolution = 1.0 / resolution;
          vec3 rgbNW = texture2D(tDiffuse, vUv + vec2(-1.0, -1.0) * inverseResolution).xyz;
          vec3 rgbNE = texture2D(tDiffuse, vUv + vec2(1.0, -1.0) * inverseResolution).xyz;
          vec3 rgbSW = texture2D(tDiffuse, vUv + vec2(-1.0, 1.0) * inverseResolution).xyz;
          vec3 rgbSE = texture2D(tDiffuse, vUv + vec2(1.0, 1.0) * inverseResolution).xyz;
          vec3 rgbM = texture2D(tDiffuse, vUv).xyz;
          
          vec3 luma = vec3(0.299, 0.587, 0.114);
          float lumaNW = dot(rgbNW, luma);
          float lumaNE = dot(rgbNE, luma);
          float lumaSW = dot(rgbSW, luma);
          float lumaSE = dot(rgbSE, luma);
          float lumaM = dot(rgbM, luma);
          
          float lumaMin = min(lumaM, min(min(lumaNW, lumaNE), min(lumaSW, lumaSE)));
          float lumaMax = max(lumaM, max(max(lumaNW, lumaNE), max(lumaSW, lumaSE)));
          
          vec2 dir;
          dir.x = -((lumaNW + lumaNE) - (lumaSW + lumaSE));
          dir.y = ((lumaNW + lumaSW) - (lumaNE + lumaSE));
          
          float dirReduce = max((lumaNW + lumaNE + lumaSW + lumaSE) * (0.25 * FXAA_REDUCE_MUL), FXAA_REDUCE_MIN);
          float rcpDirMin = 1.0 / (min(abs(dir.x), abs(dir.y)) + dirReduce);
          dir = min(vec2(FXAA_SPAN_MAX, FXAA_SPAN_MAX), max(vec2(-FXAA_SPAN_MAX, -FXAA_SPAN_MAX), dir * rcpDirMin)) * inverseResolution;
          
          vec3 rgbA = 0.5 * (texture2D(tDiffuse, vUv + dir * (1.0/3.0 - 0.5)).xyz + texture2D(tDiffuse, vUv + dir * (2.0/3.0 - 0.5)).xyz);
          vec3 rgbB = rgbA * 0.5 + 0.25 * (texture2D(tDiffuse, vUv + dir * -0.5).xyz + texture2D(tDiffuse, vUv + dir * 0.5).xyz);
          
          float lumaB = dot(rgbB, luma);
          if ((lumaB < lumaMin) || (lumaB > lumaMax)) {
            gl_FragColor = vec4(rgbA, 1.0);
          } else {
            gl_FragColor = vec4(rgbB, 1.0);
          }
        }
      `;
      
      // Apply FXAA using WebGL shader
      let fxaaCanvas = tempCanvas;
      try {
        const fxaaCanvasEl = document.createElement('canvas');
        fxaaCanvasEl.width = renderW;
        fxaaCanvasEl.height = renderH;
        const fxaaRenderer = new THREE.WebGLRenderer({ canvas: fxaaCanvasEl, alpha: true, premultipliedAlpha: false, preserveDrawingBuffer: true });
        fxaaRenderer.setSize(renderW, renderH, false);
        fxaaRenderer.setClearColor(0x000000, 0);
        
        // Create FXAA shader material
        const fxaaMaterial = new THREE.ShaderMaterial({
          uniforms: {
            tDiffuse: { value: new THREE.CanvasTexture(tempCanvas) },
            resolution: { value: new THREE.Vector2(renderW, renderH) }
          },
          vertexShader: fxaaVertexShader,
          fragmentShader: fxaaFragmentShader,
          toneMapped: !vtoFlags.toneMapping2DDisabled,
        });
        
        // Create fullscreen quad for FXAA pass
        const fxaaQuad = new THREE.Mesh(
          new THREE.PlaneGeometry(2, 2),
          fxaaMaterial
        );
        const fxaaScene = new THREE.Scene();
        fxaaScene.add(fxaaQuad);
        const fxaaCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
        
        // Render FXAA pass
        fxaaRenderer.render(fxaaScene, fxaaCamera);
        const fgl = fxaaRenderer.getContext();
        if (fgl) {
          try {
            fgl.readPixels(0, 0, 1, 1, fgl.RGBA, fgl.UNSIGNED_BYTE, new Uint8Array(4));
          } catch (_) { /* ignore */ }
        }
        fxaaCanvas = fxaaCanvasEl;
        
        // Update texture for next step
        fxaaMaterial.uniforms.tDiffuse.value.dispose();
        fxaaMaterial.dispose();
        fxaaQuad.geometry.dispose();
        fxaaRenderer.dispose();
      } catch (e) {
        console.warn('FXAA shader failed, using original render:', e);
        // Fallback to original canvas if FXAA fails
      }
      
      // Downscale high-res render to final canvas with perfect smooth image rendering
      // Use multi-step downscaling for maximum quality
      const finalCtx = glCanvas.getContext('2d');
      if (finalCtx && fxaaCanvas) {
        finalCtx.imageSmoothingEnabled = true;
        finalCtx.imageSmoothingQuality = 'high';
        // Multi-step downscaling for maximum quality: 24x -> 12x -> 6x -> 3x -> 1x
        const step1W = w * 12;
        const step1H = h * 12;
        const step2W = w * 6;
        const step2H = h * 6;
        const step3W = w * 3;
        const step3H = h * 3;
        
        const step1Canvas = document.createElement('canvas');
        step1Canvas.width = step1W;
        step1Canvas.height = step1H;
        const step1Ctx = step1Canvas.getContext('2d');
        
        const step2Canvas = document.createElement('canvas');
        step2Canvas.width = step2W;
        step2Canvas.height = step2H;
        const step2Ctx = step2Canvas.getContext('2d');
        
        const step3Canvas = document.createElement('canvas');
        step3Canvas.width = step3W;
        step3Canvas.height = step3H;
        const step3Ctx = step3Canvas.getContext('2d');
        
        if (step1Ctx && step2Ctx && step3Ctx) {
          // Enable highest quality smoothing for all steps
          step1Ctx.imageSmoothingEnabled = true;
          step1Ctx.imageSmoothingQuality = 'high';
          step2Ctx.imageSmoothingEnabled = true;
          step2Ctx.imageSmoothingQuality = 'high';
          step3Ctx.imageSmoothingEnabled = true;
          step3Ctx.imageSmoothingQuality = 'high';
          finalCtx.imageSmoothingEnabled = true;
          finalCtx.imageSmoothingQuality = 'high';
          
          // Step 1: 24x -> 12x (using FXAA-processed canvas)
          step1Ctx.drawImage(fxaaCanvas, 0, 0, renderW, renderH, 0, 0, step1W, step1H);
          // Step 2: 12x -> 6x
          step2Ctx.drawImage(step1Canvas, 0, 0, step1W, step1H, 0, 0, step2W, step2H);
          // Step 3: 6x -> 3x
          step3Ctx.drawImage(step2Canvas, 0, 0, step2W, step2H, 0, 0, step3W, step3H);
          // Step 4: 3x -> 1x (final)
          finalCtx.drawImage(step3Canvas, 0, 0, step3W, step3H, 0, 0, w, h);
        } else {
          // Fallback: direct downscale
          finalCtx.drawImage(fxaaCanvas, 0, 0, renderW, renderH, 0, 0, w, h);
        }
      }
      
      out = document.createElement('canvas');
      out.width = w;
      out.height = h;
      const ctxE = out.getContext('2d');
      if (!ctxE) throw new Error('2d context missing');
      ctxE.imageSmoothingEnabled = true;
      ctxE.imageSmoothingQuality = 'high';
      // Draw base image (selfie), then glasses overlay. Use source-over so transparent
      // overlay pixels keep the base — avoids compare B ending up completely black.
      ctxE.drawImage(baseImg, 0, 0, w, h);
      ctxE.globalCompositeOperation = 'source-over';
      ctxE.drawImage(glCanvas, 0, 0, w, h);
      renderer.dispose();
      
      const ctx = out.getContext('2d');
      if (!ctx) throw new Error('2d context missing');
      if (!skipWatermark) {
        drawWatermark2D(ctx, w, h, { opacity: 0.6 });
      }

      const result = out.toDataURL('image/jpeg', 0.92);

      // Dispose all frame geometries and materials from frameGroup
      frameGroup.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
      frameTexture.dispose?.();
      handleTexture?.dispose?.();

      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createTempleArm, drawWatermark2D]
  );

  // Preview handlers: show stored screenshot (composite or base), do not re-apply current model
  const openSelfiePreview = useCallback(
    (selfie) => {
      if (!selfie) return;
      previewResumeVideoRef.current = false;
      setFrozenStageDataUrl(null);
      const dataUrl = selfie.compositeDataUrl || selfie.baseFrameDataUrl || null;
      setPreviewPrimary({ label: t('share'), onPrimary: null, disabled: false });
      setPreviewModal({ open: true, title: t('photo'), dataUrl, videoUrl: null });
    },
    []
  );

  const openClipPreview = useCallback(
    async (clip) => {
      if (!clip) return;
      previewResumeVideoRef.current = false;
      setFrozenStageDataUrl(null);

      // Helper to create share function for a blob
      const createShareFn = (videoBlob) => async () => {
        try {
          // Detect format from blob type for social media compatibility
          const isMP4 = videoBlob.type?.includes('mp4');
          const ext = isMP4 ? 'mp4' : 'webm';
          const mimeType = isMP4 ? 'video/mp4' : 'video/webm';
          const fileName = `mojenaocale-video.${ext}`;
          
          const file = new File([videoBlob], fileName, { type: mimeType });
          if (navigator?.share && navigator?.canShare?.({ files: [file] })) {
            try {
              // Some targets (Instagram/Messenger) reject title/text with file shares.
              await navigator.share({ files: [file] });
            } catch (shareErr) {
              await navigator.share({
                title: 'MojeNaocale.ba',
                text: 'Moj izgled sa naočalama',
                files: [file],
              });
            }
            return;
          }
        } catch (e) {
          console.log('Share failed, falling back to download:', e);
        }
        const ext = videoBlob.type?.includes('mp4') ? 'mp4' : 'webm';
        downloadBlob(videoBlob, `mojenaocale-video.${ext}`);
      };

      // New format (v2): video blob is already ready
      if (clip.v === 2 && clip.withGlassesBlob) {
        const url = URL.createObjectURL(clip.withGlassesBlob);
        previewVideoUrlRef.current = url;
        setPreviewPrimary({
          label: t('share'),
          onPrimary: createShareFn(clip.withGlassesBlob),
          disabled: false,
        });
        setPreviewModal({ open: true, title: t('video'), dataUrl: null, videoUrl: url });
        return;
      }

      // Legacy format (v1): no longer supported, show error
      console.warn('Legacy clip format not supported');
      setPreviewModal({ open: false, title: t('video'), dataUrl: null, videoUrl: null });
    },
    []
  );
  
  // Keep ref updated for use in stopLongPressRecording
  useEffect(() => {
    openClipPreviewRef.current = openClipPreview;
  }, [openClipPreview]);

  const activeClip = (videoClips || []).find((c) => c && c.id === activeClipId) || null;

  // Compare functionality (screenshots only)
  const doOpenCompare = useCallback((aId, bId) => {
    if (!aId || !bId) return;
    setCompareOpen(true);
    setCompareRenders({ a: null, b: null, combined: null, loading: true });
    
    const selfie = (selfies || []).find((s) => s && s.id === activeSelfieId) || (selfies || [])[0] || null;
    if (!selfie) {
      setCompareRenders({ a: null, b: null, combined: null, loading: false });
      return;
    }
    
    (async () => {
      try {
        const w = Math.round(selfie.stage?.w || 800);
        const h = Math.round(selfie.stage?.h || 600);

        // A = model on screenshot. Use stored composite (has handles) when available; else re-render.
        let aImg;
        if (selfie.compositeDataUrl) {
          aImg = selfie.compositeDataUrl;
        } else {
          aImg = await renderSelfieWithModel(selfie, aId, { skipWatermark: true });
        }

        // B: Use 2D render only (no 3D - avoids GPU memory issues)
        const bImg = await renderSelfieWithModel(selfie, bId, { skipWatermark: true, compareB: true });

        const loadImg = (src) =>
          new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
          });

        const [ia, ib] = await Promise.all([loadImg(aImg), loadImg(bImg)]);

        const combined = document.createElement('canvas');
        combined.width = w * 2;
        combined.height = h;
        const cctx = combined.getContext('2d');
        if (cctx) {
          cctx.drawImage(ia, 0, 0, w, h);
          cctx.drawImage(ib, w, 0, w, h);
          drawWatermark2D(cctx, w * 2, h, { opacity: 0.5, height: Math.round(h * 0.025) });
        }
        const combinedUrl = combined.toDataURL('image/jpeg', 0.92);

        setCompareRenders({ a: aImg, b: bImg, combined: combinedUrl, loading: false });
      } catch (e) {
        console.warn('Compare render failed:', e);
        setCompareRenders({ a: null, b: null, combined: null, loading: false });
      }
    })();
  }, [activeSelfieId, drawWatermark2D, renderSelfieWithModel, selfies]);

  const openComparePickerFromPreview = useCallback(() => {
    if (!activeSelfieId) return;
    // Only for screenshots, not videos
    if (previewModal?.videoUrl) return;

    // Enter compare-capture mode: user browses catalog in live view and takes screenshot B.
    closePreviewModal();
    setComparingWithSelfieId(activeSelfieId);
    showToast(t('comparePickOther'), 'info', 3000, 'top');
  }, [activeSelfieId, closePreviewModal, previewModal?.videoUrl, showToast, t]);

  // Restore temple visibility after closing compare (handles can disappear otherwise)
  const restoreTempleVisibility = useCallback(() => {
    if (!glassesGroup || currentAssetTypeRef.current !== '2d') return;
    const left = glassesGroup.getObjectByName('leftTemple');
    const right = glassesGroup.getObjectByName('rightTemple');
    if (left) left.visible = true;
    if (right) right.visible = true;
  }, [glassesGroup]);

  const closeCompareModal = useCallback(() => {
    setCompareOpen(false);
    restoreTempleVisibility();
  }, [restoreTempleVisibility]);

  const closeComparePicker = useCallback(() => {
    setComparePicker({ open: false, aId: null });
    restoreTempleVisibility();
  }, [restoreTempleVisibility]);

  // Responsive stage sizing.
  // Desktop: keep 4:3 sizing.
  // Mobile: DOM-driven sizing (measure the actual fullscreen stage/canvas after it mounts).
  useEffect(() => {
    // Mobile DOM-driven sizing
    if (isMobile && MOBILE_DYNAMIC_CANVAS) {
      const el = stageRef.current || cameraBoxRef.current;
      if (!el) return;
      const update = () => {
        const r = el.getBoundingClientRect?.();
        const w = Math.round(r?.width || el.clientWidth || 0);
        const h = Math.round(r?.height || el.clientHeight || 0);
        if (w > 0 && h > 0) setStageSize({ w, h });
      };
      update();
      const ro = new ResizeObserver(() => update());
      ro.observe(el);
      window.visualViewport?.addEventListener('resize', update);
      window.addEventListener('orientationchange', update);
      return () => {
        try { ro.disconnect(); } catch {}
        window.visualViewport?.removeEventListener('resize', update);
        window.removeEventListener('orientationchange', update);
      };
    }

    // Desktop sizing
    const computeStageSize = () => {
      const maxW = 800;
      const maxH = 600;

      const viewportW = window.innerWidth || maxW;
      const viewportH = window.visualViewport?.height || window.innerHeight || maxH;

      const aspect = 4 / 3;
      const headerH = headerRef.current?.getBoundingClientRect?.().height || 0;
      const padding = 16;
      const availableH = Math.max(240, viewportH - headerH - padding);

      let w = Math.min(maxW, viewportW);
      let h = w / aspect;
      if (h > availableH) {
        h = Math.min(maxH, availableH);
        w = h * aspect;
      }

      setStageSize({ w: Math.round(w), h: Math.round(h) });
    };

    computeStageSize();
    window.addEventListener('resize', computeStageSize);
    window.visualViewport?.addEventListener('resize', computeStageSize);
    window.addEventListener('orientationchange', computeStageSize);

    return () => {
      window.removeEventListener('resize', computeStageSize);
      window.visualViewport?.removeEventListener('resize', computeStageSize);
      window.removeEventListener('orientationchange', computeStageSize);
    };
  }, [isMobile]);

  // Mobile detection for layout (only used for sizing/visuals).
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const update = () => {
      const isSmallScreen = !!mq.matches;
      setIsMobile(isSmallScreen);
    };
    update();

    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);

  // Robust mobile viewport height (handles iOS Safari address-bar resizing better than plain 100vh).
  useEffect(() => {
    const setVhVar = () => {
      const h = window.visualViewport?.height || window.innerHeight || 0;
      if (h > 0) document.documentElement.style.setProperty('--app-vh', `${h * 0.01}px`);
    };

    setVhVar();
    window.addEventListener('resize', setVhVar);
    window.visualViewport?.addEventListener('resize', setVhVar);
    window.visualViewport?.addEventListener('scroll', setVhVar);

    return () => {
      window.removeEventListener('resize', setVhVar);
      window.visualViewport?.removeEventListener('resize', setVhVar);
      window.visualViewport?.removeEventListener('scroll', setVhVar);
    };
  }, []);

  // Prevent page scroll behind the full-screen stage on mobile.
  useEffect(() => {
    if (!isMobile) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isMobile]);

  // Keep renderer + overlay canvases aligned with the stage size (important on mobile address-bar/orientation changes).
  // On mobile, multiply DPR by CSS scale factor so canvas renders at final display resolution (crisp, no blur).
  useEffect(() => {
    // DOM-first: size renderer/canvases against the actual mounted stage box.
    const el = cameraBoxRef.current || stageRef.current;
    if (!el) return;

    const applySize = () => {
      // Use actual element size; we do not apply any CSS "zoom" transforms on mobile.
      const w = Math.round(el.clientWidth || stageSize.w || 800);
      const h = Math.round(el.clientHeight || stageSize.h || 600);
      const dpr = getEffectiveDpr();

      if (rendererRef.current && cameraRef.current) {
        rendererRef.current.setPixelRatio(dpr);
        rendererRef.current.setSize(w, h, false);
        cameraRef.current.aspect = w / h;
        cameraRef.current.updateProjectionMatrix();
      }

      const scan = scanCanvasRef.current;
      if (scan) {
        // Scan overlay is purely cosmetic; keep it cheaper on mobile to avoid perceived tracker lag.
        const scanDpr = isMobile ? Math.min(1.0, dpr) : dpr;
        const bw = Math.round(w * scanDpr);
        const bh = Math.round(h * scanDpr);
        if (scan.width !== bw || scan.height !== bh) {
          scan.width = bw;
          scan.height = bh;
        }
        const ctx = scan.getContext('2d');
        if (ctx) ctx.setTransform(scanDpr, 0, 0, scanDpr, 0, 0);
      }
    };

    applySize();
    const ro = new ResizeObserver(() => applySize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [stageSize.w, stageSize.h, getEffectiveDpr]);

  useEffect(() => {
    let timeoutId = null;
    let cancelled = false;

    // Cap tracking/inference rate to reduce CPU/GPU load.
    // Rendering is handled independently by Three.js' `renderer.setAnimationLoop()`.
    // Goal: track ~30fps, render up to display refresh (often 60fps).
    const isMobileDeviceForRate = getIsMobileDevice();
    const isLowEndForRate = isMobileDeviceForRate && getIsLowEndMobile();
    // Default cadence: stable/efficient. Ultra-low-latency can run faster (esp. desktop).
    // Low-end: reduce baseline a bit to keep UI responsive under load.
    const baseTrackFps = isLowEndForRate ? 20 : 30;
    const ultraTrackFps = vtoFlags.ultraLowLatency
      ? (isMobileDeviceForRate ? (isLowEndForRate ? 24 : 45) : 60)
      : baseTrackFps;
    // Proportional reduction knob (also useful on high-end when you want smoother UI).
    const fastMul = vtoFlags.fastTracking ? 0.8 : 1.0;
    const targetTrackFps = Math.max(12, Math.round(ultraTrackFps * fastMul));
    const targetTrackIntervalMs = 1000 / Math.max(1, targetTrackFps);

    const scheduleNext = (delayMs) => {
      if (cancelled) return;
      try {
        if (timeoutId != null) window.clearTimeout(timeoutId);
      } catch {
        // ignore
      }
      const d = Math.max(0, Number(delayMs) || 0);
      timeoutId = window.setTimeout(() => {
        // Avoid running after cleanup.
        if (cancelled) return;
        detectAndPositionGlasses();
      }, d);
    };

    const detectAndPositionGlasses = async () => {
      const loopStart = performance.now();
      if (!webcamRef.current || !model || (!glassesGroup && !noWebglFallbackRef.current)) {
        // Not ready yet; poll cheaply.
        scheduleNext(60);
        return;
      }
      
      const video = webcamRef.current.video;
      if (!video || video.readyState !== 4) {
        // Camera not ready; poll cheaply.
        scheduleNext(60);
        return;
      }

      try {
        // Iris prediction improves IPD sizing accuracy.
        // Enable on desktop; on mobile enable only when not low-end and not ultra-low-latency (perf).
        const isMobileDevice = getIsMobileDevice();
        const useIrisPrediction = !vtoFlags.ultraLowLatency && (!isMobileDevice || !getIsLowEndMobile());

        // On mobile (and also in Chrome device emulation), make the tracker see the SAME crop/aspect
        // as the user sees on screen by feeding it a fixed-size, cover-cropped offscreen canvas.
        // This avoids coordinate-space mismatches (the most common cause of persistent offsets).
        let inferenceInput = video;
        let inferenceW = video.videoWidth;
        let inferenceH = video.videoHeight;
        try {
          const stageEl = cameraBoxRef.current || stageRef.current;
          const rect = stageEl?.getBoundingClientRect?.();
          const viewW = rect?.width || stageEl?.clientWidth || 0;
          const viewH = rect?.height || stageEl?.clientHeight || 0;
          const vW = video.videoWidth || 0;
          const vH = video.videoHeight || 0;
          const viewAspect = viewH > 0 ? (viewW / viewH) : 1;
          const vidAspect = vH > 0 ? (vW / vH) : 1;
          const aspectMismatch = Math.abs(Math.log(Math.max(1e-6, viewAspect) / Math.max(1e-6, vidAspect))) > 0.12;
          const forceDownscale = vtoFlags.ultraLowLatency || vtoFlags.fastTracking;
          if ((getIsMobileDevice() || aspectMismatch || forceDownscale) && viewW > 0 && viewH > 0 && vW > 0 && vH > 0) {
            const tc = trackingCanvasRef.current || (trackingCanvasRef.current = document.createElement('canvas'));
            const isMobileD = getIsMobileDevice();
            const isLowEndD = isMobileD && getIsLowEndMobile();
            let LONG = 640;
            const lastInferMs = Number(trackerStateRef.current?.lastInferenceMs) || 0;
            // If inference is running "over budget", adaptively downscale input to keep latency low.
            // Budget is derived from the current tracking tick interval (with a small margin).
            const budgetMs = Math.max(10, targetTrackIntervalMs * (vtoFlags.ultraLowLatency ? 0.70 : 0.85));
            let perfMul = 1.0;
            // High-end mobile: react a bit sooner to keep latency low.
            const isHighEndMobile = isMobileD && !isLowEndD;
            if (isHighEndMobile) {
              if (lastInferMs > budgetMs * 1.8) perfMul = 0.65;
              else if (lastInferMs > budgetMs * 1.4) perfMul = 0.75;
              else if (lastInferMs > budgetMs * 1.1) perfMul = 0.85;
            } else {
              if (lastInferMs > budgetMs * 2.0) perfMul = 0.65;
              else if (lastInferMs > budgetMs * 1.5) perfMul = 0.75;
              else if (lastInferMs > budgetMs * 1.2) perfMul = 0.85;
            }

            const round16 = (v) => Math.round(v / 16) * 16;
            // Smaller inference input => faster model => lower latency (tradeoff: slightly less precision).
            // NOTE: on desktop we only do this when forced (ultraLowLatency / fastTracking) or when aspect mismatches.
            if (isMobileD) {
              // Mobile defaults smaller because CPU inference is common there.
              // Low-end stays reduced for performance.
              // High-end: slightly reduced tracking input resolution for speed.
              // (We still keep adaptive scaling via `perfMul` if inference is over budget.)
              LONG = isLowEndD ? 192 : 240;
              // Ultra-low-latency on high-end: drop a bit more to keep infer under budget consistently.
              if (vtoFlags.ultraLowLatency) LONG = isLowEndD ? 176 : 208;
              if (vtoFlags.fastTracking) LONG = isLowEndD ? 176 : 208;
            } else if (forceDownscale) {
              // Desktop: keep decent precision, but reduce size enough to cut inference time.
              LONG = vtoFlags.ultraLowLatency ? 384 : 512;
              if (vtoFlags.fastTracking && !vtoFlags.ultraLowLatency) LONG = 448;
            }

            // Apply adaptive performance scaling + clamp to safe device bounds.
            LONG = round16(LONG * perfMul);
            if (isMobileD) {
              const minL = isLowEndD ? 144 : 176;
              const maxL = isLowEndD ? 224 : 256;
              LONG = Math.max(minL, Math.min(maxL, LONG));
            } else {
              const minL = 320;
              const maxL = forceDownscale ? 512 : 640;
              LONG = Math.max(minL, Math.min(maxL, LONG));
            }
            // Expose tuning data for debug overlay.
            trackerStateRef.current.trackingBudgetMs = budgetMs;
            trackerStateRef.current.trackingPerfMul = perfMul;
            trackerStateRef.current.trackingLong = LONG;
            let tw, th;
            if (viewW >= viewH) {
              tw = LONG;
              th = Math.round(LONG * (viewH / viewW));
            } else {
              th = LONG;
              tw = Math.round(LONG * (viewW / viewH));
            }
            // IMPORTANT: keep aspect ratio EXACT (do not clamp dimensions independently),
            // otherwise the mapping back to the fullscreen `cover` view becomes wrong and sizing drifts.
            // Ensure a minimum short side only if it doesn't explode the long side (perf guard).
            // Mobile: allow smaller short-side minimum so portrait viewports don't up-scale
            // our intended low-res tracking input (which would undo the perf win).
            // High-end: don't force a big min short-side when we're optimizing for speed,
            // otherwise portrait viewports can upscale the tracking input and erase the perf win.
            const minShort = isLowEndD ? 112 : ((vtoFlags.ultraLowLatency || vtoFlags.fastTracking) ? 112 : 128);
            const maxLong = isLowEndD ? 256 : 384;
            const short = Math.max(1, Math.min(tw, th));
            if (short < minShort) {
              const sMin = minShort / short;
              const tw2 = Math.round(tw * sMin);
              const th2 = Math.round(th * sMin);
              const long2 = Math.max(tw2, th2);
              if (long2 <= maxLong) {
                tw = tw2;
                th = th2;
              }
            }
            if (tc.width !== tw) tc.width = tw;
            if (tc.height !== th) tc.height = th;
            let ctx = null;
            try {
              // Safari/iOS can be picky about context attributes; keep this conservative.
              ctx = tc.getContext('2d', { willReadFrequently: true });
            } catch {
              ctx = null;
            }
            if (!ctx) {
              try {
                ctx = tc.getContext('2d');
              } catch {
                ctx = null;
              }
            }
            if (ctx) {
              ctx.setTransform(1, 0, 0, 1, 0, 0);
              ctx.clearRect(0, 0, tw, th);
              // Match on-screen presentation for correct sizing/overlay.
              const fit = 'cover';
              const s = fit === 'contain' ? Math.min(tw / vW, th / vH) : Math.max(tw / vW, th / vH);
              const dw = vW * s;
              const dh = vH * s;
              const dx = (tw - dw) / 2;
              const dy = (th - dh) / 2;
              ctx.drawImage(video, dx, dy, dw, dh);
              inferenceInput = tc;
              inferenceW = tw;
              inferenceH = th;
            }
          }
        } catch {
          // ignore tracking-canvas failures; fallback to direct video input
        }
        const inferStart = performance.now();
        // Use estimateFaces with optimized settings for stability
        const faceEstimates = await model.estimateFaces({ 
          input: inferenceInput, 
          predictIrises: useIrisPrediction,
          // Note: minTrackingConfidence and refineLandmarks may not be supported in this API version
          // Temporal smoothing is handled separately below
        });
        const inferMs = performance.now() - inferStart;
        trackerStateRef.current.lastInferenceMs = inferMs;
        // Store whether we have valid iris landmarks for IPD calculation.
        // (Tasks: 478 landmarks; TFJS: 468 unless predictIrises:true).
        const firstMeshLen = faceEstimates?.[0]?.scaledMesh?.length || 0;
        trackerStateRef.current.hasIrisData = !!(useIrisPrediction && firstMeshLen >= 478);
        
        if (faceEstimates.length > 0) {
          trackerMissRef.current = 0;
          setIsLoading(false);
          // Show the glasses now that we have a valid face (hides loading flash)
          if (glassesGroup && !glassesGroup.visible) {
            glassesGroup.visible = true;
          }
          const keypoints = faceEstimates[0].scaledMesh;
          
          lastKeypointsRef.current = keypoints;
          // Store dims of the coordinate space keypoints live in (video OR tracking canvas).
          lastVideoDimsRef.current = { w: inferenceW || video.videoWidth, h: inferenceH || video.videoHeight };
          // Reset boot fallback timer once we have a face.
          trackerBootRef.current.t0 = performance.now();
          trackerBootRef.current.fallbackTried = false;

          if (noWebglFallbackRef.current) {
            const stageEl = cameraBoxRef.current || stageRef.current;
            const rect = stageEl?.getBoundingClientRect?.();
            const viewW = rect?.width || stageEl?.clientWidth || canvasRef.current?.clientWidth || 800;
            const viewH = rect?.height || stageEl?.clientHeight || canvasRef.current?.clientHeight || 600;
            const vd = lastVideoDimsRef.current || { w: video.videoWidth, h: video.videoHeight };
            const vW = vd?.w || video.videoWidth;
            const vH = vd?.h || video.videoHeight;

            drawNoWebglFallback({
              keypoints,
              videoW: vW,
              videoH: vH,
              viewW,
              viewH,
              isMirrored: true,
              viewFit: 'cover',
            });

            const elapsed = performance.now() - loopStart;
            const over = elapsed - targetTrackIntervalMs;
            const isHighEndMobileForSchedule = getIsMobileDevice() && !getIsLowEndMobile();
            const delayMs = isHighEndMobileForSchedule
              ? Math.max(0, targetTrackIntervalMs - elapsed)
              : (over > 0 ? Math.min(60, 4 + over * 0.6) : Math.max(0, targetTrackIntervalMs - elapsed));
            scheduleNext(delayMs);
            return;
          }

          // Face shape (5-type) - decide once at start, then lock.
          if (!faceShapeLockedRef.current) {
            // IMPORTANT: keypoints are in the inference input coordinate space (video OR tracking canvas).
            const shapeResult = estimateFaceShape4(keypoints, inferenceW || video.videoWidth, inferenceH || video.videoHeight);
            // Skip low-quality samples (e.g., strong yaw/roll) so we don't lock the wrong shape early.
            if (shapeResult?.valid === false) {
              // keep waiting for a better pose
            } else {
              const w = faceShapeWindowRef.current;
              w.push(shapeResult);
              if (w.length > FACE_SHAPE_LOCK.window) w.shift();

              if (w.length >= FACE_SHAPE_LOCK.minFrames) {
              // Weighted vote by confidence
              const weights = w.reduce((acc, r) => {
                const s = r.shape;
                const c = r.confidence ?? 0;
                acc[s] = (acc[s] || 0) + (0.4 + 0.6 * c);
                return acc;
              }, {});
              const best = Object.entries(weights).sort((a, b) => b[1] - a[1])[0]?.[0] || null;

              // Dominance ratio in the window (unweighted, for stability)
              const counts = w.reduce((acc, r) => {
                acc[r.shape] = (acc[r.shape] || 0) + 1;
                return acc;
              }, {});
              const winRatio = best ? (counts[best] || 0) / w.length : 0;

              // Avg confidence for the winning class
              const bestConfs = best ? w.filter((r) => r.shape === best).map((r) => r.confidence ?? 0) : [];
              const avgConf =
                bestConfs.length > 0 ? bestConfs.reduce((a, b) => a + b, 0) / bestConfs.length : 0;

              setFaceShapeConfidence(avgConf);

              if (best && winRatio >= FACE_SHAPE_LOCK.minWinRatio && avgConf >= FACE_SHAPE_LOCK.minAvgConf) {
                setFaceShape(best);
                faceShapeLockedRef.current = true;
                setFaceShapeLocked(true);
                // stop collecting once locked
                faceShapeWindowRef.current = [];
              } else if (!faceShape) {
                // show current best while still analyzing (optional)
                if (best) setFaceShape(best);
              }
            }
            }
          }

          // Get key eye landmark for positioning (matching original e9b0b44 commit)
          const eyeCenter = keypoints[168]; // Bridge of nose between eyes

          // Map to displayed view coordinates (mobile can use `contain` to reduce "zoomed in" crop).
          const stageEl = cameraBoxRef.current || stageRef.current;
          const rect = stageEl?.getBoundingClientRect?.();
          const viewW = rect?.width || stageEl?.clientWidth || canvasRef.current?.clientWidth || 800;
          const viewH = rect?.height || stageEl?.clientHeight || canvasRef.current?.clientHeight || 600;
          const vd = lastVideoDimsRef.current || { w: video.videoWidth, h: video.videoHeight };
          const videoW = vd?.w || video.videoWidth;
          const videoH = vd?.h || video.videoHeight;
          const isMirrored = true;
          // Must match the live webcam presentation for correct sizing/overlay.
          const viewFit = 'cover';

          // ============================================
          // ENVIRONMENT MATCHING (real feel)
          // ============================================
          // Adjust exposure + light intensity/color from the live camera feed.
          // This makes PBR highlights and overall blend feel less "cut-out".
          try {
            const now = performance.now();
            const last = lastEnvEstimateRef.current || { t: 0, exp: 1.0, rgb: [1, 1, 1] };
            if (now - (last.t || 0) > 120) {
              if (!envSampleCanvasRef.current) {
                envSampleCanvasRef.current = document.createElement('canvas');
              }
              const c = envSampleCanvasRef.current;
              const cw = 48;
              const ch = 48;
              if (c.width !== cw) c.width = cw;
              if (c.height !== ch) c.height = ch;
              const ctx = c.getContext('2d', { willReadFrequently: true });
              if (ctx) {
                // "Cover" crop into square (matches webcam objectFit: cover-ish)
                const scale = Math.max(cw / videoW, ch / videoH);
                const dw = videoW * scale;
                const dh = videoH * scale;
                const dx = (cw - dw) / 2;
                const dy = (ch - dh) / 2;
                ctx.clearRect(0, 0, cw, ch);
                ctx.drawImage(video, dx, dy, dw, dh);
                const id = ctx.getImageData(0, 0, cw, ch);
                const d = id.data;
                let r = 0, g = 0, b = 0;
                let lSum = 0;
                let lSqSum = 0;
                for (let i = 0; i < d.length; i += 4) {
                  const r8 = d[i];
                  const g8 = d[i + 1];
                  const b8 = d[i + 2];
                  r += r8;
                  g += g8;
                  b += b8;
                  const rr0 = r8 / 255;
                  const gg0 = g8 / 255;
                  const bb0 = b8 / 255;
                  const l0 = 0.2126 * rr0 + 0.7152 * gg0 + 0.0722 * bb0;
                  lSum += l0;
                  lSqSum += l0 * l0;
                }
                const npx = d.length / 4;
                const rr = (r / npx) / 255;
                const gg = (g / npx) / 255;
                const bb = (b / npx) / 255;
                const luma = npx > 0 ? (lSum / npx) : (0.2126 * rr + 0.7152 * gg + 0.0722 * bb);
                const lumaVar = npx > 0 ? Math.max(0, (lSqSum / npx) - luma * luma) : 0;

                // Keep exposure fixed for color fidelity (avoid frame-to-frame color drift).
                const exp = 1.0;

                // Smooth color (avoid flicker)
                const rgb = [
                  last.rgb[0] * 0.9 + rr * 0.1,
                  last.rgb[1] * 0.9 + gg * 0.1,
                  last.rgb[2] * 0.9 + bb * 0.1,
                ];

                lastEnvEstimateRef.current = { t: now, exp, rgb };

                // Apply to renderer + lights
                if (rendererRef.current) rendererRef.current.toneMappingExposure = exp;
                const amb = ambientLightRef.current;
                if (amb) {
                  amb.color.setRGB(1, 1, 1);
                  amb.intensity = 0.6;
                }
                const key = keyLightRef.current;
                if (key) {
                  key.intensity = 0.8;
                }
                const fill = fillLightRef.current;
                if (fill) {
                  fill.intensity = 0.3;
                }

                // Camera artifact matching hook-point (future: grain/blur/sharpen/CA).
                // For now we just store lightweight stats so we can drive effects later.
                cameraMatchRef.current = { t: now, luma, lumaVar, rgb, exp };

                // Optional low-rate dynamic environment map (best-effort; guarded for perf).
                if (vtoFlags.reflectionsEnabled && !vtoFlags.ultraLowLatency && !getIsLowEndMobile() && pmremRef.current && sceneRef.current) {
                  const lastEnvT = (lastEnvEstimateRef.current?.envT ?? 0);
                  if (now - lastEnvT > 1200) {
                    // Build a low-res blurred equirect-like texture from the camera feed.
                    const ew = 64;
                    const eh = 32; // 2:1
                    const ec = document.createElement('canvas');
                    ec.width = ew;
                    ec.height = eh;
                    const ectx = ec.getContext('2d');
                    if (ectx) {
                      ectx.filter = 'blur(6px)';
                      const s2 = Math.max(ew / videoW, eh / videoH);
                      const ddw = videoW * s2;
                      const ddh = videoH * s2;
                      const ddx = (ew - ddw) / 2;
                      const ddy = (eh - ddh) / 2;
                      ectx.drawImage(video, ddx, ddy, ddw, ddh);
                      ectx.filter = 'none';
                      const tex = new THREE.CanvasTexture(ec);
                      tex.colorSpace = THREE.SRGBColorSpace;
                      tex.mapping = THREE.EquirectangularReflectionMapping;
                      tex.needsUpdate = true;
                      try {
                        const pmrem = pmremRef.current;
                        const rt = pmrem.fromEquirectangular(tex);
                        const prev = dynamicEnvRTRef.current;
                        if (prev && prev.dispose) prev.dispose();
                        dynamicEnvRTRef.current = rt;
                        sceneRef.current.environment = rt.texture;
                        envMapRef.current = rt.texture;
                      } catch {
                        // ignore
                      } finally {
                        tex.dispose?.();
                      }
                      // store env update timestamp
                      lastEnvEstimateRef.current = { ...lastEnvEstimateRef.current, envT: now };
                    }
                  }
                }
              }
            }
          } catch {
            // ignore env matching errors
          }

          const eyeCenterV = mapVideoPointToView(eyeCenter, videoW, videoH, viewW, viewH, isMirrored, viewFit);
          const noseBridge = keypoints[6];
          const noseBridgeV = mapVideoPointToView(noseBridge, videoW, videoH, viewW, viewH, isMirrored, viewFit);

          const anchorV = [
            eyeCenterV[0] * (1 - NOSE_ANCHOR_BLEND) + noseBridgeV[0] * NOSE_ANCHOR_BLEND,
            eyeCenterV[1] * (1 - NOSE_ANCHOR_BLEND) + noseBridgeV[1] * NOSE_ANCHOR_BLEND,
          ];
          
          // ============================================
          // IPD-BASED ACCURATE SIZING
          // ============================================
          // Use interpupillary distance (IPD) as a known reference to calculate
          // real-world mm measurements dynamically.
          
          // Get IPD from iris landmarks (468 = left iris center, 473 = right iris center)
          // Falls back to inner eye corners if iris prediction is disabled
          let ipdPixels;
          const hasValidIris = trackerStateRef.current.hasIrisData;
          
          if (hasValidIris) {
            // Use iris landmarks for accurate IPD (only when iris prediction is enabled)
            const leftIris = keypoints[468];
            const rightIris = keypoints[473];
            const leftIrisV = mapVideoPointToView(leftIris, videoW, videoH, viewW, viewH, isMirrored, viewFit);
            const rightIrisV = mapVideoPointToView(rightIris, videoW, videoH, viewW, viewH, isMirrored, viewFit);
            ipdPixels = Math.hypot(rightIrisV[0] - leftIrisV[0], rightIrisV[1] - leftIrisV[1]);
          } else {
            // Fallback: use INNER eye corners (133 = left inner, 362 = right inner)
            // Inner eye distance (~32mm) to IPD (~63mm) ratio is ~1.97
            const leftInnerEye = keypoints[133];
            const rightInnerEye = keypoints[362];
            const leftInnerV = mapVideoPointToView(leftInnerEye, videoW, videoH, viewW, viewH, isMirrored, viewFit);
            const rightInnerV = mapVideoPointToView(rightInnerEye, videoW, videoH, viewW, viewH, isMirrored, viewFit);
            const innerEyePixels = Math.hypot(rightInnerV[0] - leftInnerV[0], rightInnerV[1] - leftInnerV[1]);
            ipdPixels = innerEyePixels * 1.97;
          }

          // Face rotation (used for yaw compensation + calibration gating + PD quality gating)
          const faceRotation = calculateFaceRotation(keypoints);
          const yawMagnitude = Math.abs(faceRotation.yaw);

          // Face-width calibration (bizygomatic width) using iris diameter as a ruler.
          // Calibrate only when looking straight to avoid foreshortening.
          try {
            if (hasValidIris) {
              const irisLeftIdx = [468, 469, 470, 471, 472];
              const irisRightIdx = [473, 474, 475, 476, 477];
              const toView = (idx) => {
                const kp = keypoints[idx];
                return kp ? mapVideoPointToView(kp, videoW, videoH, viewW, viewH, isMirrored, viewFit) : null;
              };
              const maxPairwiseDist = (pts) => {
                let best = 0;
                for (let i = 0; i < pts.length; i++) {
                  for (let j = i + 1; j < pts.length; j++) {
                    const dx = pts[i][0] - pts[j][0];
                    const dy = pts[i][1] - pts[j][1];
                    const d = Math.hypot(dx, dy);
                    if (d > best) best = d;
                  }
                }
                return best;
              };

              const leftPts = irisLeftIdx.map(toView).filter(Boolean);
              const rightPts = irisRightIdx.map(toView).filter(Boolean);
              const leftDiaPx = leftPts.length >= 2 ? maxPairwiseDist(leftPts) : 0;
              const rightDiaPx = rightPts.length >= 2 ? maxPairwiseDist(rightPts) : 0;
              const irisDiaPx = (leftDiaPx > 0 && rightDiaPx > 0) ? (leftDiaPx + rightDiaPx) / 2 : (leftDiaPx || rightDiaPx);

              const leftCheek = keypoints[234];
              const rightCheek = keypoints[454];
              const leftCheekV = leftCheek ? mapVideoPointToView(leftCheek, videoW, videoH, viewW, viewH, isMirrored, viewFit) : null;
              const rightCheekV = rightCheek ? mapVideoPointToView(rightCheek, videoW, videoH, viewW, viewH, isMirrored, viewFit) : null;
              const faceWidthPx = (leftCheekV && rightCheekV)
                ? Math.hypot(rightCheekV[0] - leftCheekV[0], rightCheekV[1] - leftCheekV[1])
                : 0;

              const yawAbs = Math.abs(faceRotation.yaw || 0);
              const rollAbs = Math.abs(faceRotation.roll || 0);
              const pitchAbs = Math.abs(faceRotation.pitch || 0);
              const straightEnough =
                yawAbs <= FACE_WIDTH_CALIB.yawMaxRad &&
                rollAbs <= FACE_WIDTH_CALIB.rollMaxRad &&
                pitchAbs <= FACE_WIDTH_CALIB.pitchMaxRad;

              if (straightEnough && irisDiaPx > 2 && faceWidthPx > 20) {
                const ratio = faceWidthPx / irisDiaPx; // unitless
                const calib = faceWidthCalibRef.current;
                calib.samples.push(ratio);
                if (calib.samples.length > FACE_WIDTH_CALIB.windowSize) calib.samples.shift();

                if (!calib.locked && calib.samples.length >= FACE_WIDTH_CALIB.minSamples) {
                  const sorted = [...calib.samples].sort((a, b) => a - b);
                  const mid = Math.floor(sorted.length / 2);
                  const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
                  const faceWidthMm = median * IRIS_DIAMETER_MM;
                  calib.ratio = median;
                  calib.faceWidthMm = faceWidthMm;
                  calib.locked = true;
                  setCalibratedFaceWidthMm((prev) => {
                    if (prev == null) return Math.round(faceWidthMm);
                    return Math.abs(prev - faceWidthMm) >= 1.5 ? Math.round(faceWidthMm) : prev;
                  });
                }
              }
            }
          } catch {
            // ignore calibration errors
          }
          
          // Calculate pixels-per-mm for GLASSES SIZING.
          // Prefer IPD-based sizing (iris when available, otherwise inner-eye fallback).
          let pixelsPerMm = ipdPixels / AVERAGE_IPD_MM;
          // Kept for debug snapshot (avoid unused warnings when debug is off)
          let sizingMethod = hasValidIris ? 'ipd_iris' : 'ipd_fallback';
          let faceWidthPixels = null;

          // Mobile fallback (only if IPD-based fails): cheek-to-cheek width in VIEW space.
          // NOTE: we do NOT default to this anymore because it can bias sizing under `cover` crops.
          if (getIsMobileDevice() && (!Number.isFinite(pixelsPerMm) || pixelsPerMm <= 0)) {
            const leftCheek = keypoints[234];
            const rightCheek = keypoints[454];
            if (leftCheek && rightCheek) {
              const leftCheekV = mapVideoPointToView(leftCheek, videoW, videoH, viewW, viewH, isMirrored, viewFit);
              const rightCheekV = mapVideoPointToView(rightCheek, videoW, videoH, viewW, viewH, isMirrored, viewFit);
              const d = Math.hypot(rightCheekV[0] - leftCheekV[0], rightCheekV[1] - leftCheekV[1]);
              if (Number.isFinite(d) && d > 10) {
                faceWidthPixels = d;
                const FACE_WIDTH_REF_MM = 140;
                pixelsPerMm = d / FACE_WIDTH_REF_MM;
                sizingMethod = 'face_width';
              }
            }
          }
          
          // PD estimate (display only): iris-diameter calibration (rough) + uncertainty/quality gating.
          // Goal: NEVER show a confident-looking wrong number.
          let displayPD = null; // raw (float mm) for debug
          let pdStdDevMm = null;
          let pdConf = 0;

          if (!pdLockedRef.current) {
            if (!vtoFlags.pdEstimateWithUncertainty) {
              // Rollout flag: keep PD hidden
              if (pdConfidence !== 0) setPdConfidence(0);
              if (measuredPDRef.current != null) {
                measuredPDRef.current = null;
                setMeasuredPD(null);
              }
              if (pdUncertainty != null) setPdUncertainty(null);
            } else
            if (hasValidIris && yawMagnitude <= PD_ESTIMATE.maxYawRad) {
              try {
                const irisLeftIdx = [468, 469, 470, 471, 472];
                const irisRightIdx = [473, 474, 475, 476, 477];

                const toView = (idx) => {
                  const kp = keypoints[idx];
                  return kp ? mapVideoPointToView(kp, videoW, videoH, viewW, viewH, isMirrored, viewFit) : null;
                };

                const maxPairwiseDist = (pts) => {
                  let best = 0;
                  for (let i = 0; i < pts.length; i++) {
                    for (let j = i + 1; j < pts.length; j++) {
                      const dx = pts[i][0] - pts[j][0];
                      const dy = pts[i][1] - pts[j][1];
                      const d = Math.hypot(dx, dy);
                      if (d > best) best = d;
                    }
                  }
                  return best;
                };

                const leftPts = irisLeftIdx.map(toView).filter(Boolean);
                const rightPts = irisRightIdx.map(toView).filter(Boolean);
                const leftDiaPx = leftPts.length >= 2 ? maxPairwiseDist(leftPts) : 0;
                const rightDiaPx = rightPts.length >= 2 ? maxPairwiseDist(rightPts) : 0;
                const irisDiaPx = (leftDiaPx > 0 && rightDiaPx > 0) ? (leftDiaPx + rightDiaPx) / 2 : (leftDiaPx || rightDiaPx);

                if (irisDiaPx > 1) {
                  const mmPerPx = IRIS_DIAMETER_MM / irisDiaPx;
                  const pdMm = ipdPixels * mmPerPx;
                  if (Number.isFinite(pdMm) && pdMm > 45 && pdMm < 80) {
                    displayPD = pdMm;
                    pdSamplesRef.current.push(pdMm);
                    if (pdSamplesRef.current.length > PD_ESTIMATE.windowSize) pdSamplesRef.current.shift();

                    if (pdSamplesRef.current.length >= 2) {
                      const n = pdSamplesRef.current.length;
                      const mean = pdSamplesRef.current.reduce((a, b) => a + b, 0) / n;
                      const variance = pdSamplesRef.current.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) / n;
                      const std = Math.sqrt(Math.max(0, variance));
                      pdStdDevMm = std;
                      const uncertainty = Math.max(1, Math.round(std * 2)); // ≈95% range under normality

                      const stability = Math.max(0, Math.min(1, 1 - (std / PD_ESTIMATE.maxStdDevMm)));
                      const sampleScore = Math.max(0, Math.min(1, n / PD_ESTIMATE.minSamples));
                      const yawScore = Math.max(0, Math.min(1, 1 - (yawMagnitude / PD_ESTIMATE.maxYawRad)));
                      pdConf = Math.max(0, Math.min(1, stability * sampleScore * yawScore));
                      setPdConfidence(pdConf);

                      const shouldShow =
                        n >= PD_ESTIMATE.minSamples &&
                        pdConf >= PD_ESTIMATE.minConfidenceToShow &&
                        uncertainty <= PD_ESTIMATE.maxUncertaintyToShow;

                      if (shouldShow) {
                        const rounded = Math.round(mean);
                        measuredPDRef.current = rounded;
                        setMeasuredPD(rounded);
                        setPdUncertainty(uncertainty);
                        pdLockedRef.current = true; // keep stable once qualified
                        pdSamplesRef.current = [];
                      } else {
                        // Keep hidden until stable enough
                        setMeasuredPD(null);
                        setPdUncertainty(null);
                      }
                    } else {
                      setPdConfidence(0);
                      setMeasuredPD(null);
                      setPdUncertainty(null);
                    }
                  }
                }
              } catch {
                // ignore PD estimate errors
              }
            } else {
              // Not eligible (no iris or too much yaw)
              setPdConfidence(0);
              setMeasuredPD(null);
              setPdUncertainty(null);
            }
          }
          
          // Get frame width in mm from product settings
          const frameWidthMm = currentFrameWidthRef.current || 140;
          // Correct for transparent padding in the front frame PNG (alpha-bounds).
          // If visible content is only e.g. 0.85 of the texture width, scale up by 1/0.85.
          const frontContentScaleX = vtoFlags.assetContentScaleCorrection ? (currentFrontContentScaleXRef.current || 1) : 1;
          const frontContentScaleXClamped = Math.max(0.25, Math.min(1, frontContentScaleX));
          const manualScaleCorrection = vtoFlags.assetContentScaleCorrection ? (currentScaleCorrectionRef.current || 1) : 1;
          
          // Calculate how many pixels the glasses frame should span
          const targetGlassesPixels = frameWidthMm * pixelsPerMm;
          
          // When the head turns (yaw), 2D measurements shrink due to foreshortening.
          // Compensate so glasses don't "shrink" as you rotate.
          const yawCos = Math.cos(Math.min(yawMagnitude, 1.2)); // limit extreme angles
          const yawComp = 1 / Math.max(0.55, yawCos); // clamp to avoid blow-ups
          
          // Calculate target position
          // Move glasses closer to face when turning (higher = tighter fit, especially on mobile)
          const zOffset = yawMagnitude * 1.2;
          
          // On mobile, bring glasses closer to face for better fit
          const mobileZAdjust = getIsMobileDevice() ? MOBILE_GLASSES_Z_OFFSET : 0;
          const baseZ = BASE_GLASSES_Z - GLASSES_FACE_GLUE_Z_BIAS;
          const targetZ = THREE.MathUtils.clamp(baseZ - zOffset - mobileZAdjust, 0.55, 1.10);

          // Calculate target scale
          // BASE_FRAME_PIXELS calculated dynamically for consistent sizing across all devices
          const baseFramePixels = getBaseFramePixels(viewW, viewH);
          // Depth compensation: keep intended pixel width stable even if we move targetZ (glue-to-face).
          // Reference depth MUST match our chosen baseZ, otherwise scale drifts (especially on mobile).
          const refDepthForScale = CAMERA_Z - baseZ;
          const depthForScale = CAMERA_Z - targetZ;
          const depthCompForScale = refDepthForScale > 1e-3 ? (depthForScale / refDepthForScale) : 1;
          // Hinge-based sizing: treat `frameWidthMm` as hinge-to-hinge width (not full image width).
          // The hinge offset is in local frame units (0..1 where 1 ~= edge of the 2-unit-wide frame plane).
          // If hingeX < 1, hinge-to-hinge span is smaller than the full plane → scale up by 1/hingeX.
          // IMPORTANT: `currentHingeOffsetRef` is also used for temple placement and defaults to ~0.92
          // (slightly inward so temples overlap the frame edge). Using it raw for sizing would inflate
          // frames by ~1/0.92 ≈ +8.7%. Normalize against the default so hinge-based sizing is "neutral"
          // when hingeX is the default, and only changes size when a product provides a different hinge offset.
          const hingeXRaw = Number(currentHingeOffsetRef.current);
          const hingeXDefault = 0.92;
          const hingeXNorm = Number.isFinite(hingeXRaw) && hingeXRaw > 0 ? (hingeXRaw / hingeXDefault) : 1.0;
          const hingeXForSizing = vtoFlags.hingeBasedSizing
            ? Math.max(0.65, Math.min(1.35, hingeXNorm))
            : 1.0;
          const targetScale =
            (((targetGlassesPixels / baseFramePixels) * yawComp * manualScaleCorrection * depthCompForScale) / frontContentScaleXClamped) /
            hingeXForSizing;

          // Proper perspective projection: convert screen pixels to world coordinates
          // For a perspective camera with vertical FOV, the conversion depends on depth:
          // worldPos = (screenOffset / viewH) * 2 * tan(vFOV/2) * depth
          const vFovRad = 75 * Math.PI / 180; // Camera vertical FOV (must match camera setup)
          const tanHalfFov = Math.tan(vFovRad / 2);
          const depth = CAMERA_Z - targetZ; // Distance from camera to glasses plane
          
          // Pixels to world units conversion factor (based on camera projection math)
          const pixelsToWorld = (2 * tanHalfFov * depth) / viewH;

          // Empirical correction: when yaw changes, the chosen anchor point can drift in X.
          // Apply a slightly stronger correction at higher yaw to keep the frame "glued" to the face.
          // IMPORTANT: the displayed view is mirrored. Yaw coming from the tracker is in unmirrored image space,
          // so we must mirror yaw for any X-translation correction in view/world space. Otherwise it "drifts".
          const yawForView = isMirrored ? -(faceRotation.yaw || 0) : (faceRotation.yaw || 0);
          const yawAbsForOffset = Math.abs(yawForView);
          // Apply a deadzone + ramp so small yaw noise doesn't slide the glasses sideways.
          const yawOffsetRamp = smoothstep01(THREE.MathUtils.degToRad(5), THREE.MathUtils.degToRad(18), yawAbsForOffset);
          const yawOffsetGain = 1 + 0.55 * smoothstep01(Math.PI / 18, Math.PI / 6, yawAbsForOffset); // ~10°→30°
          const xYawOffset = THREE.MathUtils.clamp(yawForView * YAW_TO_X_OFFSET * yawOffsetGain * yawOffsetRamp, -0.16, 0.16);
          // Tiny "nose clearance": at larger yaw, shift slightly TOWARD the turn direction so the far lens
          // doesn't creep over the nose bridge. Keep this VERY subtle (few pixels max).
          const noseClearanceRamp = smoothstep01(THREE.MathUtils.degToRad(10), THREE.MathUtils.degToRad(30), yawAbsForOffset);
          const noseClearancePxMax = getIsMobileDevice() ? 5.5 : 4.0;
          const noseClearanceX = Math.sign(yawForView || 0) * (noseClearancePxMax * noseClearanceRamp) * pixelsToWorld;

          const targetPosition = new THREE.Vector3(
            // X: convert pixel offset to world coordinates using proper perspective math
            (anchorV[0] - viewW / 2) * pixelsToWorld + xYawOffset + noseClearanceX,
            // Y: negative because screen Y is inverted relative to world Y
            (anchorV[1] - viewH / 2) * -pixelsToWorld + NOSE_Y_OFFSET,
            targetZ
          );

          // Use tracker pose, with light mobile smoothing.
          let finalPos = targetPosition;
          let finalScale = targetScale;
          let finalRot = faceRotation;

          // Median-filter + velocity-adaptive EMA pose smoothing.
          //
          // STAGE 1 — 3-frame median filter on raw landmark targets:
          //   median(t-2, t-1, t) per axis kills single-frame spikes (phone shake, motion blur)
          //   perfectly, with only 1 frame of lag vs 0 for raw. Genuine movement across 2+
          //   consecutive frames passes through unaltered.
          //
          // STAGE 2 — velocity-adaptive EMA:
          //   velocity is measured on the MEDIAN output, not raw, so phone shake can no longer
          //   spike alpha. Sustained movement (≥2 frames same direction) correctly boosts alpha.
          //   Post-burst decay keeps glasses on the face as head slows down.
          const isMobileDeviceNow = getIsMobileDevice();
          const shouldSmoothPose = !vtoFlags.ultraLowLatency;
          if (shouldSmoothPose) {
            const s = poseSmoothingRef.current;
            const alphaMin = isMobileDeviceNow ? (getIsLowEndMobile() ? 0.012 : 0.016) : 0.40;
            const alphaMax = isMobileDeviceNow ? 0.82 : 0.95;

            // Desktop: window=1 → median is bypassed completely (history.length < 3 → use raw target).
            //   Desktop webcams are stable — no hand tremor, no need for any median lag.
            // Mobile: 5-frame window for aggressive tremor rejection — do NOT change.
            const posWindowSize = isMobileDeviceNow ? 5 : 1;
            const rotWindowSize = isMobileDeviceNow ? 5 : 1;

            // --- Stage 1a: median for position & scale ---
            s.history.push({ pos: finalPos.clone(), scale: finalScale });
            if (s.history.length > posWindowSize) s.history.shift();

            // --- Stage 1b: median for rotation ---
            s.rotHistory.push({ yaw: finalRot.yaw, pitch: finalRot.pitch, roll: finalRot.roll });
            if (s.rotHistory.length > rotWindowSize) s.rotHistory.shift();

            // Helper: scalar median via sort (works for any N)
            const medN = (arr) => {
              const sorted = [...arr].sort((a, b) => a - b);
              const mid = Math.floor(sorted.length / 2);
              return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
            };
            // Helper: scalar median of exactly 3 values (faster)
            const med3 = (a, b, c) => Math.max(Math.min(a, b), Math.min(Math.max(a, b), c));

            let medPos, medRot, medScale;
            if (s.history.length < 3) {
              medPos = finalPos;
              medScale = finalScale;
            } else {
              medPos = new THREE.Vector3(
                medN(s.history.map(h => h.pos.x)),
                medN(s.history.map(h => h.pos.y)),
                medN(s.history.map(h => h.pos.z)),
              );
              medScale = medN(s.history.map(h => h.scale));
            }
            if (s.rotHistory.length < 3) {
              medRot = finalRot;
            } else {
              const rh = s.rotHistory;
              medRot = {
                yaw:   medN(rh.map(r => r.yaw)),
                pitch: medN(rh.map(r => r.pitch)),
                roll:  medN(rh.map(r => r.roll)),
              };
            }

            if (!s.init) {
              s.init = true;
              s.pos.copy(medPos);
              s.prevMedPos.copy(medPos);
              s.scale = medScale;
              s.rot = { yaw: medRot.yaw, pitch: medRot.pitch, roll: medRot.roll };
              s.vel = 0;
              s.boostAlpha = 0;
            } else {
              // --- Stage 2: sustained-motion adaptive alpha ---
              // Velocity = how fast the MEDIAN TARGET moves (true head speed, not smoothing lag).
              const medDelta = s.prevMedPos.distanceTo(medPos);
              s.prevMedPos.copy(medPos);

              // rampStart: threshold that separates hand tremor from genuine head movement.
              // Hand tremor after 5-frame median ≈ 0.004–0.020 world units/frame.
              // Gentle head movement ≈ 0.028–0.060 world units/frame.
              // Setting rampStart=0.028 means tremor (≤0.020) NEVER crosses into alpha-boost zone.
              const rampStart = 0.028;
              const velHigh   = 0.052;

              // Peak-hold velocity: instantly peaks on any jerk, decays at 0.72/frame.
              s.vel = Math.max(s.vel * 0.72, medDelta);

              // Sustained motion counter:
              // MOBILE (strict): +1/-2 — tremor bursts (1 movement + 2 rest frames) net negative →
              //   counter stays at 0 → alphaMin. Needs ≥8 consecutive movement frames for full alpha.
              // DESKTOP (instant): +4/-1, sustainMax=4 — full alpha after just 1 movement frame.
              //   Desktop has zero hand tremor so there's no danger in being maximally responsive.
              const sustainInc  = isMobileDeviceNow ? 1 : 4;
              const sustainDec  = isMobileDeviceNow ? -2 : -1;
              const sustainMax  = isMobileDeviceNow ? 12 : 4;
              s.sustainCounter = Math.max(0, Math.min(sustainMax,
                s.sustainCounter + (medDelta > rampStart ? sustainInc : sustainDec)
              ));
              const sustainT = s.sustainCounter / sustainMax;
              // Mobile: sextic curve — completely flat until counter ≥ 8/12, then rises sharply.
              //   Ensures tremor bursts NEVER push into elevated alpha.
              // Desktop: linear (pow=1) — alpha ramps immediately from first movement frame.
              const curvePow = isMobileDeviceNow ? 6 : 1;
              const velAlpha = alphaMin + (alphaMax - alphaMin) * Math.pow(sustainT, curvePow);

              // Post-burst boost: only arm when BOTH velocity AND sustainCounter are high
              // (prevents single-frame phone-drop spikes from triggering settle lag).
              if (s.vel > velHigh * 0.8 && sustainT > 0.5) {
                s.boostAlpha = Math.max(s.boostAlpha, velAlpha);
              }
              s.boostAlpha *= 0.85;

              const a = Math.max(velAlpha, s.boostAlpha);

              // --- ROTATION: dedicated alpha + deadzone ---
              // Rotation titranje is the most visible artifact — fight it with:
              //   1. Fixed very low alpha (independent of velocity)
              //   2. Deadzone: changes < ~0.4° are clamped to zero (imperceptible to eye)
              const alphaRotBase = isMobileDeviceNow ? 0.035 : 0.30;
              // Allow slightly more rotation follow only during strongly confirmed sustained movement
              const aRot = alphaRotBase + (alphaMax - alphaRotBase) * Math.pow(sustainT, curvePow) * 0.25;
              // Desktop: tighter deadzone (0.23°) — webcam is stable so we can follow small rotations.
              // Mobile: wider deadzone (0.40°) — absorbs micro-jitter from hand-held devices.
              const ROT_DEADZONE = isMobileDeviceNow ? 0.007 : 0.004;

              s.pos.lerp(medPos, a);
              s.scale = s.scale * (1 - a) + medScale * a;

              const dyaw   = medRot.yaw   - s.rot.yaw;
              const dpitch = medRot.pitch - s.rot.pitch;
              const droll  = medRot.roll  - s.rot.roll;
              // Only update rotation if change exceeds deadzone
              if (Math.abs(dyaw)   > ROT_DEADZONE) s.rot.yaw   += dyaw   * aRot;
              if (Math.abs(dpitch) > ROT_DEADZONE) s.rot.pitch += dpitch * aRot;
              if (Math.abs(droll)  > ROT_DEADZONE) s.rot.roll  += droll  * aRot;
            }
            finalPos = s.pos;
            finalScale = s.scale;
            finalRot = s.rot;
          } else {
            poseSmoothingRef.current.init = false;
          }

          /*
          // Apply low-latency stabilization (One Euro Filter) with motion detection bypass
          const tMs = performance.now();
          const f = filtersRef.current;
          const m = motionRef.current;

          // ============================================
          // SOPHISTICATED CONTEXT-AWARE MOTION ANALYSIS
          // ============================================
          // Analyzes motion patterns, velocity, acceleration, direction changes
          // Adapts smoothing parameters for "glued to face" natural feel
          const ctx = motionContextRef.current;
          let motionMagnitude = 0;
          let motionState = 'still';
          
          if (m.tMs !== null) {
            const dt = Math.max(1e-3, (tMs - m.tMs) / 1000);
            
            // Calculate velocity components
            const posDelta = new THREE.Vector3().subVectors(targetPosition, m.pos);
            const posVel = posDelta.length() / dt;
            const rotDelta = {
              yaw: Math.abs(faceRotation.yaw - m.rot.yaw),
              pitch: Math.abs(faceRotation.pitch - m.rot.pitch),
              roll: Math.abs(faceRotation.roll - m.rot.roll),
            };
            const rotVel = (rotDelta.yaw + rotDelta.pitch + rotDelta.roll) / dt;
            const scaleVel = Math.abs(targetScale - m.scale) / dt;
            
            // Update velocity tracking (exponential moving average for stability)
            const velSmooth = 0.7;
            ctx.velocity.pos = ctx.velocity.pos * velSmooth + posVel * (1 - velSmooth);
            ctx.velocity.rot = ctx.velocity.rot * velSmooth + rotVel * (1 - velSmooth);
            ctx.velocity.scale = ctx.velocity.scale * velSmooth + scaleVel * (1 - velSmooth);
            
            // Calculate acceleration (rate of change of velocity)
            const prevVelPos = ctx.velocity.pos;
            const prevVelRot = ctx.velocity.rot;
            const prevVelScale = ctx.velocity.scale;
            const prevAccelPos = ctx.acceleration.pos;
            const prevAccelRot = ctx.acceleration.rot;
            const prevAccelScale = ctx.acceleration.scale;
            
            ctx.acceleration.pos = Math.abs(ctx.velocity.pos - prevVelPos) / dt;
            ctx.acceleration.rot = Math.abs(ctx.velocity.rot - prevVelRot) / dt;
            ctx.acceleration.scale = Math.abs(ctx.velocity.scale - prevVelScale) / dt;
            
            // Calculate jerk (rate of change of acceleration) - critical for jitter detection
            ctx.jerk.pos = Math.abs(ctx.acceleration.pos - prevAccelPos) / dt;
            ctx.jerk.rot = Math.abs(ctx.acceleration.rot - prevAccelRot) / dt;
            ctx.jerk.scale = Math.abs(ctx.acceleration.scale - prevAccelScale) / dt;
            
            // Combined motion magnitude (weighted)
            motionMagnitude = ctx.velocity.pos * 0.5 + ctx.velocity.rot * 0.3 + ctx.velocity.scale * 0.2;
            
            // Enhanced jitter detection using multiple methods
            const jerkMagnitude = (ctx.jerk.pos + ctx.jerk.rot + ctx.jerk.scale) / 3;
            const highFreqThreshold = 5.0; // High jerk indicates high-frequency noise
            
            // Method 1: High jerk with low velocity = jitter (hand shake / camera shake)
            const highFreqThresholdRelaxed = 3.2; // Lower = detect hand tremor sooner
            if (jerkMagnitude > highFreqThresholdRelaxed && motionMagnitude < 0.45) {
              ctx.highFrequencyNoise = Math.min(1.0, ctx.highFrequencyNoise * 0.92 + 0.12);
            } else {
              ctx.highFrequencyNoise = Math.max(0, ctx.highFrequencyNoise * 0.94);
            }
            
            // Method 2: Rapid direction changes with small magnitude = jitter (oscillating hand)
            ctx.directionChangeRate = ctx.directionChanges / (ctx.history.length * dt);
            if (ctx.directionChangeRate > 2.0 && motionMagnitude < 0.5) {
              ctx.jitterScore = Math.min(1.0, ctx.jitterScore * 0.88 + 0.18);
            } else {
              ctx.jitterScore = Math.max(0, ctx.jitterScore * 0.94);
            }
            
            // Method 3: Micro-movements (small magnitude, high frequency) = hand shake
            if (motionMagnitude < 0.18 && jerkMagnitude > 1.5) {
              ctx.microMovementCount = Math.min(10, ctx.microMovementCount + 2);
            } else {
              ctx.microMovementCount = Math.max(0, ctx.microMovementCount - 1);
            }
            
            // Combined jitter score
            const jitterFromHighFreq = ctx.highFrequencyNoise * 0.4;
            const jitterFromDirection = Math.min(1.0, ctx.directionChangeRate / 5.0) * 0.3;
            const jitterFromMicro = Math.min(1.0, ctx.microMovementCount / 5.0) * 0.3;
            ctx.jitterScore = Math.max(ctx.jitterScore, jitterFromHighFreq + jitterFromDirection + jitterFromMicro);
            
            // Sudden change detection (large acceleration spike)
            const suddenChangeThreshold = 10.0;
            if (jerkMagnitude > suddenChangeThreshold) {
              ctx.suddenChangeDetected = true;
              ctx.lastSuddenChangeTime = tMs;
            } else if (tMs - ctx.lastSuddenChangeTime > 200) {
              ctx.suddenChangeDetected = false;
            }
            
            // Detect direction changes (for oscillatory motion detection)
            const currentDirection = {
              pos: posDelta.clone().normalize(),
              rot: { yaw: Math.sign(faceRotation.yaw - m.rot.yaw), pitch: Math.sign(faceRotation.pitch - m.rot.pitch), roll: Math.sign(faceRotation.roll - m.rot.roll) }
            };
            
            if (ctx.lastDirection.pos.length() > 0 && currentDirection.pos.length() > 0) {
              const dot = ctx.lastDirection.pos.dot(currentDirection.pos);
              if (dot < 0) ctx.directionChanges++; // Direction reversed
            }
            ctx.lastDirection = currentDirection;
            
            // Add to history
            ctx.history.push({
              t: tMs,
              magnitude: motionMagnitude,
              velocity: { ...ctx.velocity },
              acceleration: { ...ctx.acceleration },
            });
            if (ctx.history.length > ctx.maxHistorySize) {
              ctx.history.shift();
            }
            
            // Analyze motion pattern and classify state
            const avgMagnitude = ctx.history.reduce((sum, h) => sum + h.magnitude, 0) / ctx.history.length;
            const maxMagnitude = Math.max(...ctx.history.map(h => h.magnitude));
            const avgAccel = (ctx.acceleration.pos + ctx.acceleration.rot + ctx.acceleration.scale) / 3;
            
            // ============================================
            // PHASE 6: ENHANCED MOTION STATE CLASSIFICATION
            // ============================================
            // Enhanced classification with jump-aware states and hysteresis
            // Priority: jump detection > jitter detection > sudden change > testing > rapid > fast > oscillatory > slow > still
            
            let newMotionState = motionState;
            
            // Camera shake / hand tremor: enter jitter mode earlier and for larger motion range
            if (ctx.jitterScore > 0.28 || ctx.highFrequencyNoise > 0.35 || ctx.microMovementCount > 2) {
              // Hand-held jitter (drhtanje ruke) - prioritize strong smoothing
              if (motionMagnitude < 0.25) {
                newMotionState = 'micro-jitter'; // Hand shake, camera noise
              } else {
                newMotionState = 'oscillatory'; // Larger oscillatory / hand drift
              }
            } else if (ctx.suddenChangeDetected) {
              // Sudden change detected - switch to 0ms latency immediately
              newMotionState = 'sudden-change'; // Sudden movement (quick head turn, etc.)
            } else if (motionMagnitude >= 1.0 || maxMagnitude >= 1.5) {
              // Lower threshold for testing mode (more aggressive 0ms latency)
              newMotionState = 'testing'; // Rapid head turning (testing scenario)
            } else if (motionMagnitude >= 0.5 || avgMagnitude >= 0.4) {
              // Lowered threshold from 0.7 to 0.5 for earlier rapid detection
              newMotionState = 'rapid'; // Fast intentional movement
            } else if (motionMagnitude >= 0.25 || avgMagnitude >= 0.15) {
              // Lowered threshold from 0.35 to 0.25 for earlier fast detection (0ms latency)
              newMotionState = 'fast'; // Normal fast movement
            } else if (ctx.directionChanges > 2 && ctx.history.length >= 4 && ctx.directionChangeRate > 2.0) {
              // More sensitive oscillatory detection
              newMotionState = 'oscillatory'; // Back-and-forth motion
            } else if (motionMagnitude >= 0.12 || avgMagnitude >= 0.08) {
              newMotionState = 'slow'; // Slow intentional movement
            } else {
              newMotionState = 'still'; // Still or micro-movements
            }
            
            // ============================================
            // STATE TRANSITION WITH HYSTERESIS
            // ============================================
            // Prevent rapid state switching (hysteresis)
            // Only switch states if new state is significantly different or confidence is high
            
            // Add to state history
            ctx.stateHistory.push({ state: newMotionState, t: tMs, confidence: ctx.stabilityScore * ctx.smoothnessScore });
            if (ctx.stateHistory.length > ctx.maxStateHistorySize) {
              ctx.stateHistory.shift();
            }
            
            // Calculate state confidence based on consistency
            const recentStates = ctx.stateHistory.slice(-3);
            const stateConsistency = recentStates.filter(s => s.state === newMotionState).length / recentStates.length;
            ctx.stateConfidence = stateConsistency;
            
            // Apply hysteresis: only switch if confidence is high or state is significantly different
            const stateChanged = newMotionState !== ctx.state;
            const isSignificantChange = 
              (ctx.state === 'still' && newMotionState !== 'still') ||
              (ctx.state === 'testing' && newMotionState !== 'testing') ||
              (ctx.state === 'jump-detected' && newMotionState !== 'jump-detected') ||
              (newMotionState === 'jump-detected' && ctx.state !== 'jump-detected');
            
            if (stateChanged) {
              if (isSignificantChange || ctx.stateConfidence > 0.7 || ctx.state === ctx.previousState) {
                // High confidence or significant change: switch immediately
                ctx.previousState = ctx.state;
                motionState = newMotionState;
              } else {
                // Low confidence: keep previous state (hysteresis)
                motionState = ctx.state;
              }
            } else {
              motionState = newMotionState;
            }
            
            // Enhanced stability and smoothness scores (more sophisticated)
            const variance = ctx.history.length > 1 
              ? ctx.history.reduce((sum, h) => sum + Math.pow(h.magnitude - avgMagnitude, 2), 0) / ctx.history.length
              : 0;
            
            // Stability score: considers variance, jitter, direction changes, and jumps
            const varianceScore = Math.max(0, 1 - Math.min(1, variance * 2));
            const jitterPenalty = ctx.jitterScore * 0.5;
            const directionPenalty = Math.min(1.0, ctx.directionChangeRate / 4.0) * 0.3;
            const jumpPenalty = ctx.jumpScore * 0.4; // Factor in jump score
            ctx.stabilityScore = Math.max(0, varianceScore - jitterPenalty - directionPenalty - jumpPenalty);
            
            // Smoothness score: considers acceleration, jerk, velocity consistency, and jumps
            const accelScore = Math.max(0, 1 - Math.min(1, avgAccel * 0.5));
            const jerkPenalty = Math.min(1.0, (ctx.jerk.pos + ctx.jerk.rot + ctx.jerk.scale) / 3 / 5.0) * 0.4;
            const velocityConsistency = ctx.history.length > 2
              ? 1 - Math.abs(ctx.velocity.pos - ctx.history[ctx.history.length - 2].velocity.pos) / Math.max(0.1, ctx.velocity.pos)
              : 1.0;
            const jumpSmoothnessPenalty = ctx.jumpScore * 0.3; // Factor in jump score
            ctx.smoothnessScore = Math.max(0, accelScore * 0.5 + velocityConsistency * 0.3 - jerkPenalty - jumpSmoothnessPenalty);
            
            // Jitter elimination factor (how aggressively to eliminate jitter)
            ctx.jitterEliminationFactor = Math.max(0.3, 1.0 - ctx.jitterScore * 0.7);
            
            // ============================================
            // PHASE 7: PREDICTION ACCURACY TRACKING
            // ============================================
            // Track prediction accuracy to improve prediction over time
            if (ctx.lastPredictedPosition !== null && m.tMs !== null) {
              // Calculate prediction error (difference between predicted and actual)
              const posError = targetPosition.distanceTo(ctx.lastPredictedPosition);
              const rotError = Math.abs(faceRotation.yaw - (ctx.lastPredictedRotation?.yaw || 0)) +
                              Math.abs(faceRotation.pitch - (ctx.lastPredictedRotation?.pitch || 0)) +
                              Math.abs(faceRotation.roll - (ctx.lastPredictedRotation?.roll || 0));
              const scaleError = Math.abs(targetScale - (ctx.lastPredictedScale || 1));
              
              // Normalize errors relative to expected motion
              const expectedMotion = motionMagnitude * dt;
              const normalizedPosError = expectedMotion > 0 ? posError / (expectedMotion + 0.001) : posError;
              const normalizedRotError = expectedMotion > 0 ? rotError / (expectedMotion + 0.001) : rotError;
              const normalizedScaleError = expectedMotion > 0 ? scaleError / (expectedMotion + 0.001) : scaleError;
              
              // Combined prediction error (0 = perfect, higher = worse)
              const predictionError = (normalizedPosError + normalizedRotError * 0.1 + normalizedScaleError * 0.1) / 1.2;
              
              // Add to prediction history
              ctx.predictionHistory.push({
                t: tMs,
                error: predictionError,
                posError,
                rotError,
                scaleError,
              });
              if (ctx.predictionHistory.length > ctx.maxPredictionHistorySize) {
                ctx.predictionHistory.shift();
              }
              
              // Calculate prediction accuracy (0-1, higher = better)
              const avgError = ctx.predictionHistory.reduce((sum, h) => sum + h.error, 0) / ctx.predictionHistory.length;
              ctx.predictionAccuracy = Math.max(0, 1 - Math.min(1, avgError * 2)); // Convert error to accuracy
            }
            
            // Reset direction change counter periodically
            if (ctx.history.length >= ctx.maxHistorySize) {
              ctx.directionChanges = Math.max(0, ctx.directionChanges - 1);
            }
          }
          
          ctx.state = motionState;

          // ============================================
          // PHASE 1: FRAME-TO-FRAME DISCONTINUITY DETECTION
          // ============================================
          // Detects frame-to-frame jumps and classifies severity
          // Critical for eliminating visible jumps in tracking
          
          // Initialize previous values on first frame (skip jump detection)
          const isFirstFrame = ctx.previousPosition.length() === 0 && 
                              ctx.previousPosition.x === 0 && 
                              ctx.previousPosition.y === 0 && 
                              ctx.previousPosition.z === 0;
          
          // Declare variables outside if block for use in jump history
          let jumpSeverity = 'none';
          let posJump = 0;
          let rotJump = 0;
          let scaleJump = 0;
          let jumpRatio = 0;
          
          if (!isFirstFrame) {
            // Calculate frame-to-frame deltas
            posJump = targetPosition.distanceTo(ctx.previousPosition);
            rotJump = Math.abs(faceRotation.yaw - ctx.previousRotation.yaw) + 
                            Math.abs(faceRotation.pitch - ctx.previousRotation.pitch) + 
                            Math.abs(faceRotation.roll - ctx.previousRotation.roll);
            scaleJump = Math.abs(targetScale - ctx.previousScale);
            
            // Normalize jumps (relative to expected motion)
            // Expected motion is based on current velocity and time delta
            const expectedMotion = motionMagnitude > 0 
              ? motionMagnitude * (m.tMs !== null ? Math.max(1e-3, (tMs - m.tMs) / 1000) : 0.016)
              : 0.001; // Small baseline to avoid division by zero
            
            const totalJump = posJump + rotJump * 0.1 + scaleJump * 0.1; // Normalize rotation/scale to position scale
            jumpRatio = totalJump / (expectedMotion + 0.001);
            
            // Classify jump severity
            if (jumpRatio > 5.0) {
              jumpSeverity = 'extreme';
            } else if (jumpRatio > 3.0) {
              jumpSeverity = 'large';
            } else if (jumpRatio > 2.0) {
              jumpSeverity = 'medium';
            } else if (jumpRatio > 1.5) {
              jumpSeverity = 'small';
            }
          }
          
          ctx.jumpSeverity = jumpSeverity;
          
          // Add to jump history (only if not first frame and jump detected)
          if (!isFirstFrame && jumpSeverity !== 'none') {
            ctx.jumpHistory.push({
              t: tMs,
              severity: jumpSeverity,
              ratio: jumpRatio,
              posJump,
              rotJump,
              scaleJump,
            });
            if (ctx.jumpHistory.length > ctx.maxJumpHistorySize) {
              ctx.jumpHistory.shift();
            }
          }
          
          // Calculate jump frequency (jumps per second)
          if (ctx.jumpHistory.length > 1) {
            const timeSpan = (ctx.jumpHistory[ctx.jumpHistory.length - 1].t - ctx.jumpHistory[0].t) / 1000;
            ctx.jumpFrequency = ctx.jumpHistory.length / Math.max(0.1, timeSpan);
          } else {
            ctx.jumpFrequency = 0;
          }
          
          // Calculate jump score (0-1, higher = more jumps)
          const recentJumps = ctx.jumpHistory.filter(j => tMs - j.t < 1000); // Last second
          const severeJumps = recentJumps.filter(j => j.severity === 'large' || j.severity === 'extreme').length;
          ctx.jumpScore = Math.min(1.0, (recentJumps.length * 0.1 + severeJumps * 0.3));
          
          // Update previous values for next frame (always update, even on first frame)
          ctx.previousPosition.copy(targetPosition);
          ctx.previousRotation = { ...faceRotation };
          ctx.previousScale = targetScale;

          // ============================================
          // PHASE 4: JUMP SMOOTHING AND INTERPOLATION
          // ============================================
          // DISABLED: Jump interpolation was causing tracking issues
          // The jump detection is still active for state classification,
          // but we don't modify target values to avoid interfering with normal tracking

          // ============================================
          // ADAPTIVE SMOOTHING PARAMETER CALCULATION
          // ============================================
          // Uses motion context to determine optimal smoothing for "glued to face" feel
          // Adapts to all scenarios: still, slow, fast, rapid, oscillatory, testing
          let rawBlend = 0;
          let adaptiveSmoothAlpha = 0.10;
          
          // ============================================
          // SIMPLE & POWERFUL PERFORMANCE ALGORITHM - OPTIMIZED FOR 0MS LATENCY
          // ============================================
          // Rule 1: Fast motion (motionMagnitude > 0.3) = 100% RAW = TRUE 0ms LATENCY
          // Rule 2: Jittery/still = MAXIMUM SMOOTHING = ZERO JITTER
          // Rule 3: Everything else = Balanced but responsive
          
          // Firmer glued: only clear fast motion gets raw; everything else heavily filtered
          const isJittering = (motionMagnitude < 0.36 && ctx.jitterScore > 0.02) || motionState === 'micro-jitter';
          const isFastMotionState = motionMagnitude > 0.50 || motionState === 'rapid' || motionState === 'testing' || motionState === 'sudden-change' || motionState === 'fast';
          
          if (isFastMotionState) {
            rawBlend = 1.0;
            adaptiveSmoothAlpha = 1.0;
          } else if (isJittering || motionState === 'oscillatory' || (motionState === 'still' && ctx.jitterScore > 0.02)) {
            rawBlend = 0.0;
            // micro-jitter (hand shake): extra-strong smoothing so glasses stay stable
            adaptiveSmoothAlpha = motionState === 'micro-jitter' ? 0.0018 : 0.0035; // ~99.8% old when hand shake
          } else if (motionState === 'slow') {
            rawBlend = 0.20;
            adaptiveSmoothAlpha = 0.28;
          } else {
            rawBlend = 0.14; // Default: very filtered = firmer
            adaptiveSmoothAlpha = 0.22;
          }
          
          rawBlend = Math.max(0, Math.min(1, rawBlend));
          adaptiveSmoothAlpha = Math.max(0.003, Math.min(1.0, adaptiveSmoothAlpha)); // allow very low for firmer glued
          
          // Store adaptive parameters
          ctx.adaptiveRawBlend = rawBlend;
          ctx.adaptiveSmoothAlpha = adaptiveSmoothAlpha;
          
          const filterBlend = 1.0 - rawBlend;

          // ============================================
          // ADAPTIVE ONEEURO FILTER PARAMETER ADJUSTMENT
          // ============================================
          // Adjust OneEuro parameters based on motion context for optimal performance
          // Lower minCutoff when jitter detected (more smoothing)
          // Higher beta when fast motion (less lag)
          // Adjust dCutoff based on motion smoothness
          
          // Reset to base parameters
          TRACKING_FILTER.position = { ...TRACKING_FILTER_BASE.position };
          TRACKING_FILTER.rotation = { ...TRACKING_FILTER_BASE.rotation };
          TRACKING_FILTER.scale = { ...TRACKING_FILTER_BASE.scale };
          
          // Jitter smoothing: stronger when jitter/still/slow; extra-strong for micro-jitter (camera shake)
          if (motionState === 'micro-jitter') {
            TRACKING_FILTER.position.minCutoff *= 0.034; // Maximum smoothing for hand shake
            TRACKING_FILTER.rotation.minCutoff *= 0.034;
            TRACKING_FILTER.scale.minCutoff *= 0.034;
          } else if (ctx.jitterScore > 0.04 || motionState === 'oscillatory' || motionState === 'still' || motionState === 'slow') {
            TRACKING_FILTER.position.minCutoff *= 0.06; // Strong smoothing for jitter
            TRACKING_FILTER.rotation.minCutoff *= 0.06;
            TRACKING_FILTER.scale.minCutoff *= 0.06;
          } else if (motionMagnitude > 0.45 || motionState === 'fast' || motionState === 'rapid' || motionState === 'testing' || motionState === 'sudden-change') {
            // Maximum responsiveness for fast motion (0ms latency goal) - increased beta multiplier
            TRACKING_FILTER.position.beta *= 1.5; // Increased from 1.3 to 1.5 for faster response
            TRACKING_FILTER.rotation.beta *= 1.5;
            TRACKING_FILTER.scale.beta *= 1.5;
            // Also slightly increase minCutoff for faster response (less smoothing)
            TRACKING_FILTER.position.minCutoff *= 1.2;
            TRACKING_FILTER.rotation.minCutoff *= 1.2;
            TRACKING_FILTER.scale.minCutoff *= 1.2;
          }
          
          // Update filter parameters (filters are already created, so we update their properties)
          f.posX.minCutoff = TRACKING_FILTER.position.minCutoff;
          f.posX.beta = TRACKING_FILTER.position.beta;
          f.posX.dCutoff = TRACKING_FILTER.position.dCutoff;
          f.posY.minCutoff = TRACKING_FILTER.position.minCutoff;
          f.posY.beta = TRACKING_FILTER.position.beta;
          f.posY.dCutoff = TRACKING_FILTER.position.dCutoff;
          f.posZ.minCutoff = TRACKING_FILTER.position.minCutoff;
          f.posZ.beta = TRACKING_FILTER.position.beta;
          f.posZ.dCutoff = TRACKING_FILTER.position.dCutoff;
          
          f.yaw.minCutoff = TRACKING_FILTER.rotation.minCutoff;
          f.yaw.beta = TRACKING_FILTER.rotation.beta;
          f.yaw.dCutoff = TRACKING_FILTER.rotation.dCutoff;
          f.pitch.minCutoff = TRACKING_FILTER.rotation.minCutoff;
          f.pitch.beta = TRACKING_FILTER.rotation.beta;
          f.pitch.dCutoff = TRACKING_FILTER.rotation.dCutoff;
          f.roll.minCutoff = TRACKING_FILTER.rotation.minCutoff;
          f.roll.beta = TRACKING_FILTER.rotation.beta;
          f.roll.dCutoff = TRACKING_FILTER.rotation.dCutoff;
          
          f.scale.minCutoff = TRACKING_FILTER.scale.minCutoff;
          f.scale.beta = TRACKING_FILTER.scale.beta;
          f.scale.dCutoff = TRACKING_FILTER.scale.dCutoff;

          // Apply OneEuro filtering (base layer)
          const oneEuroPosX = f.posX.filter(targetPosition.x, tMs);
          const oneEuroPosY = f.posY.filter(targetPosition.y, tMs);
          const oneEuroPosZ = f.posZ.filter(targetPosition.z, tMs);
          const oneEuroScale = f.scale.filter(targetScale, tMs);
          const oneEuroYaw = f.yaw.filter(faceRotation.yaw, tMs);
          const oneEuroPitch = f.pitch.filter(faceRotation.pitch, tMs);
          const oneEuroRoll = f.roll.filter(faceRotation.roll, tMs);
          
          // ============================================
          // PHASE 2: DOUBLE EXPONENTIAL SMOOTHING LAYER
          // ============================================
          // SIMPLIFIED: Use very light smoothing only when needed
          // Bypass for fast motion to maintain responsiveness
          const des = ctx.desFilters;
          
          // ============================================
          // SIMPLE FILTERING: Bypass DES for fast motion (0ms latency)
          // ============================================
          // Only use DES for jitter elimination when still/slow
          // Fast motion bypasses DES completely for true 0ms latency
          const useDES = rawBlend < 0.5; // Only when using filtered values
          
          let filteredPosX, filteredPosY, filteredPosZ, filteredScale;
          let filteredYaw, filteredPitch, filteredRoll;
          
          if (useDES && (motionState === 'still' || motionState === 'slow' || motionState === 'micro-jitter' || motionState === 'oscillatory') && ctx.jitterScore > 0.008) {
            // micro-jitter (hand shake): strongest DES to eliminate camera tremor
            const desAlpha = motionState === 'micro-jitter' ? 0.045 : 0.07;
            const desGamma = motionState === 'micro-jitter' ? 0.02 : 0.03;
            des.posX.setParams(desAlpha, desGamma);
            des.posY.setParams(desAlpha, desGamma);
            des.posZ.setParams(desAlpha, desGamma);
            des.yaw.setParams(desAlpha, desGamma);
            des.pitch.setParams(desAlpha, desGamma);
            des.roll.setParams(desAlpha, desGamma);
            des.scale.setParams(desAlpha, desGamma);
            
            filteredPosX = des.posX.filter(oneEuroPosX);
            filteredPosY = des.posY.filter(oneEuroPosY);
            filteredPosZ = des.posZ.filter(oneEuroPosZ);
            filteredScale = des.scale.filter(oneEuroScale);
            filteredYaw = des.yaw.filter(oneEuroYaw);
            filteredPitch = des.pitch.filter(oneEuroPitch);
            filteredRoll = des.roll.filter(oneEuroRoll);
            
            ctx.desTrend = {
              pos: new THREE.Vector3(des.posX.getTrend(), des.posY.getTrend(), des.posZ.getTrend()),
              rot: { yaw: des.yaw.getTrend(), pitch: des.pitch.getTrend(), roll: des.roll.getTrend() },
              scale: des.scale.getTrend(),
            };
          } else {
            // Bypass DES - use OneEuro directly (faster, 0ms latency for fast motion)
            filteredPosX = oneEuroPosX;
            filteredPosY = oneEuroPosY;
            filteredPosZ = oneEuroPosZ;
            filteredScale = oneEuroScale;
            filteredYaw = oneEuroYaw;
            filteredPitch = oneEuroPitch;
            filteredRoll = oneEuroRoll;
            
            ctx.desTrend = {
              pos: new THREE.Vector3(0, 0, 0),
              rot: { yaw: 0, pitch: 0, roll: 0 },
              scale: 0,
            };
          }

          // ============================================
          // ENHANCED CONTEXT-AWARE DEAD ZONE
          // ============================================
          // Adapts dead zone based on motion state and jitter level
          // Larger dead zone when jittery/still (filters jitter)
          // Smaller/no dead zone when moving (allows natural movement, 0ms latency)
          let DEAD_ZONE = { position: 0, rotation: 0, scale: 0 };
          let applyDeadZone = false;
          
          // Dead zone scales with jitter score for better jitter elimination
          const jitterMultiplier = 1.0 + ctx.jitterScore * 2.0; // Up to 3x larger when jittery
          
          if (motionState === 'micro-jitter' || motionState === 'oscillatory') {
            // micro-jitter (hand shake): larger dead zone to absorb camera tremor
            const basePos = motionState === 'micro-jitter' ? 0.032 : 0.022;
            DEAD_ZONE = {
              position: basePos * jitterMultiplier,
              rotation: (basePos * 1.35) * jitterMultiplier,
              scale: (basePos * 0.5) * jitterMultiplier,
            };
            applyDeadZone = true;
          } else if (motionState === 'still') {
            DEAD_ZONE = {
              position: 0.019,
              rotation: 0.026,
              scale: 0.009,
            };
            applyDeadZone = true;
          } else if (motionState === 'slow') {
            DEAD_ZONE = {
              position: 0.011,
              rotation: 0.016,
              scale: 0.005,
            };
            applyDeadZone = true;
          } else {
            // No dead zone for fast/rapid/testing/sudden-change movement (zero latency)
            applyDeadZone = false;
          }

          // Calculate changes from previous smoothed values
          const posDelta = new THREE.Vector3(
            filteredPosX - smoothedPosition.current.x,
            filteredPosY - smoothedPosition.current.y,
            filteredPosZ - smoothedPosition.current.z
          );
          const rotDelta = {
            yaw: filteredYaw - smoothedRotation.current.yaw,
            pitch: filteredPitch - smoothedRotation.current.pitch,
            roll: filteredRoll - smoothedRotation.current.roll,
          };
          const scaleDelta = filteredScale - smoothedScale.current;

          // Apply dead zone only when motion is low (for stability)
          const posDeltaLength = posDelta.length();
          const rotDeltaMagnitude = Math.abs(rotDelta.yaw) + Math.abs(rotDelta.pitch) + Math.abs(rotDelta.roll);
          
          let finalFilteredPosX = filteredPosX;
          let finalFilteredPosY = filteredPosY;
          let finalFilteredPosZ = filteredPosZ;
          let finalFilteredYaw = filteredYaw;
          let finalFilteredPitch = filteredPitch;
          let finalFilteredRoll = filteredRoll;
          let finalFilteredScale = filteredScale;

          if (applyDeadZone) {
            // Only apply dead zone when motion is low (stability mode) - bypass for fast motion (0ms latency)
            // Dead zone adds latency, so we skip it during fast motion
            const isFastMotionDeadZone = motionMagnitude > 0.3 || motionState === 'fast' || motionState === 'rapid' || motionState === 'testing' || motionState === 'sudden-change';
            
            if (!isFastMotionDeadZone && posDeltaLength < DEAD_ZONE.position) {
              finalFilteredPosX = smoothedPosition.current.x;
              finalFilteredPosY = smoothedPosition.current.y;
              finalFilteredPosZ = smoothedPosition.current.z;
            }

            if (!isFastMotionDeadZone && rotDeltaMagnitude < DEAD_ZONE.rotation) {
              finalFilteredYaw = smoothedRotation.current.yaw;
              finalFilteredPitch = smoothedRotation.current.pitch;
              finalFilteredRoll = smoothedRotation.current.roll;
            }

            if (!isFastMotionDeadZone && Math.abs(scaleDelta) < DEAD_ZONE.scale) {
              finalFilteredScale = smoothedScale.current;
            }
          }

          // Blend filtered and raw values based on motion
          smoothedPosition.current.set(
            finalFilteredPosX * filterBlend + targetPosition.x * rawBlend,
            finalFilteredPosY * filterBlend + targetPosition.y * rawBlend,
            finalFilteredPosZ * filterBlend + targetPosition.z * rawBlend
          );

          smoothedScale.current = finalFilteredScale * filterBlend + targetScale * rawBlend;
          smoothedRotation.current = {
            yaw: finalFilteredYaw * filterBlend + faceRotation.yaw * rawBlend,
            pitch: finalFilteredPitch * filterBlend + faceRotation.pitch * rawBlend,
            roll: finalFilteredRoll * filterBlend + faceRotation.roll * rawBlend,
          };

          // ============================================
          // ADAPTIVE ULTRA-SMOOTH LAYER
          // ============================================
          // Uses context-aware adaptive smoothing for "glued to face" natural feel
          // Adapts smoothing intensity based on motion state and quality metrics
          if (rawBlend >= 1.0) {
            // Testing/rapid mode: bypass ultra-smooth layer completely for TRUE 0ms latency
            ultraSmoothPosition.current.copy(smoothedPosition.current);
            ultraSmoothScale.current = smoothedScale.current;
            ultraSmoothRotation.current = { ...smoothedRotation.current };
          } else {
            // Apply context-aware adaptive smoothing
            // The adaptiveSmoothAlpha was calculated based on motion state and quality metrics
            ultraSmoothPosition.current.lerp(smoothedPosition.current, adaptiveSmoothAlpha);
            ultraSmoothScale.current = ultraSmoothScale.current * (1 - adaptiveSmoothAlpha) + smoothedScale.current * adaptiveSmoothAlpha;
            ultraSmoothRotation.current = {
              yaw: ultraSmoothRotation.current.yaw * (1 - adaptiveSmoothAlpha) + smoothedRotation.current.yaw * adaptiveSmoothAlpha,
              pitch: ultraSmoothRotation.current.pitch * (1 - adaptiveSmoothAlpha) + smoothedRotation.current.pitch * adaptiveSmoothAlpha,
              roll: ultraSmoothRotation.current.roll * (1 - adaptiveSmoothAlpha) + smoothedRotation.current.roll * adaptiveSmoothAlpha,
            };
          }

          // ============================================
          // PHASE 3: ENHANCED MOTION PREDICTION FOR 0MS LATENCY
          // ============================================
          // Adaptive lookahead with velocity/acceleration compensation
          // Uses double exponential trend for better prediction
          // Jump-aware prediction for accurate tracking
          let finalPos = ultraSmoothPosition.current;
          let finalScale = ultraSmoothScale.current;
          let finalRot = ultraSmoothRotation.current;

          if (MOTION_PREDICTION.enabled) {
            const m = motionRef.current;
            const prevT = m.tMs;
            const dt = prevT == null ? 0 : Math.max(1e-3, (tMs - prevT) / 1000);

            if (prevT != null) {
              // Enhanced velocity calculation using double exponential trend
              // Blend between traditional velocity and DES trend for better prediction
              const vpx = (ultraSmoothPosition.current.x - m.pos.x) / dt;
              const vpy = (ultraSmoothPosition.current.y - m.pos.y) / dt;
              const vpz = (ultraSmoothPosition.current.z - m.pos.z) / dt;
              
              // Use DES trend if available (better for prediction)
              const trendBlend = ctx.desTrend && ctx.desTrend.pos ? 0.7 : 0.0; // Use DES trend if available
              const velPosFromTrend = ctx.desTrend && ctx.desTrend.pos 
                ? ctx.desTrend.pos.clone().multiplyScalar(1 / dt) // Convert trend to velocity
                : new THREE.Vector3();
              const blendedVelPos = new THREE.Vector3(
                vpx * (1 - trendBlend) + velPosFromTrend.x * trendBlend,
                vpy * (1 - trendBlend) + velPosFromTrend.y * trendBlend,
                vpz * (1 - trendBlend) + velPosFromTrend.z * trendBlend
              );
              
              m.velPos.lerp(blendedVelPos, 1 - MOTION_PREDICTION.velSmoothing);

              // Rotation velocity with DES trend
              const vyaw = (ultraSmoothRotation.current.yaw - m.rot.yaw) / dt;
              const vpitch = (ultraSmoothRotation.current.pitch - m.rot.pitch) / dt;
              const vroll = (ultraSmoothRotation.current.roll - m.rot.roll) / dt;
              
              const velRotFromTrend = ctx.desTrend && ctx.desTrend.rot ? {
                yaw: ctx.desTrend.rot.yaw / dt,
                pitch: ctx.desTrend.rot.pitch / dt,
                roll: ctx.desTrend.rot.roll / dt,
              } : { yaw: 0, pitch: 0, roll: 0 };
              
              m.velRot.yaw = (m.velRot.yaw * MOTION_PREDICTION.velSmoothing + vyaw * (1 - MOTION_PREDICTION.velSmoothing)) * (1 - trendBlend) + 
                             velRotFromTrend.yaw * trendBlend;
              m.velRot.pitch = (m.velRot.pitch * MOTION_PREDICTION.velSmoothing + vpitch * (1 - MOTION_PREDICTION.velSmoothing)) * (1 - trendBlend) + 
                               velRotFromTrend.pitch * trendBlend;
              m.velRot.roll = (m.velRot.roll * MOTION_PREDICTION.velSmoothing + vroll * (1 - MOTION_PREDICTION.velSmoothing)) * (1 - trendBlend) + 
                              velRotFromTrend.roll * trendBlend;

              // Scale velocity with DES trend
              const vs = (ultraSmoothScale.current - m.scale) / dt;
              const velScaleFromTrend = ctx.desTrend && ctx.desTrend.scale ? ctx.desTrend.scale / dt : 0;
              m.velScale = (m.velScale * MOTION_PREDICTION.velSmoothing + vs * (1 - MOTION_PREDICTION.velSmoothing)) * (1 - trendBlend) + 
                           velScaleFromTrend * trendBlend;
              
              // Calculate acceleration for curved motion prediction
              const accelPos = m.velPos.clone().sub(m.prevVelPos || new THREE.Vector3()).divideScalar(dt);
              const accelRot = {
                yaw: (m.velRot.yaw - (m.prevVelRot?.yaw || 0)) / dt,
                pitch: (m.velRot.pitch - (m.prevVelRot?.pitch || 0)) / dt,
                roll: (m.velRot.roll - (m.prevVelRot?.roll || 0)) / dt,
              };
              const accelScale = (m.velScale - (m.prevVelScale || 0)) / dt;
              
              // Store for next frame
              m.prevVelPos = m.velPos.clone();
              m.prevVelRot = { ...m.velRot };
              m.prevVelScale = m.velScale;
            } else {
              // Initialize acceleration tracking
              m.prevVelPos = new THREE.Vector3();
              m.prevVelRot = { yaw: 0, pitch: 0, roll: 0 };
              m.prevVelScale = 0;
            }

            // Update "current" snapshot (using ultra-smooth values)
            m.tMs = tMs;
            m.pos.copy(ultraSmoothPosition.current);
            m.rot = { ...ultraSmoothRotation.current };
            m.scale = ultraSmoothScale.current;

            // ============================================
            // ADAPTIVE LOOKAHEAD CALCULATION
            // ============================================
            // Base lookahead + velocity factor + inference compensation - jump penalty
            const maxLookaheadMs = vtoFlags.ultraLowLatency ? 66 : MOTION_PREDICTION.maxLookaheadMs;
            const maxLeadPos = vtoFlags.ultraLowLatency ? 0.12 : MOTION_PREDICTION.maxLead.pos;
            const maxLeadRot = vtoFlags.ultraLowLatency ? 0.18 : MOTION_PREDICTION.maxLead.rot;
            const maxLeadScale = vtoFlags.ultraLowLatency ? 0.14 : MOTION_PREDICTION.maxLead.scale;
            const baseLookahead = vtoFlags.ultraLowLatency ? 0 : MOTION_PREDICTION.baseLookaheadMs;
            const inferenceCompensation = (m.lastInferenceMs || 0) * (vtoFlags.ultraLowLatency ? 1.0 : 0.8);
            
            // Velocity factor: faster motion = more lookahead (up to 2x)
            const velocityFactor = Math.min(16, motionMagnitude * 10); // Up to 16ms additional
            
            // Jump penalty: reduce lookahead when jumps detected (unreliable prediction)
            const jumpPenalty = ctx.jumpSeverity === 'extreme' ? 20 :
                               ctx.jumpSeverity === 'large' ? 15 :
                               ctx.jumpSeverity === 'medium' ? 10 :
                               ctx.jumpSeverity === 'small' ? 5 : 0;
            
            // Calculate adaptive lookahead with prediction accuracy adjustment
            const predictionAdjustment = ctx.predictionAdjustmentFactor || 1.0;
            const adaptiveLookaheadMs = Math.min(
              maxLookaheadMs,
              Math.max(0, (baseLookahead + inferenceCompensation + velocityFactor - jumpPenalty) * predictionAdjustment)
            );
            const h = adaptiveLookaheadMs / 1000;

            // ============================================
            // ENHANCED PREDICTION WITH ACCELERATION
            // ============================================
            // Predict position: pos_predicted = pos_current + velocity * dt + 0.5 * acceleration * dt²
            const accelPos = m.prevVelPos ? 
              m.velPos.clone().sub(m.prevVelPos).divideScalar(dt) : 
              new THREE.Vector3();
            
            const posLead = m.velPos.clone().multiplyScalar(h)
              .add(accelPos.clone().multiplyScalar(0.5 * h * h)); // Add acceleration component
            
            // Clamp prediction
            if (posLead.length() < MOTION_PREDICTION.deadzone.pos) posLead.set(0, 0, 0);
            if (posLead.length() > maxLeadPos) posLead.setLength(maxLeadPos);

            // Rotation prediction with acceleration
            const accelRot = m.prevVelRot ? {
              yaw: (m.velRot.yaw - m.prevVelRot.yaw) / dt,
              pitch: (m.velRot.pitch - m.prevVelRot.pitch) / dt,
              roll: (m.velRot.roll - m.prevVelRot.roll) / dt,
            } : { yaw: 0, pitch: 0, roll: 0 };
            
            const yawLead = m.velRot.yaw * h + 0.5 * accelRot.yaw * h * h;
            const pitchLead = m.velRot.pitch * h + 0.5 * accelRot.pitch * h * h;
            const rollLead = m.velRot.roll * h + 0.5 * accelRot.roll * h * h;

            const clampLead = (v, max, dead) =>
              Math.abs(v) < dead ? 0 : THREE.MathUtils.clamp(v, -max, max);

            const ry = clampLead(yawLead, maxLeadRot, MOTION_PREDICTION.deadzone.rot);
            const rp = clampLead(pitchLead, maxLeadRot, MOTION_PREDICTION.deadzone.rot);
            const rr = clampLead(rollLead, maxLeadRot, MOTION_PREDICTION.deadzone.rot);

            // Scale prediction with acceleration
            const accelScale = m.prevVelScale ? (m.velScale - m.prevVelScale) / dt : 0;
            const scaleLead = clampLead(
              m.velScale * h + 0.5 * accelScale * h * h,
              maxLeadScale,
              MOTION_PREDICTION.deadzone.scale
            );

            // ============================================
            // JUMP-AWARE PREDICTION BLENDING
            // ============================================
            // When jump detected: reduce prediction (use current position)
            // When smooth motion: use full prediction (more aggressive)
            let predictionBlend = 1.0;
            if (ctx.jumpSeverity !== 'none') {
              // Reduce prediction when jumps detected
              predictionBlend = ctx.jumpSeverity === 'extreme' ? 0.0 :
                               ctx.jumpSeverity === 'large' ? 0.3 :
                               ctx.jumpSeverity === 'medium' ? 0.6 :
                               ctx.jumpSeverity === 'small' ? 0.8 : 1.0;
            } else if (motionState === 'fast' || motionState === 'rapid' || motionState === 'testing') {
              // Increase prediction for fast motion (more aggressive)
              predictionBlend = 1.2; // Can exceed 1.0 for more aggressive prediction
            }

            // Apply prediction with blending
            finalPos = m.pos.clone().add(posLead.clone().multiplyScalar(predictionBlend));
            finalRot = {
              yaw: m.rot.yaw + ry * predictionBlend,
              pitch: m.rot.pitch + rp * predictionBlend,
              roll: m.rot.roll + rr * predictionBlend,
            };
            finalScale = m.scale + scaleLead * predictionBlend;
            
            // ============================================
            // ZERO-LATENCY MODE (Lower threshold: 1.0)
            // ============================================
            // When motion magnitude > 1.0: use 100% predicted position for TRUE 0ms latency
            if (motionMagnitude >= 1.0) {
              // Bypass all smoothing, use predicted position directly
              finalPos = m.pos.clone().add(posLead);
              finalRot = {
                yaw: m.rot.yaw + ry,
                pitch: m.rot.pitch + rp,
                roll: m.rot.roll + rr,
              };
              finalScale = m.scale + scaleLead;
            }
            
            // Store predicted values for accuracy tracking (will be compared next frame)
            ctx.lastPredictedPosition = finalPos.clone();
            ctx.lastPredictedRotation = { ...finalRot };
            ctx.lastPredictedScale = finalScale;
            
            // ============================================
            // ADAPTIVE PREDICTION PARAMETER ADJUSTMENT
            // ============================================
            // Adjust prediction parameters based on accuracy
            // If prediction is inaccurate, reduce lookahead
            // If prediction is accurate, can increase lookahead slightly
            if (ctx.predictionAccuracy < 0.7 && MOTION_PREDICTION.enabled) {
              // Poor prediction: reduce lookahead for next frame
              // This will be applied in the next frame's adaptive lookahead calculation
              // We can adjust baseLookaheadMs dynamically, but for now we'll use a multiplier
              // Store adjustment factor in context
              ctx.predictionAdjustmentFactor = Math.max(0.7, ctx.predictionAccuracy);
            } else if (ctx.predictionAccuracy > 0.9) {
              // Excellent prediction: can be slightly more aggressive
              ctx.predictionAdjustmentFactor = Math.min(1.1, 1.0 + (ctx.predictionAccuracy - 0.9) * 0.5);
            } else {
              ctx.predictionAdjustmentFactor = 1.0;
            }
          } else {
            // Prediction disabled: clear predicted values
            ctx.lastPredictedPosition = null;
            ctx.lastPredictedRotation = null;
            ctx.lastPredictedScale = null;
          }

          */
          // Branch based on asset type: 2D or 3D model
          const is3DModel = currentAssetTypeRef.current === '3d' && glbModelRef.current && glassesGroup;
          
          if (is3DModel) {
            // ============================================
            // 3D MODEL POSITIONING LOGIC (Benson Ruan approach, adapted)
            // ============================================
            // Uses 3D face mesh coordinates directly for more natural 3D positioning
            // Adapted to our coordinate system with our existing smoothing system
            
            // Get 3D face mesh keypoints (Benson Ruan's landmarks)
            const pointMidEye = keypoints[168];      // Middle between eyes (anchor point)
            const pointLeftEye = keypoints[143];     // Left eye
            const pointRightEye = keypoints[372];    // Right eye  
            const pointNoseBottom = keypoints[2];    // Nose bottom (for up vector/tilt)
            
            // 1. POSITION: Use our existing position calculation, but add 3D Z depth
            const basePos3D = finalPos.clone();
            
            // Incorporate 3D depth from face mesh (pointMidEye[2] is Z in face mesh space)
            // Face mesh Z is typically in range -100 to +100, scale to world units
            const faceMeshZ = pointMidEye[2] || 0;
            const zDepthOffset = faceMeshZ * 0.001; // Scale face mesh Z to world units
            basePos3D.z += zDepthOffset; // Add 3D depth offset to our calculated Z
            
            // 2. UP VECTOR: From mid-eye → nose bottom (Benson Ruan approach)
            // This gives natural tilt based on head pitch
            const upVector = new THREE.Vector3(
              pointMidEye[0] - pointNoseBottom[0],
              -(pointMidEye[1] - pointNoseBottom[1]), // Flip Y (screen Y is inverted)
              (pointMidEye[2] || 0) - (pointNoseBottom[2] || 0)
            );
            upVector.normalize();
            
            // 3. SCALE: Use 3D eye distance (Benson Ruan approach)
            // Calculate 3D Euclidean distance between left and right eye
            const eyeDist3D = Math.sqrt(
              Math.pow(pointRightEye[0] - pointLeftEye[0], 2) +
              Math.pow(pointRightEye[1] - pointLeftEye[1], 2) +
              Math.pow((pointRightEye[2] || 0) - (pointLeftEye[2] || 0), 2)
            );
            
            // Convert 3D eye distance to scale factor
            // Blend our IPD-based scale (existing) with 3D eye distance scale
            const eyeDistPixels = eyeDist3D; // 3D distance in pixels
            const pixelsPerMmFrom3D = eyeDistPixels / AVERAGE_IPD_MM;
            const frameWidthMm = currentFrameWidthRef.current || 140;
            const targetGlassesPixels3D = frameWidthMm * pixelsPerMmFrom3D;
            const baseFramePixels = getBaseFramePixels(viewW, viewH);
            
            // Blend our IPD-based scale with 3D eye distance scale
            const scaleFrom3DEye = (targetGlassesPixels3D / baseFramePixels) * yawComp;
            const targetScale3D = finalScale * 0.85 + scaleFrom3DEye * 0.15; // Prefer IPD, blend with 3D
            
            // Apply position and scale (using our existing smoothed values)
            glassesGroup.position.copy(basePos3D);
            if (debugEnabled) {
              debugInfoRef.current = {
                modelId: selectedGlassesId,
                assetType: '3d',
                viewW,
                viewH,
                videoW,
                videoH,
                inferMs: trackerStateRef.current?.lastInferenceMs ?? null,
                inferBudgetMs: trackerStateRef.current?.trackingBudgetMs ?? null,
                inferPerfMul: trackerStateRef.current?.trackingPerfMul ?? null,
                inferLong: trackerStateRef.current?.trackingLong ?? null,
                trackFpsTarget: targetTrackFps,
                ipdPixels,
                pixelsPerMm,
                sizingMethod,
                faceWidthPixels,
                frameWidthMm,
                hingeXForSizing,
                handleWidthMm: currentHandleWidthRef.current || null,
                frontContentScaleX: currentFrontContentScaleXRef.current ?? null,
                scaleCorrection: currentScaleCorrectionRef.current ?? null,
                templeHingeOffset: currentHingeOffsetRef.current ?? null,
                templeYOffset: currentTempleYOffsetRef.current ?? null,
                templeLength: currentTempleLengthRef.current ?? null,
                baseFramePixels,
                targetGlassesPixels,
                yaw: faceRotation?.yaw,
                yawComp,
                targetScale,
                scaleFrom3DEye,
                targetScale3D,
                finalScale: targetScale3D,
                pdRaw: displayPD != null ? Math.round(displayPD) : null,
                pdStdDevMm,
                pdConfidence: pdConf,
                pdLocked: !!pdLockedRef.current,
                hasIrisData: !!hasValidIris,
              };
            }
            glassesGroup.scale.setScalar(targetScale3D);
            
            // 4. ROTATION: Use up vector for tilt (Benson Ruan approach)
            // rotation.y = Math.PI (face camera) + slight yaw adjustment
            const smoothedYaw = -finalRot.yaw;
            glassesGroup.rotation.y = Math.PI + smoothedYaw * 0.3; // Face camera + slight yaw following
            
            // rotation.z should follow head roll (tilt). Using finalRot.roll here keeps 3D and 2D consistent.
            const yawAbs = Math.abs(smoothedYaw);
            const smoothstep01 = (e0, e1, x) => {
              const t = THREE.MathUtils.clamp((x - e0) / Math.max(1e-6, (e1 - e0)), 0, 1);
              return t * t * (3 - 2 * t);
            };
            const rollDeadzone = THREE.MathUtils.degToRad(1.0);
            // Allow up to 50° tilt — user can lean significantly (was 28°, too restrictive).
            const rollMax = THREE.MathUtils.degToRad(50);
            const rollRaw = (finalRot.roll || 0);
            const rollDz = Math.abs(rollRaw) < rollDeadzone ? 0 : rollRaw;
            const rollClamped = THREE.MathUtils.clamp(rollDz, -rollMax, rollMax);
            const yawRollDamp = 1 - 0.25 * smoothstep01(Math.PI / 22, Math.PI / 5, yawAbs);
            const rollScale = 1.0; // full follow on all devices
            glassesGroup.rotation.z = rollClamped * yawRollDamp * rollScale;
            
            // rotation.x from pitch (head up/down) - blend with up vector
            const pitchFromUp = Math.asin(Math.max(-1, Math.min(1, -upVector.y)));
            glassesGroup.rotation.x = (finalRot.pitch * 0.5 + pitchFromUp * 0.5) * 0.5;
            
            // Ensure the 3D model is visible
            glbModelRef.current.visible = true;
          } else {
            // ============================================
            // 2D GLASSES POSITIONING LOGIC (UNCHANGED)
            // ============================================
            // Apply position and scale to the entire group
            glassesGroup.position.copy(finalPos);
            if (debugEnabled) {
              debugInfoRef.current = {
                modelId: selectedGlassesId,
                assetType: '2d',
                viewW,
                viewH,
                videoW,
                videoH,
                inferMs: trackerStateRef.current?.lastInferenceMs ?? null,
                inferBudgetMs: trackerStateRef.current?.trackingBudgetMs ?? null,
                inferPerfMul: trackerStateRef.current?.trackingPerfMul ?? null,
                inferLong: trackerStateRef.current?.trackingLong ?? null,
                trackFpsTarget: targetTrackFps,
                ipdPixels,
                pixelsPerMm,
                sizingMethod,
                faceWidthPixels,
                frameWidthMm,
                hingeXForSizing,
                handleWidthMm: currentHandleWidthRef.current || null,
                frontContentScaleX: currentFrontContentScaleXRef.current ?? null,
                scaleCorrection: currentScaleCorrectionRef.current ?? null,
                templeHingeOffset: currentHingeOffsetRef.current ?? null,
                templeYOffset: currentTempleYOffsetRef.current ?? null,
                templeLength: currentTempleLengthRef.current ?? null,
                baseFramePixels,
                targetGlassesPixels,
                yaw: faceRotation?.yaw,
                yawComp,
                targetScale,
                finalScale,
                pdRaw: displayPD != null ? Math.round(displayPD) : null,
                pdStdDevMm,
                pdConfidence: pdConf,
                pdLocked: !!pdLockedRef.current,
                hasIrisData: !!hasValidIris,
              };
            }
            glassesGroup.scale.setScalar(finalScale);

            // Apply rotations
            // Use smoothed yaw for rotation
            const smoothedYaw = -finalRot.yaw;
            const yawAbs = Math.abs(smoothedYaw);
            const smoothstep01 = (e0, e1, x) => {
              const t = THREE.MathUtils.clamp((x - e0) / Math.max(1e-6, (e1 - e0)), 0, 1);
              return t * t * (3 - 2 * t);
            };

            // Roll (tilt) affects the whole group.
            // MediaPipe roll can slightly over-tilt (especially when yaw is non-zero).
            // Apply a tiny deadzone + clamp and gently damp roll as yaw increases.
            const rollDeadzone = THREE.MathUtils.degToRad(1.0);
            // Allow up to 50° tilt — user can lean significantly (was 28°, too restrictive).
            const rollMax = THREE.MathUtils.degToRad(50);
            const rollRaw = (finalRot.roll || 0);
            const rollDz = Math.abs(rollRaw) < rollDeadzone ? 0 : rollRaw;
            const rollClamped = THREE.MathUtils.clamp(rollDz, -rollMax, rollMax);
            const yawRollDamp = 1 - 0.25 * smoothstep01(Math.PI / 22, Math.PI / 5, yawAbs); // ~8°→36° => 1 → 0.75
            const rollScale = 1.0; // full follow on all devices
            glassesGroup.rotation.z = rollClamped * yawRollDamp * rollScale;
            
            // Apply subtle yaw rotation to the frame for perspective
            // When yaw is large (profile), tracking yaw tends to overshoot slightly; damp it so glasses
            // don't rotate "too far" to the side while turning.
            const yawDamp = 1 - 0.18 * smoothstep01(Math.PI / 22, Math.PI / 5, yawAbs); // ~8°→36°
            glassesGroup.rotation.y = smoothedYaw * GLASSES_YAW_MULT * THREE.MathUtils.clamp(yawDamp, 0.82, 1.0);
            
            // Pitch (up/down tilt) affects the whole glasses frame
            const pitch = finalRot.pitch;
            glassesGroup.rotation.x = pitch * 0.5;

            // Get temple references
            const leftTemple = glassesGroup.getObjectByName('leftTemple');
            const rightTemple = glassesGroup.getObjectByName('rightTemple');

            if (leftTemple && rightTemple) {
              // Temples rotate around Y axis to follow face yaw
              // Base angles are opposite (-baseAngle vs +baseAngle), so the SAME
              // yaw coefficient produces opposite visual effects (one opens, one folds)
              
              // Base rotation - temples angled outward/back.
              // Mobile tends to lose the handle visually if we wrap too close to 90deg,
              // so keep a slightly smaller angle there for consistent visibility.
              const isMobile = getIsMobileDevice();
              const baseAngle = isMobile ? (Math.PI * 0.40) : (Math.PI * 0.46); // ~72° mobile, ~83° desktop

              const smoothstep = (e0, e1, x) => {
                const t = THREE.MathUtils.clamp((x - e0) / Math.max(1e-6, (e1 - e0)), 0, 1);
                return t * t * (3 - 2 * t);
              };

              // Determine which temple is "far" (should be behind face) based on yaw.
              // smoothedYaw > 0 => turning right => right temple is far side
              // smoothedYaw < 0 => turning left  => left  temple is far side
              const yawAbs = Math.abs(smoothedYaw);
              // "Looking straight" boost: when yaw ≈ 0, make temples more visible (slightly more open)
              // and thicker, then fade out smoothly as the user turns.
              const straightStart = Math.PI / 180; // ~1°
              const straightEnd = Math.PI / 10;    // ~18°
              const straightFactor = 1 - smoothstep(straightStart, straightEnd, yawAbs); // 1 at straight, 0 when turning
              // Mobile: yaw near 0 is noisy; add hysteresis so far/near doesn't swap every frame.
              const tState = templeStabilityRef.current;
              if (!tState.init) tState.rightIsFar = smoothedYaw > 0;
              if (isMobile) {
                const flipThreshold = THREE.MathUtils.degToRad(4.0);
                if (yawAbs > flipThreshold) tState.rightIsFar = smoothedYaw > 0;
              } else {
                tState.rightIsFar = smoothedYaw > 0;
              }
              const rightIsFar = tState.rightIsFar;
              const farTemple = rightIsFar ? rightTemple : leftTemple;
              const nearTemple = rightIsFar ? leftTemple : rightTemple;

              // Keep far temple somewhat visible (realistic), but let the face-occluder hide the overlap.
              const fadeStart = Math.PI / 30; // ~6°
              const fadeEnd = Math.PI / 6;    // ~30°
              const farFactor = smoothstep(fadeStart, fadeEnd, yawAbs);
              // Far temple should be subtly visible even straight-on, and never "fully vanish" on yaw.
              const farOpacityStraight = 0.6;
              const minFarOpacity = 0.35;
              const farOpacity = THREE.MathUtils.lerp(farOpacityStraight, minFarOpacity, farFactor);

              const setTempleRenderParams = (temple, { opacity, depthTest }) => {
                temple.visible = opacity > 0.03;
                temple.renderOrder = 2;
                // Cache meshes list to avoid per-frame traverse (mobile perf).
                if (!temple.userData._templeMeshes) {
                  const meshes = [];
                  temple.traverse((o) => {
                    if (o?.isMesh && o.material) meshes.push(o);
                  });
                  temple.userData._templeMeshes = meshes;
                }
                const meshes = temple.userData._templeMeshes || [];
                for (const o of meshes) {
                  o.renderOrder = 2;
                  // Only trigger a shader update once if transparent was previously false.
                  if (o.material.transparent !== true) {
                    o.material.transparent = true;
                    o.material.needsUpdate = true;
                  }
                  o.material.opacity = opacity;
                  o.material.depthTest = depthTest;
                  o.material.depthWrite = false;
                }
              };
              // Near temple should stay visible; far temple should respect face-occluder depth.
              // Frame overlays temples via renderOrder (z-index style), so near temple can ignore depth safely.
              setTempleRenderParams(nearTemple, { opacity: 1.0, depthTest: false });
              setTempleRenderParams(farTemple, { opacity: farOpacity, depthTest: true });

              // Pull the far temple slightly back so it's reliably behind the face occluder.
              const farPull = FAR_TEMPLE_Z_PULL_MAX * smoothstep(Math.PI / 36, Math.PI / 8, yawAbs);
              nearTemple.position.z = 0;
              farTemple.position.z = -farPull;

              // Update face occluder geometry (depth-only) in glassesGroup local space.
              const faceOccluder = faceOccluderRef.current;
              const facePosAttr = faceOccluderPosAttrRef.current;
              const faceIndexAttr = faceOccluderIndexAttrRef.current;
              if (faceOccluder && facePosAttr && currentAssetTypeRef.current === '2d') {
                const occlOn = yawAbs > Math.PI / 40; // ~4.5°
                faceOccluder.visible = occlOn;
                faceOccluder.renderOrder = 1;
                if (occlOn) {
                  faceOccluder.position.z = FACE_OCCLUDER_Z;
                  const s = FACE_OCCLUDER_SCALE_MIN + (FACE_OCCLUDER_SCALE_MAX - FACE_OCCLUDER_SCALE_MIN) * farFactor;
                  // Slightly wider than taller helps cover cheek/temple overlap.
                  faceOccluder.scale.set(s * 1.04, s * 0.98, 1);

                  // Use the current (smoothed) Z for best alignment with the displayed group.
                  const depthOccl = CAMERA_Z - (finalPos?.z ?? BASE_GLASSES_Z);
                  const pixelsToWorldOccl = (2 * tanHalfFov * depthOccl) / viewH;

                  const useFullMesh = facePosAttr.count >= FACEMESH_VERTEX_COUNT;
                  const count = useFullMesh ? FACEMESH_VERTEX_COUNT : FACE_OVAL_INDICES.length;
                  for (let i = 0; i < count; i++) {
                    const lmIdx = useFullMesh ? i : FACE_OVAL_INDICES[i];
                    const p = keypoints[lmIdx];
                    if (!p) continue;
                    const [vx, vy] = mapVideoPointToView(p, videoW, videoH, viewW, viewH, true, viewFit);
                    const wx = (vx - viewW / 2) * pixelsToWorldOccl + xYawOffset;
                    const wy = (vy - viewH / 2) * -pixelsToWorldOccl + NOSE_Y_OFFSET;
                    facePosAttr.setXYZ(i, wx - finalPos.x, wy - finalPos.y, 0);
                  }
                  facePosAttr.needsUpdate = true;

                  // Only re-triangulate when using the face-oval fallback.
                  if (!useFullMesh && faceIndexAttr && faceIndexAttr.array && (faceOccluderTriCounterRef.current++ % FACE_OCCLUDER_RETRIANGULATE_EVERY === 0)) {
                    try {
                      const contour = [];
                      for (let i = 0; i < FACE_OVAL_INDICES.length; i++) {
                        contour.push(new THREE.Vector2(facePosAttr.getX(i), facePosAttr.getY(i)));
                      }
                      const tris = THREE.ShapeUtils.triangulateShape(contour, []);
                      const arr = faceIndexAttr.array;
                      let k = 0;
                      for (let i = 0; i < tris.length; i++) {
                        const t = tris[i];
                        arr[k++] = t[0];
                        arr[k++] = t[1];
                        arr[k++] = t[2];
                      }
                      faceIndexAttr.needsUpdate = true;
                    } catch {
                      // ignore triangulation issues; keep previous indices
                    }
                  }
                }
              }
              
              // Temple scaling is inherited from parent glassesGroup (which has yawComp)
              // No additional local scaling needed - temples scale proportionally with frame
              
              // Temple hinge→ear constraint (cheap, stable):
              // Use ear landmarks to keep temples "tucked" and avoid intersecting the face on yaw.
              // We solve only for the temple yaw angle in XZ, plus a small vertical drop in Z-rotation.
              try {
                const leftEarKp = keypoints[127];
                const rightEarKp = keypoints[356];
                if (leftEarKp && rightEarKp) {
                  const depthNow = CAMERA_Z - (finalPos?.z ?? BASE_GLASSES_Z);
                  const pxToWorldNow = (2 * tanHalfFov * depthNow) / viewH;

                  // Convert a landmark into glassesGroup local coordinates.
                  // IMPORTANT: include glassesGroup rotation (roll) so ear vector doesn't "droop"
                  // when the user tilts their head left/right.
                  glassesGroup.updateMatrixWorld(true);
                  const inv = new THREE.Matrix4().copy(glassesGroup.matrixWorld).invert();
                  const toLocal = (kp) => {
                    const [vx, vy] = mapVideoPointToView(kp, videoW, videoH, viewW, viewH, true, 'cover');
                    const wx = (vx - viewW / 2) * pxToWorldNow + xYawOffset;
                    const wy = (vy - viewH / 2) * -pxToWorldNow + NOSE_Y_OFFSET;
                    const wp = new THREE.Vector3(wx, wy, finalPos?.z ?? BASE_GLASSES_Z);
                    return wp.applyMatrix4(inv);
                  };

                  const earA = toLocal(leftEarKp);
                  const earB = toLocal(rightEarKp);
                  const leftEarLocal = earA.x <= earB.x ? earA : earB;
                  const rightEarLocal = earA.x <= earB.x ? earB : earA;

                  // Estimate face height in local space so we can target slightly above the ear center.
                  let faceHLocal = 0;
                  try {
                    const foreheadKp = keypoints[10];
                    const chinKp = keypoints[152];
                    if (foreheadKp && chinKp) {
                      const foreheadLocal = toLocal(foreheadKp);
                      const chinLocal = toLocal(chinKp);
                      faceHLocal = Math.abs((chinLocal?.y ?? 0) - (foreheadLocal?.y ?? 0));
                    }
                  } catch {
                    faceHLocal = 0;
                  }

                  // "Above ear" target: bias ear point upward using a temple/upper-cheek landmark.
                  // Use indices 162 and 389 and assign left/right by X like we do for ears.
                  let leftAboveEarLocal = null;
                  let rightAboveEarLocal = null;
                  try {
                    const kpA = keypoints[162];
                    const kpB = keypoints[389];
                    if (kpA && kpB) {
                      const pA = toLocal(kpA);
                      const pB = toLocal(kpB);
                      leftAboveEarLocal = pA.x <= pB.x ? pA : pB;
                      rightAboveEarLocal = pA.x <= pB.x ? pB : pA;
                    }
                  } catch {
                    leftAboveEarLocal = null;
                    rightAboveEarLocal = null;
                  }

                  const getEarAimLocal = (earLocal, aboveLocal) => {
                    if (!earLocal) return new THREE.Vector3();
                    if (earLocal && aboveLocal) {
                      // Keep X anchored to the true ear (avoid yaw-dependent X drift),
                      // but let Y bias upward toward the above-ear landmark.
                      const aim = earLocal.clone();
                      aim.y = THREE.MathUtils.lerp(earLocal.y, aboveLocal.y, 0.9);
                      if (Number.isFinite(faceHLocal) && faceHLocal > 0) aim.y += faceHLocal * 0.04;
                      return aim;
                    }
                    const aim = earLocal.clone();
                    if (Number.isFinite(faceHLocal) && faceHLocal > 0) aim.y += faceHLocal * 0.14;
                    return aim;
                  };

                  const computeAngleForTemple = (temple, earLocal, isLeft) => {
                    const L = Math.max(1e-3, temple?.userData?.templeLengthWorld || 2.2);
                    // Target: just above ear line
                    const earAim = isLeft
                      ? getEarAimLocal(earLocal, leftAboveEarLocal)
                      : getEarAimLocal(earLocal, rightAboveEarLocal);
                    const dx = earAim.x - temple.position.x;
                    const outward = Math.max(1e-6, Math.abs(dx));
                    const ratio = THREE.MathUtils.clamp(outward / L, 0.05, 0.999);
                    const a = Math.acos(ratio);
                    const aClamped = THREE.MathUtils.clamp(a, Math.PI / 10, Math.PI / 2 - 0.03);

                    // Plausibility: the ear should be "outside" the hinge in X.
                    // If it isn't (landmark swap/noise), avoid snapping by reducing ear influence.
                    const isPlausible = isLeft ? (earAim.x <= temple.position.x - 0.02) : (earAim.x >= temple.position.x + 0.02);

                    const earBlendBase = smoothstep(
                      isMobile ? (Math.PI / 20) : (Math.PI / 30), // ~9° mobile, ~6° desktop
                      isMobile ? (Math.PI / 7) : (Math.PI / 10),  // ~25° mobile, ~18° desktop
                      yawAbs
                    ); // rely more as yaw increases
                    const earBlend = isPlausible ? earBlendBase : 0;

                    // Mobile: when looking straight, tuck temples closer to the head (not "open").
                    // Desktop behavior stays unchanged (slight open boost is fine there).
                    let baseAngleStraight = baseAngle;
                    if (isMobile) {
                      const tuckBoost = THREE.MathUtils.degToRad(10.0);
                      baseAngleStraight = baseAngle + tuckBoost * straightFactor;
                    } else {
                      const openBoost = THREE.MathUtils.degToRad(6.0);
                      baseAngleStraight = baseAngle - openBoost * straightFactor;
                    }

                    const angleY = THREE.MathUtils.lerp(baseAngleStraight, aClamped, earBlend);

                    // Vertical stability:
                    // Keep the temple Z rotation identical to the "looking straight" behavior
                    // so handles don't drift up/down while yawing.
                    const angleZ = 0;

                    return { angleY, angleZ };
                  };

                  const left = computeAngleForTemple(leftTemple, leftEarLocal, true);
                  const right = computeAngleForTemple(rightTemple, rightEarLocal, false);

                  // Mobile-only smoothing to avoid visible "flapping" when turning sideways.
                  // Also clamp per-frame angular velocity to prevent spikes from landmark noise.
                  const nowMs = performance.now();
                  const dt = tState.lastT ? Math.min(0.05, Math.max(0.001, (nowMs - tState.lastT) / 1000)) : (1 / 60);
                  tState.lastT = nowMs;
                  const tau = isMobile ? 0.10 : 0.05; // seconds
                  const alpha = dt / (tau + dt);
                  const maxTurnSpeed = isMobile ? THREE.MathUtils.degToRad(220) : THREE.MathUtils.degToRad(420); // rad/s
                  const maxDelta = maxTurnSpeed * dt;

                  const stepAngle = (prev, target) => {
                    if (!Number.isFinite(prev)) return target;
                    if (!Number.isFinite(target)) return prev;
                    const limited = THREE.MathUtils.clamp(target, prev - maxDelta, prev + maxDelta);
                    return prev + (limited - prev) * alpha;
                  };

                  if (!tState.init) {
                    tState.leftAngleY = left.angleY;
                    tState.rightAngleY = right.angleY;
                    tState.leftAngleZ = left.angleZ;
                    tState.rightAngleZ = right.angleZ;
                    tState.lengthScale = 1;
                    tState.init = true;
                  }

                  tState.leftAngleY = isMobile ? stepAngle(tState.leftAngleY, left.angleY) : left.angleY;
                  tState.rightAngleY = isMobile ? stepAngle(tState.rightAngleY, right.angleY) : right.angleY;
                  tState.leftAngleZ = isMobile ? stepAngle(tState.leftAngleZ, left.angleZ) : left.angleZ;
                  tState.rightAngleZ = isMobile ? stepAngle(tState.rightAngleZ, right.angleZ) : right.angleZ;

                  leftTemple.rotation.y = -tState.leftAngleY;
                  rightTemple.rotation.y = tState.rightAngleY;
                  leftTemple.rotation.z = tState.leftAngleZ;
                  rightTemple.rotation.z = tState.rightAngleZ;

                  // Thicker handles only when looking straight (depth/thickness in Z).
                  const thicknessZ = THREE.MathUtils.lerp(1.0, 1.9, straightFactor);
                  leftTemple.scale.z = thicknessZ;
                  rightTemple.scale.z = thicknessZ;

                  // "If it fits, it fits": do not artificially shorten temples on mobile.
                  leftTemple.scale.x = 1.0;
                  rightTemple.scale.x = 1.0;
                } else {
                  // Fallback (no ear landmarks): keep mostly open with slight yaw follow.
                  const yawFollow = smoothedYaw * 0.10;
                  leftTemple.rotation.y = -baseAngle - yawFollow;
                  rightTemple.rotation.y = baseAngle - yawFollow;
                  leftTemple.rotation.z = 0;
                  rightTemple.rotation.z = 0;
                  leftTemple.scale.z = 1.0;
                  rightTemple.scale.z = 1.0;
                  leftTemple.scale.x = 1.0;
                  rightTemple.scale.x = 1.0;
                }
              } catch {
                const yawFollow = smoothedYaw * 0.10;
                leftTemple.rotation.y = -baseAngle - yawFollow;
                rightTemple.rotation.y = baseAngle - yawFollow;
                leftTemple.rotation.z = 0;
                rightTemple.rotation.z = 0;
                leftTemple.scale.z = 1.0;
                rightTemple.scale.z = 1.0;
                leftTemple.scale.x = 1.0;
                rightTemple.scale.x = 1.0;
              }

              // Pitch adjustment - temples tilt with head
              leftTemple.rotation.x = pitch * 0.25;
              rightTemple.rotation.x = pitch * 0.25;
            }
          }
        } else {
          trackerMissRef.current += 1;
          if (trackerMissRef.current >= 4) {
            resetTrackingState({ hideModel: true });
          }
          if (noWebglFallbackRef.current && canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            if (ctx) ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          }
          // If MediaPipe Tasks is initialized but not detecting,
          // automatically fallback to TFJS after a short grace period.
          const boot = trackerBootRef.current || { t0: 0, fallbackTried: false };
          const now = performance.now();
          const isTasks = model?.kind === 'MediaPipeTasksFaceLandmarker';
          if (isTasks && !boot.fallbackTried && boot.t0 && (now - boot.t0) > 2500) {
            boot.fallbackTried = true;
            trackerBootRef.current = boot;
            try {
              const tfjsModel = await loadFaceTrackingModel({ prefer: 'tfjs', maxFaces: 1 });
              setModel(tfjsModel);
            } catch (e) {
              console.warn('[VTO] TFJS fallback failed:', e);
            }
          }
        }
        } catch (error) {
        console.error("Face detection error:", error);
        // If Tasks is erroring (common WebGL/WASM edge case), fallback to TFJS.
        try {
          const boot = trackerBootRef.current || { t0: 0, fallbackTried: false };
          const isTasks = model?.kind === 'MediaPipeTasksFaceLandmarker';
          if (isTasks && !boot.fallbackTried) {
            boot.fallbackTried = true;
            trackerBootRef.current = boot;
            const tfjsModel = await loadFaceTrackingModel({ prefer: 'tfjs', maxFaces: 1 });
            setModel(tfjsModel);
          }
        } catch (e) {
          console.warn('[VTO] TFJS fallback after error failed:', e);
        }
      }
      // Schedule next tracking tick with an upper FPS cap.
      const elapsed = performance.now() - loopStart;
      // If we're over budget, add a small backoff so we don't immediately re-run inference
      // and starve the main thread (important on low-end devices).
      // High-end: avoid the extra penalty so tracking stays as responsive as possible.
      const over = elapsed - targetTrackIntervalMs;
      const isHighEndMobileForSchedule = getIsMobileDevice() && !getIsLowEndMobile();
      const delayMs = isHighEndMobileForSchedule
        ? Math.max(0, targetTrackIntervalMs - elapsed)
        : (over > 0 ? Math.min(60, 4 + over * 0.6) : Math.max(0, targetTrackIntervalMs - elapsed));
      scheduleNext(delayMs);
    };

    // Start immediately.
    scheduleNext(0);

    return () => {
      cancelled = true;
      try {
        if (timeoutId != null) window.clearTimeout(timeoutId);
      } catch {
        // ignore
      }
      timeoutId = null;
    };
  // `faceShape` is used as a fallback while analysis is still running.
  }, [model, glassesGroup, faceShape, resetTrackingState]);

  // Animated "scan lines" overlay while face shape is being determined
  useEffect(() => {
    let rafId;
    let lastDrawT = 0;

    const draw = (t) => {
      // Mobile: throttle this cosmetic overlay to reduce jank.
      if (isMobile && t - lastDrawT < 33) { // ~30fps
        rafId = requestAnimationFrame(draw);
        return;
      }
      lastDrawT = t;
      const canvas = scanCanvasRef.current;
      const video = webcamRef.current?.video;
      const keypoints = lastKeypointsRef.current;

      if (!canvas) {
        rafId = requestAnimationFrame(draw);
        return;
      }

      const w = canvas.clientWidth || stageSize.w || 800;
      const h = canvas.clientHeight || stageSize.h || 600;
      const dpr = getEffectiveDpr();
      const scanDpr = isMobile ? Math.min(1.0, dpr) : dpr;
      const bw = Math.round(w * scanDpr);
      const bh = Math.round(h * scanDpr);
      if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw;
        canvas.height = bh;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        rafId = requestAnimationFrame(draw);
        return;
      }

      ctx.setTransform(scanDpr, 0, 0, scanDpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      // Once we lock (or if the overlay has expired), don't keep showing scan cues.
      if (faceShapeLockedRef.current && scanOverlayUntilRef.current) {
        scanOverlayUntilRef.current = 0;
      }

      // Only show scan animation briefly while not locked (and we have keypoints)
      if (video && keypoints && lastVideoDimsRef.current.w > 0) {
        if (!scanOverlayUntilRef.current) {
          // Show for ~1.6s after we first have landmarks.
          scanOverlayUntilRef.current = t + 1600;
        }
      }
      if (!faceShapeLockedRef.current && video && keypoints && lastVideoDimsRef.current.w > 0 && t < (scanOverlayUntilRef.current || 0)) {
        const vw = lastVideoDimsRef.current.w;
        const vh = lastVideoDimsRef.current.h;

        const map = (idx) => {
          const p = keypoints[idx];
          const fit = 'cover';
          const [x, y] = mapVideoPointToView(p, vw, vh, w, h, true, fit);
          return [x, y];
        };

        // Outline-ish path using stable anchor points
        const outlineIdx = [54, 234, 127, 152, 356, 454, 284, 10, 54];
        const outline = outlineIdx.map(map);

        // Animated dashed stroke
        const dashPhase = -(t * 0.03);
        ctx.save();
        ctx.lineWidth = 2.2;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.setLineDash([10, 10]);
        ctx.lineDashOffset = dashPhase;

        // Glow pass
        ctx.globalAlpha = 0.55;
        ctx.strokeStyle = 'rgba(56, 189, 248, 0.95)'; // cyan
        ctx.shadowColor = 'rgba(56, 189, 248, 0.7)';
        ctx.shadowBlur = 14;
        ctx.beginPath();
        ctx.moveTo(outline[0][0], outline[0][1]);
        for (let i = 1; i < outline.length; i++) ctx.lineTo(outline[i][0], outline[i][1]);
        ctx.stroke();

        // Crisp pass
        ctx.globalAlpha = 0.9;
        ctx.shadowBlur = 0;
        ctx.strokeStyle = 'rgba(167, 139, 250, 0.95)'; // purple
        ctx.beginPath();
        ctx.moveTo(outline[0][0], outline[0][1]);
        for (let i = 1; i < outline.length; i++) ctx.lineTo(outline[i][0], outline[i][1]);
        ctx.stroke();

        // Feature lines (nose + eye line)
        const nose = [6, 1].map(map);
        const eyeLine = [33, 263].map(map);
        ctx.setLineDash([6, 10]);
        ctx.lineDashOffset = dashPhase * 1.2;
        ctx.globalAlpha = 0.65;
        ctx.strokeStyle = 'rgba(34, 211, 238, 0.9)';
        ctx.beginPath();
        ctx.moveTo(nose[0][0], nose[0][1]);
        ctx.lineTo(nose[1][0], nose[1][1]);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(eyeLine[0][0], eyeLine[0][1]);
        ctx.lineTo(eyeLine[1][0], eyeLine[1][1]);
        ctx.stroke();

        // Scanning sweep overlay
        const sweepY = (Math.sin(t * 0.002) * 0.45 + 0.5) * h;
        const grad = ctx.createLinearGradient(0, sweepY - 60, 0, sweepY + 60);
        grad.addColorStop(0, 'rgba(56,189,248,0)');
        grad.addColorStop(0.5, 'rgba(56,189,248,0.10)');
        grad.addColorStop(1, 'rgba(56,189,248,0)');
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;
        ctx.fillStyle = grad;
        ctx.fillRect(0, sweepY - 60, w, 120);

        ctx.restore();
      }

      rafId = requestAnimationFrame(draw);
    };

    rafId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafId);
  }, [stageSize.w, stageSize.h, faceShape, getEffectiveDpr, isMobile]);

  // (mobile contain/blur approach removed - we render fullscreen cover like Jeeliz)

  // Video constraints for webcam - works on both desktop and mobile.
  //
  // On mobile: keep constraints MINIMAL so the browser picks the device's native mode (closest to the phone camera app).
  // Forcing aspect/size can cause sensor crop which feels like unwanted "zoom".
  const mobilePreferLowerRes = getIsLowEndMobile() || vtoFlags.ultraLowLatency || vtoFlags.fastTracking;
  const isLowEndMobile = getIsLowEndMobile();
  const mobileIdealW = isLowEndMobile ? 420 : (vtoFlags.ultraLowLatency ? 420 : 480);
  const mobileIdealH = isLowEndMobile ? 320 : (vtoFlags.ultraLowLatency ? 320 : 360);
  const videoConstraints = isMobile
    ? ({
        ...(mobilePreferLowerRes ? {
          width: { ideal: mobileIdealW },
          height: { ideal: mobileIdealH },
        } : {}),
        frameRate: vtoFlags.ultraLowLatency ? { ideal: 60, max: 60 } : { ideal: 30, max: 30 },
        facingMode: 'user',
      })
    : ({
        width: vtoFlags.ultraLowLatency ? { ideal: 560 } : { ideal: 800 },
        height: vtoFlags.ultraLowLatency ? { ideal: 420 } : { ideal: 600 },
        frameRate: vtoFlags.ultraLowLatency ? { ideal: 60, max: 60 } : { ideal: 30, max: 60 },
        facingMode: 'user',
      });

  // Camera ready: clear loader immediately; on mobile also reduce digital zoom if supported.
  const handleUserMedia = useCallback((stream) => {
    try {
      setIsLoading(false);

      if (!getIsMobileDevice()) return;
      const track = stream?.getVideoTracks?.()?.[0];
      if (!track?.applyConstraints) return;
      const caps = track.getCapabilities?.() || {};
      const z = caps?.zoom;
      if (typeof z?.min === 'number') {
        track.applyConstraints({ advanced: [{ zoom: z.min }] }).catch(() => {});
      }
    } catch {
      // ignore
    }
  }, []);

  function drawNoWebglFallback({ keypoints, videoW, videoH, viewW, viewH, isMirrored, viewFit }) {
    const overlay = canvasRef.current;
    if (!overlay || !keypoints) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const targetW = Math.max(1, Math.round(viewW * dpr));
    const targetH = Math.max(1, Math.round(viewH * dpr));
    if (overlay.width !== targetW || overlay.height !== targetH) {
      overlay.width = targetW;
      overlay.height = targetH;
    }

    const ctx = overlay.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, viewW, viewH);

    const glasses = getGlassesFromCatalog(selectedGlassesId);
    const src = glasses?.src;
    if (!src) return;

    if (fallbackFrameSrcRef.current !== src) {
      fallbackFrameSrcRef.current = src;
      fallbackFrameImageRef.current = null;
      const img = new Image();
      img.onload = () => {
        if (fallbackFrameSrcRef.current === src) fallbackFrameImageRef.current = img;
      };
      img.src = src;
      return;
    }

    const img = fallbackFrameImageRef.current;
    if (!img) return;

    const eyeCenter = keypoints[168] || keypoints[6];
    const noseBridge = keypoints[6] || eyeCenter;
    if (!eyeCenter || !noseBridge) return;

    const eyeV = mapVideoPointToView(eyeCenter, videoW, videoH, viewW, viewH, isMirrored, viewFit);
    const noseV = mapVideoPointToView(noseBridge, videoW, videoH, viewW, viewH, isMirrored, viewFit);
    const anchorX = eyeV[0] * (1 - NOSE_ANCHOR_BLEND) + noseV[0] * NOSE_ANCHOR_BLEND;
    const anchorY = eyeV[1] * (1 - NOSE_ANCHOR_BLEND) + noseV[1] * NOSE_ANCHOR_BLEND;

    let ipdPx = 0;
    if (keypoints[468] && keypoints[473]) {
      const l = mapVideoPointToView(keypoints[468], videoW, videoH, viewW, viewH, isMirrored, viewFit);
      const r = mapVideoPointToView(keypoints[473], videoW, videoH, viewW, viewH, isMirrored, viewFit);
      ipdPx = Math.hypot(r[0] - l[0], r[1] - l[1]);
    } else if (keypoints[133] && keypoints[362]) {
      const l = mapVideoPointToView(keypoints[133], videoW, videoH, viewW, viewH, isMirrored, viewFit);
      const r = mapVideoPointToView(keypoints[362], videoW, videoH, viewW, viewH, isMirrored, viewFit);
      ipdPx = Math.hypot(r[0] - l[0], r[1] - l[1]) * 1.97;
    }

    const frameWidthMm = currentFrameWidthRef.current || 140;
    const pixelsPerMm = ipdPx > 0 ? (ipdPx / AVERAGE_IPD_MM) : (viewW / 140);
    const contentScale = Math.max(0.25, Math.min(1, currentFrontContentScaleXRef.current || 1));
    const yawAbs = Math.abs(calculateFaceRotation(keypoints)?.yaw || 0);
    const yawComp = 1 / Math.max(0.62, Math.cos(Math.min(yawAbs, 1.2)));
    const drawW = Math.max(96, (frameWidthMm * pixelsPerMm * yawComp) / contentScale);
    const aspect = (img.naturalHeight || img.height || 1) / (img.naturalWidth || img.width || 1);
    const drawH = drawW * aspect;
    const roll = calculateFaceRotation(keypoints)?.roll || 0;

    ctx.save();
    ctx.translate(anchorX, anchorY);
    ctx.rotate(roll);
    ctx.globalAlpha = 0.98;
    ctx.drawImage(img, -drawW / 2, -drawH * 0.44, drawW, drawH);
    ctx.restore();
  }

  // MojeNaocale.ba brand color
  const brandColor = '#3894A8';
  const brandColorLight = '#4BA8BC';
  const brandColorDark = '#2D7A8A';

  // Reusable button styles
  const btnPrimary = {
    background: `linear-gradient(135deg, ${brandColor} 0%, ${brandColorDark} 100%)`,
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '12px 20px',
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: '0.02em',
    boxShadow: '0 4px 14px rgba(56, 148, 168, 0.35)',
    transition: 'all 150ms ease',
  };
  const btnSecondary = {
    background: 'rgba(255, 255, 255, 0.06)',
    color: '#e2e8f0',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 10,
    padding: '11px 18px',
    fontSize: 13,
    fontWeight: 500,
    transition: 'all 150ms ease',
  };
  const btnGhost = {
    background: 'transparent',
    color: '#94a3b8',
    border: 'none',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 500,
    transition: 'all 150ms ease',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a0a0f 0%, #12121a 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Global animation for fit toast */}
      <style>{`
        @keyframes fitToastSlideIn {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      {/* Subtle gradient orbs for depth */}
      <div style={{
        position: 'fixed',
        top: '-20%',
        right: '-10%',
        width: '50vw',
        height: '50vw',
        background: 'radial-gradient(circle, rgba(56, 148, 168, 0.1) 0%, transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />
      <div style={{
        position: 'fixed',
        bottom: '-30%',
        left: '-15%',
        width: '60vw',
        height: '60vw',
        background: 'radial-gradient(circle, rgba(56, 148, 168, 0.06) 0%, transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      {/* Mobile: FULL-SCREEN camera with slide-out menu */}
      {isMobile ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: 'calc(var(--app-vh, 1vh) * 100)',
            background: '#000',
            zIndex: 9999,
            touchAction: 'none',
            overscrollBehavior: 'none',
          }}
        >
          {/* Full-screen camera viewport */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Stage: dynamic canvas = viewport size, no CSS scale needed */}
            <div
              ref={stageRef}
              onTouchStart={handleSwipeTouchStart}
              onTouchEnd={handleSwipeTouchEnd}
              onMouseDown={handleSwipeMouseDown}
              onMouseUp={handleSwipeMouseUp}
              onMouseLeave={handleSwipeMouseLeave}
              style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                background: '#000',
                // No extra stage zoom on mobile; match device viewport size.
              }}
            >
            {showSwipeHint && !isLoading && activeCatalog.length > 1 && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.5) 40%, rgba(0,0,0,0.7) 100%)',
                  pointerEvents: 'none',
                }}
              >
                <style>{`
                  @keyframes swipeHintBounceM { 0%, 100% { transform: translateX(0); opacity: 0.9; } 25% { transform: translateX(-14px); opacity: 1; } 75% { transform: translateX(14px); opacity: 1; } }
                `}</style>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, animation: 'swipeHintBounceM 1.2s ease-in-out infinite' }}>
                  <span style={{ fontSize: 32, opacity: 0.9 }}>←</span>
                  <span style={{ fontSize: 32, opacity: 0.9 }}>→</span>
                </div>
                <div style={{ marginTop: 12, color: 'rgba(255,255,255,0.95)', fontSize: 15, fontWeight: 600, textAlign: 'center', textShadow: '0 1px 4px rgba(0,0,0,0.5)', padding: '0 24px' }}>
                  {t('swipeHint')}
                </div>
              </div>
            )}
            {isLoading && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 6,
                  pointerEvents: 'none',
                  background: 'linear-gradient(180deg, rgba(10, 12, 18, 0.92) 0%, rgba(6, 8, 14, 0.97) 100%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 16,
                }}
              >
                <div style={{ width: 40, height: 40, border: `3px solid rgba(56,148,168,0.2)`, borderTop: `3px solid ${brandColor}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <div style={{ color: '#94a3b8', fontSize: 13, fontWeight: 500 }}>{t('loadingCamera')}</div>
              </div>
            )}
            {(!isLoading && (!hasInitialGlassesLoaded || isGlassesAssetLoading)) && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 6,
                  pointerEvents: 'none',
                  overflow: 'hidden',
                  background: 'transparent',
                }}
              >
                <style>{`
                  @keyframes vtoScanBar {
                    0%   { top: -6px; }
                    100% { top: calc(100% + 6px); }
                  }
                  @keyframes vtoScanFade {
                    0%, 100% { opacity: 0.35; }
                    50% { opacity: 0.65; }
                  }
                `}</style>
                {/* Ping-pong scan line */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: '-6px',
                    height: 3,
                    background: `linear-gradient(90deg, transparent 0%, rgba(56,148,168,0.35) 20%, rgba(255,255,255,0.45) 50%, rgba(56,148,168,0.35) 80%, transparent 100%)`,
                    boxShadow: `0 0 12px 4px rgba(56,148,168,0.38), 0 0 24px 8px rgba(56,148,168,0.2)`,
                    filter: 'blur(3px)',
                    opacity: 0.75,
                    animation: 'vtoScanBar 1.4s cubic-bezier(0.4, 0, 0.6, 1) infinite alternate',
                    borderRadius: 4,
                  }}
                />
                {/* Status label */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 28,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    animation: 'vtoScanFade 1.4s ease-in-out infinite',
                  }}
                >
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 14px',
                    borderRadius: 999,
                    background: 'rgba(6, 10, 18, 0.5)',
                    border: `1px solid ${brandColor}2e`,
                    backdropFilter: 'blur(8px)',
                  }}>
                    <div style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: brandColor,
                      boxShadow: `0 0 6px 2px ${brandColor}`,
                      animation: 'vtoScanFade 0.8s ease-in-out infinite',
                    }} />
                    <span style={{ color: '#cbd5e1', fontSize: 12, fontWeight: 600, letterSpacing: '0.03em' }}>
                      {t('loadingGlasses') || 'Loading glasses...'}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div
              ref={cameraBoxRef}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                background: '#000',
              }}
            >
              <Webcam
                ref={webcamRef}
                autoPlay
                playsInline
                muted
                videoConstraints={videoConstraints}
                onUserMedia={handleUserMedia}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  backgroundColor: '#000',
                  zIndex: 1,
                }}
                mirrored={true}
                onUserMediaError={(err) => {
                  console.error('Webcam error:', err);
                  setIsLoading(false);
                }}
              />
              <canvas
                ref={scanCanvasRef}
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 2,
                  pointerEvents: 'none',
                }}
              />
              <canvas
                ref={canvasRef}
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 3,
                  pointerEvents: 'none',
                }}
              />
              <canvas
                ref={watermarkCanvasRef}
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 4,
                  pointerEvents: 'none',
                }}
              />
            </div>
            {debugEnabled && (
              <div
                style={{
                  position: 'absolute',
                  right: 12,
                  bottom: 12,
                  zIndex: 20,
                  padding: '10px 12px',
                  borderRadius: 12,
                  background: 'rgba(0,0,0,0.55)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  color: '#fff',
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  fontSize: 11,
                  lineHeight: 1.35,
                  maxWidth: 360,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ fontWeight: 800, letterSpacing: '0.06em' }}>VTO DEBUG</div>
                  <button
                    type="button"
                    onClick={toggleDebug}
                    style={{
                      marginLeft: 'auto',
                      background: 'rgba(255,255,255,0.10)',
                      border: '1px solid rgba(255,255,255,0.18)',
                      borderRadius: 10,
                      padding: '6px 8px',
                      color: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    Hide
                  </button>
                  <button
                    type="button"
                    onClick={downloadDiagnosticsJson}
                    style={{
                      background: 'rgba(56, 148, 168, 0.25)',
                      border: '1px solid rgba(56, 148, 168, 0.45)',
                      borderRadius: 10,
                      padding: '6px 8px',
                      color: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    Export
                  </button>
                </div>
                {debugSnapshot ? (
                  <>
                    <div>model: {String(debugSnapshot.modelId || '')}</div>
                    <div>asset: {String(debugSnapshot.assetType || '')}</div>
                    <div>view: {String(debugSnapshot.viewW ?? '')}x{String(debugSnapshot.viewH ?? '')}</div>
                    <div>video: {String(debugSnapshot.videoW ?? '')}x{String(debugSnapshot.videoH ?? '')}</div>
                    <div>inferMs: {debugSnapshot.inferMs?.toFixed?.(1) ?? String(debugSnapshot.inferMs ?? '')}</div>
                    <div>budgetMs: {debugSnapshot.inferBudgetMs?.toFixed?.(1) ?? String(debugSnapshot.inferBudgetMs ?? '')}</div>
                    <div>perfMul: {debugSnapshot.inferPerfMul?.toFixed?.(2) ?? String(debugSnapshot.inferPerfMul ?? '')}</div>
                    <div>LONG: {String(debugSnapshot.inferLong ?? '')}</div>
                    <div>trackFps: {String(debugSnapshot.trackFpsTarget ?? '')}</div>
                    <div>ipdPx: {debugSnapshot.ipdPixels?.toFixed?.(1) ?? String(debugSnapshot.ipdPixels ?? '')}</div>
                    <div>ppmm: {debugSnapshot.pixelsPerMm?.toFixed?.(3) ?? String(debugSnapshot.pixelsPerMm ?? '')}</div>
                    <div>frameMm: {String(debugSnapshot.frameWidthMm ?? '')}</div>
                    <div>basePx: {debugSnapshot.baseFramePixels?.toFixed?.(1) ?? String(debugSnapshot.baseFramePixels ?? '')}</div>
                    <div>targetPx: {debugSnapshot.targetGlassesPixels?.toFixed?.(1) ?? String(debugSnapshot.targetGlassesPixels ?? '')}</div>
                    <div>hingeX: {debugSnapshot.templeHingeOffset?.toFixed?.(3) ?? String(debugSnapshot.templeHingeOffset ?? '')}</div>
                    <div>hingeSizeX: {debugSnapshot.hingeXForSizing?.toFixed?.(3) ?? String(debugSnapshot.hingeXForSizing ?? '')}</div>
                    <div>contentX: {debugSnapshot.frontContentScaleX?.toFixed?.(3) ?? String(debugSnapshot.frontContentScaleX ?? '')}</div>
                    <div>scaleCorr: {debugSnapshot.scaleCorrection?.toFixed?.(3) ?? String(debugSnapshot.scaleCorrection ?? '')}</div>
                    <div>yaw: {debugSnapshot.yaw?.toFixed?.(3) ?? String(debugSnapshot.yaw ?? '')}</div>
                    <div>yawComp: {debugSnapshot.yawComp?.toFixed?.(3) ?? String(debugSnapshot.yawComp ?? '')}</div>
                    <div>scaleTarget: {debugSnapshot.targetScale?.toFixed?.(4) ?? String(debugSnapshot.targetScale ?? '')}</div>
                    <div>scaleFinal: {debugSnapshot.finalScale?.toFixed?.(4) ?? String(debugSnapshot.finalScale ?? '')}</div>
                    <div>pdRaw: {String(debugSnapshot.pdRaw ?? '')}</div>
                    <div>pdStd: {debugSnapshot.pdStdDevMm?.toFixed?.(2) ?? String(debugSnapshot.pdStdDevMm ?? '')}</div>
                    <div>pdConf: {debugSnapshot.pdConfidence?.toFixed?.(2) ?? String(debugSnapshot.pdConfidence ?? '')}</div>
                    <div>pdLocked: {String(debugSnapshot.pdLocked ?? '')}</div>
                    <div>iris: {String(debugSnapshot.hasIrisData ?? '')}</div>
                  </>
                ) : (
                  <div style={{ color: 'rgba(255,255,255,0.75)' }}>Waiting for diagnostics…</div>
                )}
              </div>
            )}
            {frozenStageDataUrl && (
              <img
                src={frozenStageDataUrl}
                alt="frozen"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 10,
                  pointerEvents: 'none',
                }}
              />
            )}

            <MediaPreviewModal
              open={previewModal.open}
              onClose={closePreviewModal}
              title={previewModal.title}
              dataUrl={previewModal.dataUrl}
              videoUrl={previewModal.videoUrl}
              primaryLabel={previewPrimary.label}
              onPrimary={previewPrimary.onPrimary}
              primaryDisabled={previewPrimary.disabled}
              tertiaryLabel={previewModal.dataUrl && !previewModal.videoUrl ? "Uporedi sa drugim modelom" : null}
              tertiaryDisabled={!activeSelfieId}
              onTertiary={previewModal.dataUrl && !previewModal.videoUrl ? openComparePickerFromPreview : null}
              secondaryLabel="Nastavi"
              onContinue={closePreviewModal}
              mobile={true}
            />

            {/* Compare Modal (screenshots only) */}
            <CompareModal
              open={compareOpen}
              onClose={closeCompareModal}
              a={compareAId ? getGlassesFromCatalog(compareAId) : null}
              b={compareBId ? getGlassesFromCatalog(compareBId) : null}
              aSnapshot={compareRenders.a ? { dataUrl: compareRenders.a } : null}
              bSnapshot={compareRenders.b ? { dataUrl: compareRenders.b } : null}
              combinedSnapshot={compareRenders.combined ? { dataUrl: compareRenders.combined } : null}
              loading={compareRenders.loading}
              mobile={true}
            />

            {/* Compare Picker Modal (mobile: fullwidth, no border/radius) */}
            {comparePicker.open && (
              <div
                role="dialog"
                aria-modal="true"
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0, 0, 0, 0.82)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  zIndex: 100001,
                  display: 'flex',
                  alignItems: 'stretch',
                  justifyContent: 'stretch',
                  padding: 0,
                }}
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) closeComparePicker();
                }}
              >
                <div
                  style={{
                    width: '100%',
                    maxHeight: '100%',
                    overflow: 'auto',
                    borderRadius: 0,
                    border: 'none',
                    background: 'linear-gradient(180deg, #16161e 0%, #0e0e14 100%)',
                    boxShadow: 'none',
                    color: '#fff',
                  }}
                >
                  <div
                    style={{
                      padding: '18px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#f8fafc' }}>Odaberite model za usporedbu</div>
                    <button
                      type="button"
                      onClick={closeComparePicker}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        color: '#94a3b8',
                        fontSize: 18,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                      aria-label="Zatvori"
                    >
                      ×
                    </button>
                  </div>

                  <div style={{ padding: 16 }}>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
                      Trenutno: <b>{getGlassesFromCatalog(comparePicker.aId)?.name || 'Nije odabrano'}</b>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(activeCatalog || [])
                        .filter((g) => g && g.id !== comparePicker.aId)
                        .map((g) => (
                          <button
                            key={g.id}
                            type="button"
                            onClick={() => {
                              const aId = comparePicker.aId || selectedGlassesId;
                              const bId = g.id;
                              setComparePicker({ open: false, aId: null });
                              setCompareAId(aId);
                              setCompareBId(bId);
                              doOpenCompare(aId, bId);
                            }}
                            style={{
                              borderRadius: 12,
                              border: '1px solid rgba(255,255,255,0.10)',
                              background: 'rgba(255,255,255,0.02)',
                              color: '#fff',
                              padding: '12px 14px',
                              textAlign: 'left',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              cursor: 'pointer',
                            }}
                          >
                            <div
                              style={{
                                width: 56,
                                height: 42,
                                borderRadius: 8,
                                background: 'rgba(255,255,255,0.04)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                flex: '0 0 auto',
                              }}
                            >
                              <img
                                src={g.src}
                                alt={g.name}
                                loading="lazy"
                                decoding="async"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: '#f8fafc',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {g.name}
                              </div>
                            </div>
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Gallery Modal (mobile: fullwidth, no border/radius) */}
            {galleryOpen && (
              <div
                role="dialog"
                aria-modal="true"
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0, 0, 0, 0.9)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  zIndex: 100002,
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  borderRadius: 0,
                  border: 'none',
                  animation: 'fadeIn 0.2s ease-out',
                }}
              >
                {/* Header */}
                <div style={{
                  padding: '16px 20px',
                  paddingTop: 'calc(16px + env(safe-area-inset-top))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                }}>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>Moja Galerija</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      Pohranjeno lokalno u vašem pregledniku
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setGalleryOpen(false)}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#94a3b8',
                      fontSize: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Tabs */}
                <div style={{
                  display: 'flex',
                  gap: 8,
                  padding: '12px 20px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                }}>
                  <button
                    type="button"
                    onClick={() => setGalleryTab('photos')}
                    style={{
                      padding: '10px 18px',
                      borderRadius: 10,
                      border: 'none',
                      background: galleryTab === 'photos' ? brandColor : 'rgba(255, 255, 255, 0.06)',
                      color: galleryTab === 'photos' ? '#fff' : '#94a3b8',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                    Fotografije ({selfies.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setGalleryTab('videos')}
                    style={{
                      padding: '10px 18px',
                      borderRadius: 10,
                      border: 'none',
                      background: galleryTab === 'videos' ? brandColor : 'rgba(255, 255, 255, 0.06)',
                      color: galleryTab === 'videos' ? '#fff' : '#94a3b8',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 7l-7 5 7 5V7z"/>
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                    </svg>
                    {t('video')} ({videoClips.length})
                  </button>
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
                  {galleryTab === 'photos' && (
                    <>
                      {selfies.length === 0 ? (
                        <div style={{
                          textAlign: 'center',
                          padding: '60px 20px',
                          color: '#64748b',
                        }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}>
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21 15 16 10 5 21"/>
                          </svg>
                          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Nemate fotografija</div>
                          <div style={{ fontSize: 13 }}>Uslikajte svoj prvi izgled pritiskom na gumb kamere</div>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                          {selfies.map((s) => (
                            <div key={s.id} style={{ position: 'relative' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setGalleryOpen(false);
                                  setActiveSelfieId(s.id);
                                  openSelfiePreview(s);
                                }}
                                style={{
                                  width: '100%',
                                  padding: 0,
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  borderRadius: 14,
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  background: '#000',
                                }}
                              >
                                <img
                                  src={s.compositeDataUrl || s.baseFrameDataUrl}
                                  alt="fotografija"
                                  style={{ width: '100%', aspectRatio: '4/3', objectFit: 'contain', display: 'block' }}
                                />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const next = removeSelfie(s.id);
                                  setSelfies(next);
                                  if (activeSelfieId === s.id) setActiveSelfieId(next[0]?.id || null);
                                }}
                                style={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  width: 32,
                                  height: 32,
                                  borderRadius: 10,
                                  background: 'rgba(0, 0, 0, 0.7)',
                                  border: '1px solid rgba(255, 255, 255, 0.15)',
                                  color: '#ef4444',
                                  fontSize: 14,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                }}
                                title="Obriši"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {selfies.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm('Jeste li sigurni da želite obrisati sve fotografije?')) {
                              setSelfies(clearSelfies());
                              setActiveSelfieId(null);
                            }
                          }}
                          style={{
                            marginTop: 20,
                            width: '100%',
                            padding: '14px 20px',
                            borderRadius: 12,
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            background: 'rgba(239, 68, 68, 0.1)',
                            color: '#ef4444',
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Obriši sve fotografije
                        </button>
                      )}
                    </>
                  )}

                  {galleryTab === 'videos' && (
                    <>
                      {videoClips.length === 0 ? (
                        <div style={{
                          textAlign: 'center',
                          padding: '60px 20px',
                          color: '#64748b',
                        }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}>
                            <path d="M23 7l-7 5 7 5V7z"/>
                            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                          </svg>
                          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Nemate videa</div>
                          <div style={{ fontSize: 13 }}>Držite gumb kamere 1-6 sekundi za snimanje videa</div>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                          {videoClips.map((c) => {
                            const thumb = clipThumbs?.[c.id] || null;
                            return (
                              <div key={c.id} style={{ position: 'relative' }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setGalleryOpen(false);
                                    setActiveClipId(c.id);
                                    openClipPreview(c);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: 0,
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: 14,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    background: '#000',
                                  }}
                                >
                                  {thumb ? (
                                    <img
                                      src={thumb}
                                      alt="video"
                                      style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }}
                                    />
                                  ) : (
                                    <div style={{ width: '100%', aspectRatio: '4/3', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                                      Učitavam...
                                    </div>
                                  )}
                                </button>
                                <div style={{
                                  position: 'absolute',
                                  bottom: 8,
                                  left: 8,
                                  padding: '4px 10px',
                                  borderRadius: 8,
                                  background: 'rgba(0, 0, 0, 0.7)',
                                  color: '#fff',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                    <polygon points="5 3 19 12 5 21 5 3"/>
                                  </svg>
                                  {Math.round((c.durationMs || 0) / 1000)}s
                                </div>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      await deleteVideoClip(c.id);
                                      const next = await loadVideoClips();
                                      setVideoClips(next);
                                      if (activeClipId === c.id) setActiveClipId(next[0]?.id || null);
                                    } catch {
                                      // ignore
                                    }
                                  }}
                                  style={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    width: 32,
                                    height: 32,
                                    borderRadius: 10,
                                    background: 'rgba(0, 0, 0, 0.7)',
                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                    color: '#ef4444',
                                    fontSize: 14,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                  }}
                                  title="Obriši"
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6"/>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                  </svg>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {videoClips.length > 0 && (
                        <button
                          type="button"
                          onClick={async () => {
                            if (window.confirm('Jeste li sigurni da želite obrisati sve videe?')) {
                              for (const c of videoClips) {
                                await deleteVideoClip(c.id);
                              }
                              setVideoClips([]);
                              setActiveClipId(null);
                            }
                          }}
                          style={{
                            marginTop: 20,
                            width: '100%',
                            padding: '14px 20px',
                            borderRadius: 12,
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            background: 'rgba(239, 68, 68, 0.1)',
                            color: '#ef4444',
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Obriši sve videe
                        </button>
                      )}
                    </>
                  )}
                </div>

                {/* Footer info */}
                <div style={{
                  padding: '14px 20px',
                  paddingBottom: 'calc(14px + env(safe-area-inset-bottom))',
                  borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                  background: 'rgba(0, 0, 0, 0.4)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    <span style={{ fontSize: 12, color: '#64748b' }}>
                      Sve fotografije i videi su pohranjeni samo na vašem uređaju. Ne šaljemo podatke na server.
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Corner Info Widget - Face Shape + Dimensions (mobile) */}
            {(() => {
              const selectedGlasses = getGlassesFromCatalog(selectedGlassesId);
              const modelName = selectedGlasses?.name ? String(selectedGlasses.name) : '';
              const toMm = (v) => {
                const n = Number(v);
                if (!Number.isFinite(n) || n <= 0) return null;
                return Math.round(n);
              };
              const toTempleMm = (v) => {
                const n = Number(v);
                if (!Number.isFinite(n) || n <= 0) return null;
                // Legacy: some catalogs store temple length in "world units" (~3 == 140mm).
                if (n < 50) return Math.round((n / 3) * FRAME_REFERENCE_MM);
                return Math.round(n);
              };
              const frameW = toMm(selectedGlasses?.frameWidth);
              const templeL = toTempleMm(selectedGlasses?.handleWidth ?? selectedGlasses?.templeLength);
              const bridgeFromCm = selectedGlasses?.bridgeLengthCm != null
                ? toMm(Number(selectedGlasses.bridgeLengthCm) * 10)
                : null;
              const bridgeW = bridgeFromCm ?? toMm(selectedGlasses?.bridgeWidth ?? selectedGlasses?.bridge);
              const dimsRows = [];
              if (frameW) dimsRows.push({ key: 'frame', label: 'Širina', value: `${frameW}mm` });
              if (templeL) dimsRows.push({ key: 'temple', label: 'Ručke', value: `${templeL}mm` });
              if (bridgeW) dimsRows.push({ key: 'bridge', label: 'Most', value: `${bridgeW}mm` });
              const showFaceShape = !!faceShape;
              const show = showFaceShape || !!modelName || dimsRows.length > 0;
              if (!show) return null;
              const iconStyle = { width: 14, height: 14, flexShrink: 0, opacity: 0.9, display: 'block' };
              const iconColor = 'rgba(255,255,255,0.78)';
              const renderIcon = (key) => {
                if (key === 'frame') {
                  // Ruler
                  return (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle} aria-hidden>
                      <path d="M4 7h16v10H4z" />
                      <path d="M8 7v4" />
                      <path d="M12 7v2" />
                      <path d="M16 7v4" />
                    </svg>
                  );
                }
                if (key === 'temple') {
                  // Temple arm
                  return (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle} aria-hidden>
                      <path d="M4 12h10" />
                      <path d="M14 12c3 0 6 2 6 5" />
                      <path d="M20 17v3" />
                    </svg>
                  );
                }
                // Bridge
                return (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle} aria-hidden>
                    <path d="M6 14c2-3 4-4 6-4s4 1 6 4" />
                    <path d="M6 14v3" />
                    <path d="M18 14v3" />
                  </svg>
                );
              };
              return (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(16px + env(safe-area-inset-top))',
                  left: 'calc(16px + env(safe-area-inset-left))',
                  padding: '10px 14px',
                  borderRadius: 14,
                  background: 'rgba(0, 0, 0, 0.5)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  color: '#fff',
                  zIndex: 50,
                  pointerEvents: 'none',
                }}
              >
                {/* Face shape + active model name (same row) */}
                {(showFaceShape || modelName) && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      minWidth: 0,
                    }}
                  >
                    {showFaceShape && (
                      <>
                        <img src={iconFaceShape} alt="" style={{ width: 16, height: 16, objectFit: 'contain', flexShrink: 0, filter: 'brightness(0) invert(0.75)' }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#fff', flexShrink: 0 }}>
                          {t('faceShape_' + (faceShape || 'oval'))}
                        </span>
                      </>
                    )}
                    {modelName && (
                      <div
                        style={{
                          marginLeft: showFaceShape ? 4 : 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '6px 10px',
                          borderRadius: 14,
                          background: 'rgba(255,255,255,0.10)',
                          border: '1px solid rgba(255,255,255,0.12)',
                          color: '#e2e8f0',
                          boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
                          maxWidth: 220,
                          fontSize: 13,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          minWidth: 0,
                        }}
                      >
                        {modelName}
                      </div>
                    )}
                  </div>
                )}

                {/* Dimensions row under face shape */}
                {dimsRows.length > 0 && (
                  <div
                    style={{
                      marginTop: (showFaceShape || modelName) ? 8 : 0,
                      paddingTop: (showFaceShape || modelName) ? 8 : 0,
                      borderTop: (showFaceShape || modelName) ? '1px solid rgba(255,255,255,0.10)' : 'none',
                      fontSize: 11,
                      color: 'rgba(255,255,255,0.75)',
                      lineHeight: 1.25,
                      maxWidth: 220,
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {dimsRows.map((r) => (
                        <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: iconColor, display: 'inline-flex', alignItems: 'center' }}>
                            {renderIcon(r.key)}
                          </span>
                          <span style={{ minWidth: 56, color: 'rgba(255,255,255,0.70)', fontWeight: 600 }}>
                            {r.label}
                          </span>
                          <span style={{ color: 'rgba(255,255,255,0.92)', fontWeight: 700 }}>
                            {r.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              );
            })()}
            </div>
          </div>

          {/* Horizontal glasses strip (mobile only): centered selection, transparent arrows, swipe hint + icon animation */}
          {showCatalog && compareCatalog.length > 1 && (() => {
            const selectedIdx = compareCatalog.findIndex((g) => g.id === selectedGlassesId);
            return (
            <div
              style={{
                position: 'absolute',
                bottom: 'calc(90px + env(safe-area-inset-bottom))',
                left: 0,
                right: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                zIndex: 50,
              }}
            >
              <style>{`
                .glasses-strip::-webkit-scrollbar { display: none; }
                @keyframes stripSwipeHint {
                  0%, 100% { transform: translateX(0); }
                  35% { transform: translateX(-10px); }
                  65% { transform: translateX(10px); }
                }
              `}</style>
              {showStripSwipeHint && !isLoading && (
                <div
                  role="status"
                  aria-live="polite"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 6,
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      animation: 'stripSwipeHint 1.4s ease-in-out 3 both',
                      animationDelay: '0.6s',
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9 }}>
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9 }}>
                      <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                  </div>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', textAlign: 'center', padding: '0 16px' }}>
                    Swipe lijevo/desno da mijenjaš modele
                  </span>
                </div>
              )}
              <div
                className="glasses-strip-outer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '8px 8px 12px',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
              <button
                type="button"
                onClick={() => {
                  if (selectedIdx <= 0) return;
                  const prev = compareCatalog[selectedIdx - 1];
                  setSelectedGlassesId(prev.id);
                  if (comparingWithSelfieId) showToast(prev.name, 'info', 1200, 'top');
                  setShowStripSwipeHint(false);
                }}
                disabled={selectedIdx <= 0}
                style={{
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  border: 'none',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#e2e8f0',
                  cursor: 'pointer',
                  opacity: selectedIdx <= 0 ? 0.35 : 1,
                }}
                aria-label="Prethodni model"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <div
                ref={glassesStripRef}
                className="glasses-strip"
                style={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 64,
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  WebkitOverflowScrolling: 'touch',
                  scrollSnapType: 'x mandatory',
                  scrollBehavior: 'smooth',
                  padding: '4px 0',
                  boxSizing: 'content-box',
                  maskImage: 'linear-gradient(to right, transparent 0, black 24px, black calc(100% - 24px), transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(to right, transparent 0, black 24px, black calc(100% - 24px), transparent 100%)',
                  maskSize: '100% 100%',
                  maskRepeat: 'no-repeat',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '0 calc(50% - 32px)',
                    width: 'max-content',
                    minWidth: '100%',
                    minHeight: 62,
                    boxSizing: 'border-box',
                  }}
                >
                  {compareCatalog.map((g, i) => {
                    const active = g.id === selectedGlassesId;
                    return (
                      <LazyReveal key={g.id} eager={i < catalogRevealCount} rootMargin="200px" placeholder={<div style={{ flex: '0 0 auto', width: 64, height: 52, borderRadius: 14, background: 'rgba(255,255,255,0.04)' }} />}>
                      <button
                        type="button"
                        data-glasses-id={g.id}
                        onClick={() => {
                          setSelectedGlassesId(g.id);
                          setShowStripSwipeHint(false);
                          if (comparingWithSelfieId) {
                            showToast(g.name, 'info', 1200, 'top');
                          }
                        }}
                        style={{
                          flex: '0 0 auto',
                          width: 64,
                          height: 52,
                          borderRadius: 14,
                          border: active ? `2px solid ${brandColor}` : '1px solid rgba(255,255,255,0.15)',
                          background: active ? 'rgba(56,148,168,0.25)' : 'rgba(0,0,0,0.4)',
                          backdropFilter: 'blur(10px)',
                          WebkitBackdropFilter: 'blur(10px)',
                          padding: 6,
                          transform: active ? 'scale(1.08)' : 'scale(1)',
                          transition: 'transform 150ms ease, border-color 150ms ease, box-shadow 150ms ease',
                          scrollSnapAlign: 'center',
                          boxShadow: active ? '0 4px 16px rgba(56,148,168,0.35)' : '0 2px 8px rgba(0,0,0,0.2)',
                          cursor: 'pointer',
                          position: 'relative',
                        }}
                      >
                        <LazyImageWithFallback
                          src={hasInitialGlassesLoaded ? g.src : undefined}
                          alt=""
                          containerStyle={{
                            width: '100%',
                            height: '100%',
                          }}
                          imgStyle={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            filter: active ? 'none' : 'brightness(0.85)',
                          }}
                        />
                      </button>
                      </LazyReveal>
                    );
                  })}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (selectedIdx === -1 || selectedIdx >= compareCatalog.length - 1) return;
                  const next = compareCatalog[selectedIdx + 1];
                  setSelectedGlassesId(next.id);
                  if (comparingWithSelfieId) showToast(next.name, 'info', 1200, 'top');
                  setShowStripSwipeHint(false);
                }}
                disabled={selectedIdx >= compareCatalog.length - 1}
                style={{
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  border: 'none',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#e2e8f0',
                  cursor: 'pointer',
                  opacity: selectedIdx >= compareCatalog.length - 1 ? 0.35 : 1,
                }}
                aria-label="Sljedeći model"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              </button>
              </div>
            </div>
            );
          })()}

          {/* Gallery button (bottom-left) – 100% round, multimedia icon */}
          <button
            type="button"
            onClick={() => setGalleryOpen(true)}
            style={{
              position: 'absolute',
              bottom: 'calc(36px + env(safe-area-inset-bottom))',
              left: 'calc(24px + env(safe-area-inset-left))',
              zIndex: 100,
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0,
              cursor: 'pointer',
            }}
          >
            <img src={iconMultimedia} alt="Galerija" style={{ width: 22, height: 22, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
            {(selfies.length + videoClips.length) > 0 && (
              <span style={{ position: 'absolute', top: 2, right: 2, fontSize: 10, fontWeight: 700, color: '#fff', minWidth: 16, height: 16, borderRadius: 8, background: 'rgba(56,148,168,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {selfies.length + videoClips.length}
              </span>
            )}
          </button>

          {/* Add to Cart button (bottom-right) */}
          {(() => {
            const currentGlasses = getGlassesFromCatalog(selectedGlassesId);
            const cartUrl = currentGlasses?.addToCartUrl;
            if (!cartUrl) return null;

            return (
              <button
                type="button"
                onClick={() => {
                  window.open(cartUrl, '_blank', 'noopener,noreferrer');
                }}
                style={{
                  position: 'absolute',
                  bottom: 'calc(36px + env(safe-area-inset-bottom))',
                  right: 'calc(24px + env(safe-area-inset-right))',
                  zIndex: 100,
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(72, 133, 237, 0.9) 0%, rgba(55, 108, 204, 0.9) 100%)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(72, 133, 237, 0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0,
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(72, 133, 237, 0.4)',
                  transition: 'all 150ms ease',
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.95)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <img src={iconCart} alt={t('addToCart')} style={{ width: 22, height: 22, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
              </button>
            );
          })()}

          {/* Clip-on toggle button */}
          {(() => {
            const currentGlasses = getGlassesFromCatalog(selectedGlassesId);
            if (!currentGlasses?.clipOnSrc) return null;
            return (
              <button
                type="button"
                onClick={() => { setUseClipOn(prev => !prev); }}
                title={useClipOn ? 'Show original frame' : 'Show clip-on'}
                style={{
                  position: 'absolute',
                  top: 'calc(160px + env(safe-area-inset-top))',
                  left: 'calc(16px + env(safe-area-inset-left))',
                  zIndex: 100,
                  height: 36,
                  borderRadius: 18,
                  padding: '0 14px',
                  width: 'auto',
                  background: useClipOn
                    ? 'linear-gradient(135deg, rgba(251,191,36,0.9) 0%, rgba(217,119,6,0.9) 100%)'
                    : 'rgba(0,0,0,0.5)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: useClipOn
                    ? '1px solid rgba(251,191,36,0.4)'
                    : '1px solid rgba(255,255,255,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  boxShadow: useClipOn ? '0 4px 16px rgba(251,191,36,0.4)' : 'none',
                  transition: 'all 150ms ease',
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{ fontSize: 14, color: '#fff', fontWeight: 600, letterSpacing: '0.01em' }}>
                  {useClipOn ? '✕ Clip-on' : '✦ Clip-on'}
                </span>
              </button>
            );
          })()}

          {/* Floating shutter button (bottom-center) with long-press recording */}
          <div
            style={{
              position: 'absolute',
              bottom: 'calc(24px + env(safe-area-inset-bottom))',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 100,
            }}
          >
            {/* SVG Progress Ring */}
            <svg
              viewBox="0 0 100 100"
              style={{
                position: 'absolute',
                top: -8,
                left: -8,
                width: 88,
                height: 88,
                transform: 'rotate(-90deg)',
                pointerEvents: 'none',
              }}
            >
              {/* Background circle */}
              <circle
                cx="50"
                cy="50"
                r="46"
                fill="none"
                stroke="rgba(255,255,255,0.15)"
                strokeWidth="4"
              />
              {/* Progress circle */}
              <circle
                cx="50"
                cy="50"
                r="46"
                fill="none"
                stroke={brandColor}
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={`${recordingProgress * 2.89} 289`}
                style={{
                  transition: 'stroke-dasharray 50ms linear',
                  filter: recordingProgress > 0 ? 'drop-shadow(0 0 4px rgba(56, 148, 168, 0.6))' : 'none',
                }}
              />
            </svg>
            
            <button
              type="button"
              onPointerDown={handleShutterPointerDown}
              onPointerUp={handleShutterPointerUp}
              onPointerLeave={handleShutterPointerLeave}
              onPointerCancel={handleShutterPointerLeave}
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: isLongPressing
                  ? 'rgba(239, 68, 68, 0.25)'
                  : comparingWithSelfieId
                    ? 'rgba(251, 146, 60, 0.25)'
                    : 'rgba(255, 255, 255, 0.15)',
                border: isLongPressing
                  ? '4px solid rgba(239, 68, 68, 0.9)'
                  : comparingWithSelfieId
                    ? '4px solid rgba(251, 146, 60, 0.95)'
                    : '4px solid rgba(255, 255, 255, 0.9)',
                boxShadow: comparingWithSelfieId
                  ? '0 4px 20px rgba(251, 146, 60, 0.4), inset 0 0 20px rgba(251, 146, 60, 0.1)'
                  : '0 4px 20px rgba(0, 0, 0, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                transition: 'all 150ms ease',
                touchAction: 'none',
              }}
            >
              {comparingWithSelfieId ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(251, 146, 60, 0.95)" strokeWidth="2.5">
                    <rect x="3" y="3" width="7" height="18" />
                    <rect x="14" y="3" width="7" height="18" />
                  </svg>
                  <span style={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: 'rgba(251, 146, 60, 0.95)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>Uporedi</span>
                </div>
              ) : (
                <div style={{
                  width: isLongPressing ? 28 : 54,
                  height: isLongPressing ? 28 : 54,
                  borderRadius: isLongPressing ? 6 : '50%',
                  background: isLongPressing ? 'rgba(239, 68, 68, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                  transition: 'all 150ms ease',
                }} />
              )}
            </button>
          </div>
        </div>
      ) : (
        /* Desktop: Premium layout */
        <div 
          style={{ 
            position: 'relative',
            zIndex: 1,
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            padding: embedded ? '0' : '24px 32px',
          }}
        >
          {/* Main content */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: 24,
              maxWidth: 1400,
              margin: '0 auto',
              width: '100%',
              padding: embedded ? 0 : undefined,
            }}
          >
            {/* Left: Camera + Controls */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Camera viewport */}
              <div
                ref={stageRef}
                onTouchStart={handleSwipeTouchStart}
                onTouchEnd={handleSwipeTouchEnd}
                onMouseDown={handleSwipeMouseDown}
                onMouseUp={handleSwipeMouseUp}
                onMouseLeave={handleSwipeMouseLeave}
                style={{
                  position: 'relative',
                  width: `${stageSize.w}px`,
                  height: `${stageSize.h}px`,
                  overflow: 'hidden',
                  borderRadius: 20,
                  background: '#000',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255,255,255,0.06)',
                }}
              >
        {showSwipeHint && !isLoading && activeCatalog.length > 1 && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.5) 40%, rgba(0,0,0,0.7) 100%)',
              pointerEvents: 'none',
              borderRadius: 20,
            }}
          >
            <style>{`
              @keyframes swipeHintBounce { 0%, 100% { transform: translateX(0); opacity: 0.9; } 25% { transform: translateX(-12px); opacity: 1; } 75% { transform: translateX(12px); opacity: 1; } }
            `}</style>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, animation: 'swipeHintBounce 1.2s ease-in-out infinite' }}>
              <span style={{ fontSize: 28, opacity: 0.9 }}>←</span>
              <span style={{ fontSize: 28, opacity: 0.9 }}>→</span>
            </div>
            <div style={{ marginTop: 10, color: 'rgba(255,255,255,0.95)', fontSize: 14, fontWeight: 600, textAlign: 'center', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
              {t('swipeHint')}
            </div>
          </div>
        )}
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 6,
              pointerEvents: 'none',
              background: 'linear-gradient(180deg, rgba(10, 12, 18, 0.92) 0%, rgba(6, 8, 14, 0.97) 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 16,
            }}
          >
            <div style={{ width: 40, height: 40, border: `3px solid rgba(56,148,168,0.2)`, borderTop: `3px solid ${brandColor}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <div style={{ color: '#94a3b8', fontSize: 13, fontWeight: 500 }}>{t('loadingCamera')}</div>
          </div>
        )}
        {(!isLoading && (!hasInitialGlassesLoaded || isGlassesAssetLoading)) && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 6,
              pointerEvents: 'none',
              overflow: 'hidden',
              background: 'transparent',
            }}
          >
            <style>{`
              @keyframes vtoScanBarD {
                0%   { top: -6px; }
                100% { top: calc(100% + 6px); }
              }
              @keyframes vtoScanFadeD {
                0%, 100% { opacity: 0.35; }
                50% { opacity: 0.65; }
              }
            `}</style>
            {/* Ping-pong scan line */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '-6px',
                height: 3,
                background: `linear-gradient(90deg, transparent 0%, rgba(56,148,168,0.35) 20%, rgba(255,255,255,0.45) 50%, rgba(56,148,168,0.35) 80%, transparent 100%)`,
                boxShadow: `0 0 12px 4px rgba(56,148,168,0.38), 0 0 24px 8px rgba(56,148,168,0.2)`,
                filter: 'blur(3px)',
                opacity: 0.75,
                animation: 'vtoScanBarD 1.4s cubic-bezier(0.4, 0, 0.6, 1) infinite alternate',
                borderRadius: 4,
              }}
            />
            {/* Status label */}
            <div
              style={{
                position: 'absolute',
                bottom: 28,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                animation: 'vtoScanFadeD 1.4s ease-in-out infinite',
              }}
            >
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 14px',
                borderRadius: 999,
                background: 'rgba(6, 10, 18, 0.5)',
                border: `1px solid ${brandColor}2e`,
                backdropFilter: 'blur(8px)',
              }}>
                <div style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: brandColor,
                  boxShadow: `0 0 6px 2px ${brandColor}`,
                  animation: 'vtoScanFadeD 0.8s ease-in-out infinite',
                }} />
                <span style={{ color: '#cbd5e1', fontSize: 13, fontWeight: 600, letterSpacing: '0.03em' }}>
                  {t('loadingGlasses') || 'Loading glasses...'}
                </span>
              </div>
            </div>
          </div>
        )}
        <Webcam 
          ref={webcamRef} 
          autoPlay 
          playsInline
          muted
          videoConstraints={videoConstraints}
          onUserMedia={handleUserMedia}
          style={{ 
            width: '100%', 
            height: '100%',
            objectFit: 'cover'
          }} 
          mirrored={true}
          onUserMediaError={(err) => {
            console.error('Webcam error:', err);
            setIsLoading(false);
          }}
        />
        <canvas
          ref={scanCanvasRef}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
        <canvas 
          ref={canvasRef} 
          style={{ 
            width: '100%', 
            height: '100%', 
            position: 'absolute', 
            top: 0, 
            left: 0,
            zIndex: 2,
            pointerEvents: 'none'
          }} 
        />
        <canvas
          ref={watermarkCanvasRef}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 3,
            pointerEvents: 'none',
          }}
        />
        {debugEnabled && (
          <div
            style={{
              position: 'absolute',
              right: 12,
              bottom: 12,
              zIndex: 20,
              padding: '10px 12px',
              borderRadius: 12,
              background: 'rgba(0,0,0,0.55)',
              border: '1px solid rgba(255,255,255,0.14)',
              color: '#fff',
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              fontSize: 11,
              lineHeight: 1.35,
              maxWidth: 360,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ fontWeight: 800, letterSpacing: '0.06em' }}>VTO DEBUG</div>
              <button
                type="button"
                onClick={toggleDebug}
                style={{
                  marginLeft: 'auto',
                  background: 'rgba(255,255,255,0.10)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  borderRadius: 10,
                  padding: '6px 8px',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                Hide
              </button>
              <button
                type="button"
                onClick={downloadDiagnosticsJson}
                style={{
                  background: 'rgba(56, 148, 168, 0.25)',
                  border: '1px solid rgba(56, 148, 168, 0.45)',
                  borderRadius: 10,
                  padding: '6px 8px',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                Export
              </button>
            </div>
            {debugSnapshot ? (
              <>
                <div>model: {String(debugSnapshot.modelId || '')}</div>
                <div>asset: {String(debugSnapshot.assetType || '')}</div>
                <div>view: {String(debugSnapshot.viewW ?? '')}x{String(debugSnapshot.viewH ?? '')}</div>
                <div>video: {String(debugSnapshot.videoW ?? '')}x{String(debugSnapshot.videoH ?? '')}</div>
                <div>inferMs: {debugSnapshot.inferMs?.toFixed?.(1) ?? String(debugSnapshot.inferMs ?? '')}</div>
                <div>budgetMs: {debugSnapshot.inferBudgetMs?.toFixed?.(1) ?? String(debugSnapshot.inferBudgetMs ?? '')}</div>
                <div>perfMul: {debugSnapshot.inferPerfMul?.toFixed?.(2) ?? String(debugSnapshot.inferPerfMul ?? '')}</div>
                <div>LONG: {String(debugSnapshot.inferLong ?? '')}</div>
                <div>trackFps: {String(debugSnapshot.trackFpsTarget ?? '')}</div>
                <div>ipdPx: {debugSnapshot.ipdPixels?.toFixed?.(1) ?? String(debugSnapshot.ipdPixels ?? '')}</div>
                <div>ppmm: {debugSnapshot.pixelsPerMm?.toFixed?.(3) ?? String(debugSnapshot.pixelsPerMm ?? '')}</div>
                <div>frameMm: {String(debugSnapshot.frameWidthMm ?? '')}</div>
                <div>basePx: {debugSnapshot.baseFramePixels?.toFixed?.(1) ?? String(debugSnapshot.baseFramePixels ?? '')}</div>
                <div>targetPx: {debugSnapshot.targetGlassesPixels?.toFixed?.(1) ?? String(debugSnapshot.targetGlassesPixels ?? '')}</div>
                <div>hingeX: {debugSnapshot.templeHingeOffset?.toFixed?.(3) ?? String(debugSnapshot.templeHingeOffset ?? '')}</div>
                <div>hingeSizeX: {debugSnapshot.hingeXForSizing?.toFixed?.(3) ?? String(debugSnapshot.hingeXForSizing ?? '')}</div>
                <div>contentX: {debugSnapshot.frontContentScaleX?.toFixed?.(3) ?? String(debugSnapshot.frontContentScaleX ?? '')}</div>
                <div>scaleCorr: {debugSnapshot.scaleCorrection?.toFixed?.(3) ?? String(debugSnapshot.scaleCorrection ?? '')}</div>
                <div>yaw: {debugSnapshot.yaw?.toFixed?.(3) ?? String(debugSnapshot.yaw ?? '')}</div>
                <div>yawComp: {debugSnapshot.yawComp?.toFixed?.(3) ?? String(debugSnapshot.yawComp ?? '')}</div>
                <div>scaleTarget: {debugSnapshot.targetScale?.toFixed?.(4) ?? String(debugSnapshot.targetScale ?? '')}</div>
                <div>scaleFinal: {debugSnapshot.finalScale?.toFixed?.(4) ?? String(debugSnapshot.finalScale ?? '')}</div>
                <div>pdRaw: {String(debugSnapshot.pdRaw ?? '')}</div>
                <div>pdStd: {debugSnapshot.pdStdDevMm?.toFixed?.(2) ?? String(debugSnapshot.pdStdDevMm ?? '')}</div>
                <div>pdConf: {debugSnapshot.pdConfidence?.toFixed?.(2) ?? String(debugSnapshot.pdConfidence ?? '')}</div>
                <div>pdLocked: {String(debugSnapshot.pdLocked ?? '')}</div>
                <div>iris: {String(debugSnapshot.hasIrisData ?? '')}</div>
              </>
            ) : (
              <div style={{ color: 'rgba(255,255,255,0.75)' }}>Waiting for diagnostics…</div>
            )}
          </div>
        )}
        {frozenStageDataUrl && (
          <img
            src={frozenStageDataUrl}
            alt="frozen"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 10,
              pointerEvents: 'none',
            }}
          />
        )}

        <MediaPreviewModal
          open={previewModal.open}
          onClose={closePreviewModal}
          title={previewModal.title}
          dataUrl={previewModal.dataUrl}
          videoUrl={previewModal.videoUrl}
          primaryLabel={previewPrimary.label}
          onPrimary={previewPrimary.onPrimary}
          primaryDisabled={previewPrimary.disabled}
          tertiaryLabel={previewModal.dataUrl && !previewModal.videoUrl ? "Uporedi sa drugim modelom" : null}
          tertiaryDisabled={!activeSelfieId}
          onTertiary={previewModal.dataUrl && !previewModal.videoUrl ? openComparePickerFromPreview : null}
          secondaryLabel="Nastavi"
          onContinue={closePreviewModal}
        />

        {/* Compare Modal (screenshots only - desktop) */}
        <CompareModal
          open={compareOpen}
          onClose={closeCompareModal}
          a={compareAId ? getGlassesFromCatalog(compareAId) : null}
          b={compareBId ? getGlassesFromCatalog(compareBId) : null}
          aSnapshot={compareRenders.a ? { dataUrl: compareRenders.a } : null}
          bSnapshot={compareRenders.b ? { dataUrl: compareRenders.b } : null}
          combinedSnapshot={compareRenders.combined ? { dataUrl: compareRenders.combined } : null}
          loading={compareRenders.loading}
        />

        {/* Compare Picker Modal (desktop) */}
        {comparePicker.open && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.82)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              zIndex: 100001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 18,
            }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeComparePicker();
            }}
          >
            <div
              style={{
                width: 'min(500px, 100%)',
                maxHeight: '80vh',
                overflow: 'auto',
                borderRadius: 22,
                background: 'linear-gradient(180deg, #16161e 0%, #0e0e14 100%)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: '0 40px 100px rgba(0, 0, 0, 0.6)',
                color: '#fff',
              }}
            >
              <div
                style={{
                  padding: '18px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 800, color: '#f8fafc' }}>Odaberite model za usporedbu</div>
                <button
                  type="button"
                  onClick={closeComparePicker}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    color: '#94a3b8',
                    fontSize: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                  aria-label="Zatvori"
                >
                  ×
                </button>
              </div>

              <div style={{ padding: 16 }}>
                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
                  Trenutno: <b>{getGlassesFromCatalog(comparePicker.aId)?.name || 'Nije odabrano'}</b>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                  {(activeCatalog || [])
                    .filter((g) => g && g.id !== comparePicker.aId)
                    .map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => {
                          const aId = comparePicker.aId || selectedGlassesId;
                          const bId = g.id;
                          setComparePicker({ open: false, aId: null });
                          setCompareAId(aId);
                          setCompareBId(bId);
                          doOpenCompare(aId, bId);
                        }}
                        style={{
                          borderRadius: 12,
                          border: '1px solid rgba(255,255,255,0.10)',
                          background: 'rgba(255,255,255,0.02)',
                          color: '#fff',
                          padding: '12px 14px',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          cursor: 'pointer',
                        }}
                      >
                        <div
                          style={{
                            width: 56,
                            height: 42,
                            borderRadius: 8,
                            background: 'rgba(255,255,255,0.04)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flex: '0 0 auto',
                            position: 'relative',
                          }}
                        >
                          <LazyImageWithFallback
                            src={g.src}
                            alt={g.name}
                            containerStyle={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            imgStyle={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: '#f8fafc',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {g.name}
                          </div>
                        </div>
                      </button>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gallery Modal (desktop) */}
        {galleryOpen && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.85)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              zIndex: 100002,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 40,
              animation: 'fadeIn 0.2s ease-out',
            }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setGalleryOpen(false);
            }}
          >
            <div
              style={{
                width: 'min(900px, 100%)',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 24,
                background: 'linear-gradient(180deg, #16161e 0%, #0e0e14 100%)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: '0 40px 100px rgba(0, 0, 0, 0.6)',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <div style={{
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
              }}>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>Moja Galerija</div>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                    Fotografije i videi pohranjeni lokalno u vašem pregledniku
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setGalleryOpen(false)}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#94a3b8',
                    fontSize: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
              </div>

              {/* Tabs */}
              <div style={{
                display: 'flex',
                gap: 10,
                padding: '16px 24px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
              }}>
                <button
                  type="button"
                  onClick={() => setGalleryTab('photos')}
                  style={{
                    padding: '12px 20px',
                    borderRadius: 12,
                    border: 'none',
                    background: galleryTab === 'photos' ? brandColor : 'rgba(255, 255, 255, 0.06)',
                    color: galleryTab === 'photos' ? '#fff' : '#94a3b8',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                  Fotografije ({selfies.length})
                </button>
                <button
                  type="button"
                  onClick={() => setGalleryTab('videos')}
                  style={{
                    padding: '12px 20px',
                    borderRadius: 12,
                    border: 'none',
                    background: galleryTab === 'videos' ? brandColor : 'rgba(255, 255, 255, 0.06)',
                    color: galleryTab === 'videos' ? '#fff' : '#94a3b8',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 7l-7 5 7 5V7z"/>
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                  </svg>
                  {t('video')} ({videoClips.length})
                </button>
              </div>

              {/* Content */}
              <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
                {galleryTab === 'photos' && (
                  <>
                    {selfies.length === 0 ? (
                      <div style={{
                        textAlign: 'center',
                        padding: '80px 40px',
                        color: '#64748b',
                      }}>
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 20 }}>
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5"/>
                          <polyline points="21 15 16 10 5 21"/>
                        </svg>
                        <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 10 }}>Nemate fotografija</div>
                        <div style={{ fontSize: 14 }}>Uslikajte svoj prvi izgled pritiskom na gumb kamere</div>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                        {selfies.map((s) => (
                          <div key={s.id} style={{ position: 'relative' }}>
                            <button
                              type="button"
                              onClick={() => {
                                setGalleryOpen(false);
                                setActiveSelfieId(s.id);
                                openSelfiePreview(s);
                              }}
                              style={{
                                width: '100%',
                                padding: 0,
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: 16,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                background: '#000',
                              }}
                            >
                              <img
                                src={s.compositeDataUrl || s.baseFrameDataUrl}
                                alt="fotografija"
                                style={{ width: '100%', aspectRatio: '4/3', objectFit: 'contain', display: 'block' }}
                              />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const next = removeSelfie(s.id);
                                setSelfies(next);
                                if (activeSelfieId === s.id) setActiveSelfieId(next[0]?.id || null);
                              }}
                              style={{
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                width: 36,
                                height: 36,
                                borderRadius: 12,
                                background: 'rgba(0, 0, 0, 0.75)',
                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                color: '#ef4444',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                              }}
                              title="Obriši"
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {selfies.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm('Jeste li sigurni da želite obrisati sve fotografije?')) {
                            setSelfies(clearSelfies());
                            setActiveSelfieId(null);
                          }
                        }}
                        style={{
                          marginTop: 24,
                          padding: '14px 24px',
                          borderRadius: 12,
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          background: 'rgba(239, 68, 68, 0.1)',
                          color: '#ef4444',
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Obriši sve fotografije
                      </button>
                    )}
                  </>
                )}

                {galleryTab === 'videos' && (
                  <>
                    {videoClips.length === 0 ? (
                      <div style={{
                        textAlign: 'center',
                        padding: '80px 40px',
                        color: '#64748b',
                      }}>
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 20 }}>
                          <path d="M23 7l-7 5 7 5V7z"/>
                          <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                        </svg>
                        <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 10 }}>Nemate videa</div>
                        <div style={{ fontSize: 14 }}>Držite gumb kamere 1-6 sekundi za snimanje videa</div>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                        {videoClips.map((c) => {
                          const thumb = clipThumbs?.[c.id] || null;
                          return (
                            <div key={c.id} style={{ position: 'relative' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setGalleryOpen(false);
                                  setActiveClipId(c.id);
                                  openClipPreview(c);
                                }}
                                style={{
                                  width: '100%',
                                  padding: 0,
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  borderRadius: 16,
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  background: '#000',
                                }}
                              >
                                {thumb ? (
                                  <img
                                    src={thumb}
                                    alt="video"
                                    style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }}
                                  />
                                ) : (
                                  <div style={{ width: '100%', aspectRatio: '4/3', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                                    Učitavam...
                                  </div>
                                )}
                              </button>
                              <div style={{
                                position: 'absolute',
                                bottom: 10,
                                left: 10,
                                padding: '5px 12px',
                                borderRadius: 10,
                                background: 'rgba(0, 0, 0, 0.75)',
                                color: '#fff',
                                fontSize: 12,
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 5,
                              }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                  <polygon points="5 3 19 12 5 21 5 3"/>
                                </svg>
                                {Math.round((c.durationMs || 0) / 1000)}s
                              </div>
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    await deleteVideoClip(c.id);
                                    const next = await loadVideoClips();
                                    setVideoClips(next);
                                    if (activeClipId === c.id) setActiveClipId(next[0]?.id || null);
                                  } catch {
                                    // ignore
                                  }
                                }}
                                style={{
                                  position: 'absolute',
                                  top: 10,
                                  right: 10,
                                  width: 36,
                                  height: 36,
                                  borderRadius: 12,
                                  background: 'rgba(0, 0, 0, 0.75)',
                                  border: '1px solid rgba(255, 255, 255, 0.15)',
                                  color: '#ef4444',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                }}
                                title="Obriši"
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {videoClips.length > 0 && (
                      <button
                        type="button"
                        onClick={async () => {
                          if (window.confirm('Jeste li sigurni da želite obrisati sve videe?')) {
                            for (const c of videoClips) {
                              await deleteVideoClip(c.id);
                            }
                            setVideoClips([]);
                            setActiveClipId(null);
                          }
                        }}
                        style={{
                          marginTop: 24,
                          padding: '14px 24px',
                          borderRadius: 12,
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          background: 'rgba(239, 68, 68, 0.1)',
                          color: '#ef4444',
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Obriši sve videe
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Footer info */}
              <div style={{
                padding: '16px 24px',
                borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                background: 'rgba(0, 0, 0, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <span style={{ fontSize: 13, color: '#64748b' }}>
                  Sve fotografije i videi su pohranjeni samo na vašem uređaju. Ne šaljemo podatke na server.
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Corner Info Widget - Face Shape + Model + Dimensions (desktop) */}
        {(() => {
          const selectedGlasses = getGlassesFromCatalog(selectedGlassesId);
          const modelName = selectedGlasses?.name ? String(selectedGlasses.name) : '';
          const toMm = (v) => {
            const n = Number(v);
            if (!Number.isFinite(n) || n <= 0) return null;
            return Math.round(n);
          };
          const toTempleMm = (v) => {
            const n = Number(v);
            if (!Number.isFinite(n) || n <= 0) return null;
            // Legacy: some catalogs store temple length in "world units" (~3 == 140mm).
            if (n < 50) return Math.round((n / 3) * FRAME_REFERENCE_MM);
            return Math.round(n);
          };
          const frameW = toMm(selectedGlasses?.frameWidth);
          const templeL = toTempleMm(selectedGlasses?.handleWidth ?? selectedGlasses?.templeLength);
          const bridgeFromCm = selectedGlasses?.bridgeLengthCm != null
            ? toMm(Number(selectedGlasses.bridgeLengthCm) * 10)
            : null;
          const bridgeW = bridgeFromCm ?? toMm(selectedGlasses?.bridgeWidth ?? selectedGlasses?.bridge);
          const dimsRows = [];
          if (frameW) dimsRows.push({ key: 'frame', label: 'Širina', value: `${frameW}mm` });
          if (templeL) dimsRows.push({ key: 'temple', label: 'Ručke', value: `${templeL}mm` });
          if (bridgeW) dimsRows.push({ key: 'bridge', label: 'Most', value: `${bridgeW}mm` });
          const showFaceShape = !!faceShape;
          const show = showFaceShape || !!modelName || dimsRows.length > 0;
          if (!show) return null;
          const iconStyle = { width: 16, height: 16, flexShrink: 0, opacity: 0.9, display: 'block' };
          const iconColor = 'rgba(255,255,255,0.80)';
          const renderIcon = (key) => {
            if (key === 'frame') {
              return (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle} aria-hidden>
                  <path d="M4 7h16v10H4z" />
                  <path d="M8 7v4" />
                  <path d="M12 7v2" />
                  <path d="M16 7v4" />
                </svg>
              );
            }
            if (key === 'temple') {
              return (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle} aria-hidden>
                  <path d="M4 12h10" />
                  <path d="M14 12c3 0 6 2 6 5" />
                  <path d="M20 17v3" />
                </svg>
              );
            }
            return (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle} aria-hidden>
                <path d="M6 14c2-3 4-4 6-4s4 1 6 4" />
                <path d="M6 14v3" />
                <path d="M18 14v3" />
              </svg>
            );
          };
          return (
          <div
            style={{
              position: 'absolute',
              top: 16,
              left: 16,
              padding: '12px 16px',
              borderRadius: 14,
              background: 'rgba(15, 18, 25, 0.6)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#fff',
              zIndex: 10,
              pointerEvents: 'none',
            }}
          >
            {/* Face shape + active model name (same row) */}
            {(showFaceShape || modelName) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  minWidth: 0,
                }}
              >
                {showFaceShape && (
                  <>
                    <img src={iconFaceShape} alt="" style={{ width: 18, height: 18, objectFit: 'contain', flexShrink: 0, filter: 'brightness(0) invert(0.75)' }} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#fff', flexShrink: 0 }}>
                      {t('faceShape_' + (faceShape || 'oval'))}
                    </span>
                  </>
                )}
                {modelName && (
                  <div
                    style={{
                      marginLeft: showFaceShape ? 6 : 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '10px 14px',
                      borderRadius: 14,
                      background: 'rgba(255,255,255,0.10)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      color: '#e2e8f0',
                      boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
                      maxWidth: 360,
                      fontSize: 13,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: 0,
                    }}
                  >
                    {modelName}
                  </div>
                )}
              </div>
            )}

            {/* Dimensions under model name */}
            {dimsRows.length > 0 && (
              <div
                style={{
                  marginTop: (showFaceShape || modelName) ? 10 : 0,
                  paddingTop: (showFaceShape || modelName) ? 10 : 0,
                  borderTop: (showFaceShape || modelName) ? '1px solid rgba(255,255,255,0.08)' : 'none',
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.75)',
                  lineHeight: 1.25,
                  maxWidth: 360,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {dimsRows.map((r) => (
                    <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ color: iconColor, display: 'inline-flex', alignItems: 'center' }}>
                        {renderIcon(r.key)}
                      </span>
                      <span style={{ minWidth: 64, color: 'rgba(255,255,255,0.70)', fontWeight: 600 }}>
                        {r.label}
                      </span>
                      <span style={{ color: 'rgba(255,255,255,0.92)', fontWeight: 700 }}>
                        {r.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          );
        })()}
      </div>

              {/* Action bar below camera */}
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                {/* Combined shutter button with long-press for video */}
                <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                  <svg
                    viewBox="0 0 100 100"
                    style={{
                      position: 'absolute',
                      top: -4,
                      left: -4,
                      width: 'calc(100% + 8px)',
                      height: 'calc(100% + 8px)',
                      transform: 'rotate(-90deg)',
                      pointerEvents: 'none',
                    }}
                  >
                    <rect
                      x="4"
                      y="4"
                      width="92"
                      height="92"
                      rx="14"
                      ry="14"
                      fill="none"
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth="3"
                    />
                    <rect
                      x="4"
                      y="4"
                      width="92"
                      height="92"
                      rx="14"
                      ry="14"
                      fill="none"
                      stroke={brandColor}
                      strokeWidth="3"
                      strokeDasharray={`${recordingProgress * 3.68} 368`}
                      style={{
                        transition: 'stroke-dasharray 50ms linear',
                        filter: recordingProgress > 0 ? 'drop-shadow(0 0 4px rgba(56, 148, 168, 0.6))' : 'none',
                      }}
                    />
                  </svg>
                  <button 
                    type="button" 
                    onPointerDown={handleShutterPointerDown}
                    onPointerUp={handleShutterPointerUp}
                    onPointerLeave={handleShutterPointerLeave}
                    onPointerCancel={handleShutterPointerLeave}
                    style={{
                      ...btnPrimary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: isLongPressing
                        ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.9) 0%, rgba(220, 38, 38, 0.9) 100%)'
                        : comparingWithSelfieId
                          ? 'linear-gradient(135deg, rgba(251, 146, 60, 0.9) 0%, rgba(249, 115, 22, 0.9) 100%)'
                          : btnPrimary.background,
                      border: comparingWithSelfieId ? '2px solid rgba(251, 146, 60, 1)' : btnPrimary.border,
                      boxShadow: comparingWithSelfieId
                        ? '0 4px 16px rgba(251, 146, 60, 0.4)'
                        : btnPrimary.boxShadow,
                      touchAction: 'none',
                      userSelect: 'none',
                    }}
                    title={`Klikni za screenshot, drži za snimanje videa (max ${MAX_RECORDING_S}s)`}
                  >
                    {isLongPressing ? (
                      <>
                        <div style={{
                          width: 14,
                          height: 14,
                          borderRadius: 3,
                          background: '#fff',
                        }} />
                        Snimam... {Math.ceil(((100 - recordingProgress) / 100) * MAX_RECORDING_S)}s
                      </>
                    ) : comparingWithSelfieId ? (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <rect x="3" y="3" width="7" height="18" />
                          <rect x="14" y="3" width="7" height="18" />
                        </svg>
                        <span style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Uporedi
                        </span>
                      </>
                    ) : (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="3"/>
                          <path d="M5 7h2a2 2 0 0 0 2-2 1 1 0 0 1 1-1h4a1 1 0 0 1 1 1 2 2 0 0 0 2 2h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2"/>
                        </svg>
                        Uslikaj / Drži za video
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Saved shots gallery */}
              <div 
                style={{ 
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  borderRadius: 16,
                  padding: 16,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8' }}>Moja Galerija</span>
                    <span style={{ 
                      fontSize: 11, 
                      fontWeight: 500, 
                      color: '#64748b',
                      background: 'rgba(255, 255, 255, 0.06)',
                      padding: '2px 8px',
                      borderRadius: 10,
                    }}>
                      {selfies.length + videoClips.length}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setGalleryOpen(true)}
                    style={{
                      ...btnGhost,
                      color: brandColor,
                    }}
                  >
                    Otvori galeriju
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
                  {selfies.map((s) => {
                    const active = s.id === activeSelfieId;
                    return (
                      <div key={s.id} style={{ flex: '0 0 auto', position: 'relative' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setActiveSelfieId(s.id);
                            openSelfiePreview(s);
                          }}
                          style={{
                            border: active ? `2px solid ${brandColor}` : '1px solid rgba(255,255,255,0.08)',
                            padding: 0,
                            borderRadius: 14,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            background: '#000',
                            boxShadow: active ? '0 0 24px rgba(56, 148, 168, 0.3)' : 'none',
                            transition: 'all 150ms ease',
                          }}
                          title="Koristi za usporedbu"
                        >
                          <img
                            src={s.compositeDataUrl || s.baseFrameDataUrl}
                            alt="selfie"
                            style={{ width: 128, height: 96, objectFit: 'contain', display: 'block' }}
                          />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const next = removeSelfie(s.id);
                            setSelfies(next);
                            if (activeSelfieId === s.id) setActiveSelfieId(next[0]?.id || null);
                          }}
                          style={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            width: 24,
                            height: 24,
                            borderRadius: 8,
                            background: 'rgba(0,0,0,0.7)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#94a3b8',
                            fontSize: 14,
                          }}
                          title="Obriši"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                  {!selfies.length && (
      <div style={{
                      padding: '24px 32px', 
                      color: '#64748b', 
                      fontSize: 13,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                    }}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                      <span>Još nemate fotografija. Uslikajte svoj prvi izgled!</span>
      </div>
                  )}
                </div>
              </div>

              {/* Video klipovi */}
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  borderRadius: 16,
                  padding: 16,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 7l-7 5 7 5V7z" />
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                    </svg>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8' }}>{t('videoClips')}</span>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: '#64748b',
                        background: 'rgba(255, 255, 255, 0.06)',
                        padding: '2px 8px',
                        borderRadius: 10,
                      }}
                    >
                      {videoClips.length}
                    </span>
                  </div>
                  {videoClips.length > 0 && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          // delete active clip only (safe MVP)
                          if (activeClipId) {
                            await deleteVideoClip(activeClipId);
                            const next = await loadVideoClips();
                            setVideoClips(next);
                            setActiveClipId(next[0]?.id || null);
                          }
                        } catch {
                          // ignore
                        }
                      }}
                      style={btnGhost}
                    >
                      Obriši odabrani
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
                  {videoClips.map((c) => {
                    const active = c.id === activeClipId;
                    const thumb = clipThumbs?.[c.id] || null;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setActiveClipId(c.id);
                          openClipPreview(c);
                        }}
                        style={{
                          flex: '0 0 auto',
                          border: active ? `2px solid ${brandColor}` : '1px solid rgba(255,255,255,0.08)',
                          padding: 0,
                          borderRadius: 14,
                          overflow: 'hidden',
                          background: '#000',
                          width: 160,
                          height: 96,
                          position: 'relative',
                          boxShadow: active ? '0 0 24px rgba(56, 148, 168, 0.25)' : 'none',
                        }}
                        title="Odaberite klip za video usporedbu"
                      >
                        {thumb ? (
                          <img src={thumb} alt="video klip" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        ) : (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: 12 }}>
                            Učitavam…
                          </div>
                        )}
                        <div
                          style={{
                            position: 'absolute',
                            left: 8,
                            bottom: 8,
                            padding: '4px 8px',
                            borderRadius: 999,
                            background: 'rgba(0,0,0,0.6)',
                            border: '1px solid rgba(255,255,255,0.10)',
                            color: '#e2e8f0',
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                        >
                          {MAX_RECORDING_S}s
                        </div>
                      </button>
                    );
                  })}

                  {!videoClips.length && (
                    <div style={{ padding: '18px 10px', color: '#64748b', fontSize: 13 }}>
                      Nema video klipova. Kliknite <b>“Snimi video ({MAX_RECORDING_S}s)”</b>.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Eyewear Panel - Images Only */}
            {showCatalog && compareCatalog.length > 1 && (
              <div 
                style={{ 
                  position: 'sticky', 
                  top: 24,
                    width: 280,
                    maxHeight: 'calc(100vh - 120px)',
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  borderRadius: 16,
                  padding: 12,
                }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {compareCatalog.map((g, i) => {
                    const active = g.id === selectedGlassesId;
                    return (
                      <LazyReveal key={g.id} eager={i < catalogRevealCount} rootMargin="300px" placeholder={<div style={{ borderRadius: 12, background: 'rgba(255,255,255,0.04)', minHeight: 64 }} />}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedGlassesId(g.id);
                          if (comparingWithSelfieId) {
                            showToast(g.name, 'info', 1200, 'top');
                          }
                        }}
                        style={{
                          borderRadius: 12,
                          border: active ? `2px solid ${brandColor}` : '1px solid rgba(255,255,255,0.08)',
                          background: active ? 'rgba(56, 148, 168, 0.12)' : 'rgba(255,255,255,0.02)',
                          padding: 8,
                          cursor: 'pointer',
                          transition: 'all 150ms ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                        }}
                      >
                        <LazyImageWithFallback
                          src={hasInitialGlassesLoaded ? g.src : undefined}
                          alt=""
                          containerStyle={{
                            width: '100%',
                            height: 48,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          imgStyle={{
                            width: '100%',
                            height: 48,
                            objectFit: 'contain',
                          }}
                        />
                      </button>
                      </LazyReveal>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fit Toast - top-right corner below watermark */}
      {fitToast.open && fitToast.type && FIT_TOAST_CONFIG[fitToast.type] && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            // Lower so it never overlaps the top-right watermark.
            top: isMobile ? 'calc(64px + env(safe-area-inset-top))' : 84,
            right: isMobile ? 12 : 18,
            zIndex: 100003,
            padding: isMobile ? '8px 12px' : '10px 16px',
            borderRadius: 12,
            background: FIT_TOAST_CONFIG[fitToast.type].bgColor,
            border: `1px solid ${FIT_TOAST_CONFIG[fitToast.type].borderColor}`,
            color: FIT_TOAST_CONFIG[fitToast.type].color,
            boxShadow: '0 10px 28px rgba(0,0,0,0.55)',
            fontSize: isMobile ? 12 : 13,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            animation: 'fitToastSlideIn 0.3s ease-out',
          }}
        >
          {/* Checkmark icon */}
          <svg width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} viewBox="0 0 16 16" fill="none">
            <path
              d="M13.5 4.5L6.5 11.5L3 8"
              stroke={FIT_TOAST_CONFIG[fitToast.type].color}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {t(FIT_TOAST_CONFIG[fitToast.type].labelKey) || FIT_TOAST_CONFIG[fitToast.type].fallbackLabel}
        </div>
      )}

      {/* Toast */}
      {toast.open && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            left: '50%',
            ...(toast.position === 'top' ? { top: 18 } : { bottom: 18 }),
            transform: 'translateX(-50%)',
            zIndex: 100003,
            padding: '10px 14px',
            borderRadius: 14,
            background:
              toast.kind === 'success'
                ? 'rgba(16, 185, 129, 0.18)'
                : toast.kind === 'error'
                  ? 'rgba(239, 68, 68, 0.18)'
                  : 'rgba(255,255,255,0.10)',
            border:
              toast.kind === 'success'
                ? '1px solid rgba(16, 185, 129, 0.35)'
                : toast.kind === 'error'
                  ? '1px solid rgba(239, 68, 68, 0.35)'
                  : '1px solid rgba(255,255,255,0.12)',
            color: '#e2e8f0',
            boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
            maxWidth: 'min(720px, calc(100vw - 32px))',
            fontSize: 13,
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default VirtualTryOn;
